/*
 * Copyright (c) 2021 SGS Europe. All rights reserved.
 *
 * This program is the CONFIDENTIAL and PROPRIETARY property of SGS Europe.
 * Any unauthorised use, reproduction, or transfer of this program is strictly
 * prohibited.
 *
 * File Name:	GreenlightTextCompare.js
 *
 * Description:	The class containing the functionality required by the Greenlight for text compare.
 * 
 */

// The text compare object.
var g_oTextCompare = {};

(function () {
	"use strict";

	/**
	 * Load the text content for the page.
	 */
	function TextCompare() {

		// Outline the compare handle types.
		this.eTextCompareHandleTypes = {
			AddText: 0,
			AddParagraph: 1,
			AddArea: 2,
			RemoveText: 3,
			Delete: 4
		};

		// Outline the difference types.
		this.eDifferenceTypes = {
			Deletion: -1,
			NoDifference: 0,
			Addition: 1,
			Substitution: 2,
			WeightSubstitution: 3,
			StyleSubstitution: 4,
			WeightStyleSubstitution: 5,
			FailedToCalculate: 6
		};

		// Outline the marker types.
		this.eMarkerTypes = {
			NoMarker: 0,
			MarkerBefore: 1,
			MarkerAfter: 2
		};

		// Difference mapping.
		this.g_eDifferenceMapping = {
			Both: 0,
			ApprovalOnly: 1,
			CompareOnly: 2
		};

		// Outline the difference statuses.
		this.eDifferenceStatuses = {
			Initial: 0,
			Ignore: 1,
			Accept: 2,
			Reject: 3
		};

		// Outline the accuracy levels.
		this.eAccuracyLevels = {
			Word: 1,
			Character: 2
		};

		// Outline the case sensitivity levels.
		this.eCaseSensitivityLevels = {
			Off: 1,
			On: 2
		};

		// Outline the bold italic detection levels.
		this.eBoldItalicDetectionLevels = {
			Off: 1,
			On: 2
		};

		// Outline the whitespace detection levels.
		this.eWhitespaceDetectionLevels = {
			Off: 1,
			On: 2
		};

		// Outline the auto selection levels.
		this.eAutoSelectionLevels = {
			Off: 1,
			On: 2
		};

		// Keeps track of if a text compare is in progress.
		this.g_bTextCompareInProgress = false;

		// Keep track of if the text data has been loaded for each canvas.
		this.g_bArtworkTextDataLoaded = false;
		this.g_bCompareTextDataLoaded = false;

		// Keep track of stored text selection groups.
		this.g_oStoredTextSelectionGroups = [];

		// Keep track of the text selection groups for both canvas.
		this.g_oArtworkTextSelectionGroups = [];
		this.g_oCompareTextSelectionGroups = [];

		// Keep track of if the text compare div is shown.
		this.g_bTextCompareDivShown = false;

		// Keep track of any collapsed sections.
		this.g_oCollapsedSectionOrders = [];

		// Keep track of the hidden separations.
		this.g_oHiddenSeparations = [];

		// Keep track of if we have calcuated differences.
		this.g_bDifferencesCalculated = false;

		// Keep track of the number of differences.
		this.g_iNumberOfDifferences = 0;

		// Keep track of the step through difference.
		this.g_iStepThroughDifference = null;

		// The size of the marker.
		this.g_iMarkerSize = 25 * g_oViewer.c_iDPI;

		// The colour to highlight in.
		this.g_szHighlightColour = "yellow";

		// Keep track of if we can leave difference annotations.
		this.g_bDifferenceAnnotations = false;

		// Keep track of the accuracy.
		this.g_iAccuracy = this.eAccuracyLevels.Word;

		// Keep track of the case sensitivity.
		this.g_iCaseSensitivity = this.eCaseSensitivityLevels.On;

		// Keep track of the bold italic detection.
		this.g_iBoldItalicDetection = this.eBoldItalicDetectionLevels.On;

		// Keep track of the whitespace detection.
		this.g_iWhitespaceDetection = this.eWhitespaceDetectionLevels.On;

		// Keep track of the whitespace detection.
		this.g_iAutoSelection = this.eAutoSelectionLevels.On;

		// Keep track of if we are drag scrolling.
		this.g_bDragScrolling = false;

		// Update flag for automatically activating the pan tool on highlight end.
		this.g_bAutoActivatePanTool = true;

		// Keep track of any auto activate text highlight object.
		this.g_oAutoActivateTextHighlight = null;

		// Keep track of if the new highlight is being added to an existing group.
		this.g_iAddHighlightToExistingGroup = null;
	};

	/**
	 * Sets the text compare mode active.
	 */
	TextCompare.prototype.setTextCompareActive = function () {

		// Set the initial settings.
		g_oTextCompare.setTextCompareSettings();

		// Update the settings.
		g_oTextCompare.updateTextCompareSettings();

		// Set that we are now in text compare mode.
		g_oTextCompare.g_bTextCompareInProgress = true;
	};

	/**
	 * Sets the text compare settings.
	 */
	TextCompare.prototype.setTextCompareSettings = function () {

		// Update the text compare settings levels to the users settings.
		$("#divTextComparisonAccuracy").val(g_oSettings.g_oUserSettings.TextComparisonAccuracy);
		$("#divTextComparisonCaseSensitivity").val(g_oSettings.g_oUserSettings.TextComparisonCaseSensitivity);
		$("#divTextComparisonBoldItalicDetection").val(g_oSettings.g_oUserSettings.TextComparisonBoldItalicDetection);
		$("#divTextComparisonWhitespaceDetection").val(g_oSettings.g_oUserSettings.TextComparisonWhitespaceDetection);
		$("#divTextComparisonAutoSelection").val(g_oSettings.g_oUserSettings.TextComparisonAutoSelection);
	};

	/**
	 * Updates the text compare settings.
	 */
	TextCompare.prototype.updateTextCompareSettings = function () {

		// Update the settings.
		g_oTextCompare.g_iAccuracy = parseInt($("#divTextComparisonAccuracy").val());
		g_oTextCompare.g_iCaseSensitivity = parseInt($("#divTextComparisonCaseSensitivity").val());
		g_oTextCompare.g_iBoldItalicDetection = parseInt($("#divTextComparisonBoldItalicDetection").val());
		g_oTextCompare.g_iWhitespaceDetection = parseInt($("#divTextComparisonWhitespaceDetection").val());
		g_oTextCompare.g_iAutoSelection = parseInt($("#divTextComparisonAutoSelection").val());

		// Update the initial highlight colour.
		g_oTextCompare.g_szHighlightColour = g_oAnnotations.getStringColour(g_oSettings.g_oUserSettings.TextComparisonHighlightColour);

		// If we have the text compare div shown.
		if (g_oTextCompare.g_bTextCompareDivShown) {

			// Render the text compare sections to the new settings.
			g_oTextCompare.renderTextCompareSections();
		}
	};

	/**
	 * Sets the text compare mode inactive.
	 */
	TextCompare.prototype.setTextCompareUnactive = function () {

		// Reset the text selection groups.
		g_oTextCompare.g_oArtworkTextSelectionGroups = [];
		g_oTextCompare.g_oCompareTextSelectionGroups = [];

		// Reset the step through.
		g_oTextCompare.g_iStepThroughDifference = null;

		// Hide the differences bottom bar.
		$("#divTextDifferences").hide();

		// Hide the text compare page navigations.
		$("#divTextCompare_ApprovalPageNavigation").hide();
		$("#divTextCompare_ComparePageNavigation").hide();

		// Set that we are no longer in text compare mode.
		g_oTextCompare.g_bTextCompareInProgress = false;
	};

	/**
	 * Stores the active text selection groups.
	 */
	TextCompare.prototype.storeActiveTextSelectionGroups = function () {

		// Store the active text selection groups for both canvas.
		g_oTextCompare.storeTextSelectionGroups(g_oTextCompare.g_oArtworkTextSelectionGroups, g_oViewer.g_oArtworkViewer);
		g_oTextCompare.storeTextSelectionGroups(g_oTextCompare.g_oCompareTextSelectionGroups, g_oCompare.g_oCompareViewer);
	};

	/**
	 * Stores the text selection groups.
	 * @param {object} oTextSelectionGroups - The text selection groups.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 */
	TextCompare.prototype.storeTextSelectionGroups = function (oTextSelectionGroups, oArtworkCanvas) {

		// If this artwork already has stored text selection groups, remove the old data.
		if (g_oTextCompare.getStoredTextSelectionGroupIndex(oArtworkCanvas) != -1) {
			g_oTextCompare.g_oStoredTextSelectionGroups.splice(g_oTextCompare.getStoredTextSelectionGroupIndex(oArtworkCanvas), 1);
		}

		// Store the text selection groups.
		g_oTextCompare.g_oStoredTextSelectionGroups.push(new g_oTextCompare.textSelectionGroups(deepCopyArray(oTextSelectionGroups), oArtworkCanvas));
	};

	/**
	 * Retrieves the stored text selection groups.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 */
	TextCompare.prototype.retrieveTextSelectionGroups = function (oArtworkCanvas) {
		var iIndex = g_oTextCompare.getStoredTextSelectionGroupIndex(oArtworkCanvas);
		return iIndex > -1 ? deepCopyArray(g_oTextCompare.g_oStoredTextSelectionGroups[iIndex].g_oTextSelectionGroups) : [];
	};

	/**
	 * Gets the stored text selection index.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 */
	TextCompare.prototype.getStoredTextSelectionGroupIndex = function (oArtworkCanvas) {
		for (var i = 0; i < g_oTextCompare.g_oStoredTextSelectionGroups.length; i++) {
			if (g_oTextCompare.g_oStoredTextSelectionGroups[i].g_szCanvasId == oArtworkCanvas.g_szCanvasId && g_oTextCompare.g_oStoredTextSelectionGroups[i].g_iDocumentId == oArtworkCanvas.g_oDoc.documentId) {
				return i;
			}
		}
		return -1;
	};

	/**
	 * Initialises text compare.
	 */
	TextCompare.prototype.initialiseTextCompare = function () {

		// Close any active confirm bubbles as these may be related to text compare.
		g_oGreenlightMessages.closeBubbles("Confirm");

		// If we have not yet loaded the text equivalents, load these now.
		if (g_oTextCompare_Equivalents.g_oTextEquivalents == null) {
			g_oTextCompare_Equivalents.loadTextEquivalents();
		}

		// Reset the loaded flags.
		g_oTextCompare.g_bArtworkTextDataLoaded = false;
		g_oTextCompare.g_bCompareTextDataLoaded = false;

		// Load the artwork and compare canvas.
		var oArtworkCanvas = g_oViewer.g_oArtworkViewer;
		var oCompareCanvas = g_oCompare.g_oCompareViewer;

		// Retrieve the stored text selection groups for both canvas.
		g_oTextCompare.g_oArtworkTextSelectionGroups = g_oTextCompare.retrieveTextSelectionGroups(oArtworkCanvas);
		g_oTextCompare.g_oCompareTextSelectionGroups = g_oTextCompare.retrieveTextSelectionGroups(oCompareCanvas);

		// Call to load the text data for the canvas.
		g_oTextData.loadFullTextData(oArtworkCanvas.g_oDoc, function () { g_oTextCompare.g_bArtworkTextDataLoaded = true; g_oTextCompare.initialiseTextCompare_CH(); });
		g_oTextData.loadFullTextData(oCompareCanvas.g_oDoc, function () { g_oTextCompare.g_bCompareTextDataLoaded = true; g_oTextCompare.initialiseTextCompare_CH(); });
	};

	/**
	 * Callback for initialising text compare.
	 */
	TextCompare.prototype.initialiseTextCompare_CH = function () {

		// Only continue once all text data has been loaded.
		if (!g_oTextCompare.g_bArtworkTextDataLoaded || !g_oTextCompare.g_bCompareTextDataLoaded) {
			return;
		}

		// Update the approval and compare document page navigations.
		g_oTextCompare.updatePageNavigation("divApprovalName", "divTextCompare_ApprovalPageNavigation", g_oViewer.g_oArtworkViewer);
		g_oTextCompare.updatePageNavigation("divCompareName", "divTextCompare_ComparePageNavigation", g_oCompare.g_oCompareViewer);

		// Render the text compare annotations.
		g_oTextCompare.renderTextCompareAnnotations();

		// Show the text compare div.
		g_oTextCompare.showTextCompareDiv();

		// Re-activate any stored text selection tool.
		g_oTextCompare.setActiveTextSelectionTool();

		// Enable the select lists if these are not locked by the consumer.
		$("#divTextComparisonAccuracy").prop("disabled", g_oSettings.g_oConsumerSettings.LockTextComparisonAccuracy ? "disabled" : "");
		$("#divTextComparisonCaseSensitivity").prop("disabled", g_oSettings.g_oConsumerSettings.LockTextComparisonCaseSensitivity ? "disabled" : "");
		$("#divTextComparisonBoldItalicDetection").prop("disabled", g_oSettings.g_oConsumerSettings.LockTextComparisonBoldItalicDetection ? "disabled" : "");
		$("#divTextComparisonWhitespaceDetection").prop("disabled", g_oSettings.g_oConsumerSettings.LockTextComparisonWhitespaceDetection ? "disabled" : "");
		$("#divTextComparisonAutoSelection").prop("disabled", g_oSettings.g_oConsumerSettings.LockTextComparisonAutoSelection ? "disabled" : "");
	};

	/**
	 * Stores the active text selection tool.
	 */
	TextCompare.prototype.storeActiveTextSelectionTool = function () {

		// Reset the active text selection tool.
		g_oTextCompare.resetActiveTextSelectionTool();

		// If we are actively adding to a text group on this page, store this so we can continue adding after changing page.
		if (g_oTextTools.g_bHighlightInProgress && g_oTextTools.g_eActiveToolType !== 3) {

			// Determine if we are adding to an existing group.
			var iAddToExistingGroup = null;
			if (g_oTextCompare.g_iAddHighlightToExistingGroup != null) {
				iAddToExistingGroup = g_oTextCompare.g_iAddHighlightToExistingGroup;
			}
			else if (g_oTextTools.g_iExistingAnnotationId != null) {
				iAddToExistingGroup = g_oTextTools.g_iExistingAnnotationId.selectionGroup;
			}

			// Setup the auto activate object.
			g_oTextCompare.g_oAutoActivateTextHighlight = {
				bCompareCanvas: g_oTextTools.g_bHighlightOnCompareCanvas,
				eActiveToolType: g_oTextTools.g_eActiveToolType,
				iAddToExistingGroup: iAddToExistingGroup
			};
		}
	};

	/**
	 * Sets the active text selection tool.
	 */
	TextCompare.prototype.setActiveTextSelectionTool = function () {

		// If we want to automatically activate a text highlight tool, start this back up.
		if (g_oTextCompare.g_oAutoActivateTextHighlight != null) {
			setTimeout(function () {

				// Re-activate the tool.
				switch (g_oTextCompare.g_oAutoActivateTextHighlight.eActiveToolType) {
					case g_oTextTools.g_eToolTypes.TextHighlight:
						g_oTextCompare.initialiseTextCompareHighlight(g_oTextCompare.g_oAutoActivateTextHighlight.bCompareCanvas, g_oTextCompare.g_oAutoActivateTextHighlight.iAddToExistingGroup);
						break;
					case g_oTextTools.g_eToolTypes.ParagraphHighlight:
						g_oTextCompare.initialiseTextCompareParagraphHighlight(g_oTextCompare.g_oAutoActivateTextHighlight.bCompareCanvas, g_oTextCompare.g_oAutoActivateTextHighlight.iAddToExistingGroup);
						break;
					case g_oTextTools.g_eToolTypes.AreaTextHighlight:
						g_oTextCompare.initialiseTextCompareAreaHighlight(g_oTextCompare.g_oAutoActivateTextHighlight.bCompareCanvas, g_oTextCompare.g_oAutoActivateTextHighlight.iAddToExistingGroup);
						break;
					case g_oTextTools.g_eToolTypes.RemoveTextHighlight:
						return;
						break;
				}

				// Reset the active text selection tool.
				g_oTextCompare.resetActiveTextSelectionTool();
			}, 500);
		}
	};

	/**
	 * Resets the text selection tool.
	 */
	TextCompare.prototype.resetActiveTextSelectionTool = function () {
		g_oTextCompare.g_oAutoActivateTextHighlight = null;
	};

	/**
	 * Shows the text compare div.
	 */
	TextCompare.prototype.showTextCompareDiv = function () {

		// Set the bottom position and height of the div to match the canvas height.
		$("#divTextCompare").css("bottom", "-" + $("#divCanvasHolder").height() + "px");
		$("#divTextCompareContent").css("height", $("#divCanvasHolder").height());

		// Show the text compare div.
		$("#divTextCompare").show();
	};

	/**
	 * Hides the text compare div.
	 */
	TextCompare.prototype.hideTextCompareDiv = function () {

		// Hide the text compare div.
		$("#divTextCompare").hide();
	};

	/**
	 * Toggles the text compare div.
	 */
	TextCompare.prototype.toggleTextCompareDiv = function () {

		// If we have an active text highlight, end this.
		if (g_oTextTools.g_bHighlightInProgress) {
			g_oTextCompare.endTextCompareHighlight();
		}

		// Toggle the text compare div state.
		g_oTextCompare.g_bTextCompareDivShown = !g_oTextCompare.g_bTextCompareDivShown;

		// Render the text compare annotations.
		g_oTextCompare.renderTextCompareAnnotations();

		// Update the size of the text compare div.
		g_oTextCompare.updateTextCompareDiv();
	};

	/**
	 * Updates the text compare div.
	 */
	TextCompare.prototype.updateTextCompareDiv = function () {

		// Determine the div and icon position.
		var iDivPosition = g_oTextCompare.g_bTextCompareDivShown ? 0 : 0 - $("#divCanvasHolder").height();
		var iIconMargin = g_oTextCompare.g_bTextCompareDivShown ? 0 : -40;
		var iIconRotation = g_oTextCompare.g_bTextCompareDivShown ? 90 : -90;
		var iIconBorderRadius = g_oTextCompare.g_bTextCompareDivShown ? "0px 0px 0px 8px" : "8px 0px 0px 0px";

		// Update the height of the text compare div.
		$("#divTextCompareContent").css("height", $("#divCanvasHolder").height());

		// Slide the div to the correct position.
		$("#divTextCompare").animate({
			bottom: iDivPosition + 'px'
		}, 800);

		// Move the icon to the correct position.
		$("#divTextCompareButton").animate({
			"margin-top": iIconMargin + 'px'
		}, 800);
		$("#divTextCompareButton").css("border-radius", iIconBorderRadius);
		$("#divTextCompareArrow").css("transform", "rotate(" + iIconRotation + "deg)");

		// Update the height of the text compare sections.
		g_oTextCompare.updateTextCompareSectionsHeight();
	};

	/**
	 * Updates the height of the text comapare sections div.
	 */
	TextCompare.prototype.updateTextCompareSectionsHeight = function () {

		// Set the height of the sections.
		$("#divTextCompareSections").css("height", ($(".divTextCompareContent__Wrapper").height() - $("#divTextCompareSections").position().top - 20) + "px");
	};

	/**
	 * Updates the page navigation.
	 * @param {string} szParentDiv - The parent div.
	 * @param {string} szNavigationDiv - The navigation div.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 */
	TextCompare.prototype.updatePageNavigation = function (szParentDiv, szNavigationDiv, oArtworkCanvas) {

		// Ensure the navigation div exists.
		if ($("#" + szParentDiv).children("#" + szNavigationDiv).length == 0) {
			$("#" + szParentDiv).append("<div id='" + szNavigationDiv + "' style='margin-left: 10px; margin-top: 2px; display: none;'></div>");
		}

		// If this doc does not have multiple pages, hide the navigation div.
		if (oArtworkCanvas.g_oDoc.pages.length < 2) {
			$("#" + szNavigationDiv).css("display", "none");
			return;
		}

		// Determine if the navigation buttons are enabled / disabled.
		var bBackButtonDisabled = oArtworkCanvas.g_iCurrentPage == 1;
		var bNextButtonDisabled = oArtworkCanvas.g_iCurrentPage == oArtworkCanvas.g_oDoc.pages.length;

		// Build the document page navigation for the control.
		var szHTML = '';
		szHTML += '<input type="image" class="multiDocumentSelect tooltip" src="' + (bBackButtonDisabled ? "img/Last-Page_disabled_48.png" : "img/Last-Page_blue_48.png") + '" title="' + c_szPrevious + '" style="padding-right: 3px; vertical-align: middle;" ' + (bBackButtonDisabled ? '' : 'onclick="g_oTextCompare.changePage(false, ' + (szParentDiv == "divCompareName") + ');"') + ' />';
		szHTML += '<span style="vertical-align: middle;">Page ' + oArtworkCanvas.g_iCurrentPage + ' of ' + oArtworkCanvas.g_oDoc.pages.length + '</span>';
		szHTML += '<input type="image" class="multiDocumentSelect tooltip" src="' + (bNextButtonDisabled ? "img/Next-Page_disabled_48.png" : "img/Next-Page_blue_48.png") + '" title="' + c_szNext + '" style="padding-left: 3px; vertical-align: middle;" ' + (bNextButtonDisabled ? '' : 'onclick="g_oTextCompare.changePage(true, ' + (szParentDiv == "divCompareName") + ');"') + ' />';

		// Apply the html.
		$("#" + szNavigationDiv).html(szHTML);

		// Show the navigation div.
		$("#" + szNavigationDiv).css("display", "inline-block");
	};

	/**
	 * Changes the document.
	 */
	TextCompare.prototype.changeDocument = function () {

		// If the review screen is open, close this.
		if (g_oTextCompare.g_bTextCompareDivShown) {
			g_oTextCompare.toggleTextCompareDiv();
		}

		// Hide the text compare information.
		g_oTextCompare.hideTextCompareInformation();

		// Hide the separations.
		g_oTextCompare.hideTextCompareSeparations();

		// Store the active text selections.
		g_oTextCompare.storeActiveTextSelectionGroups();

		// Reset the loading status of both documents.
		g_oTextCompare.g_bArtworkTextDataLoaded = false;
		g_oTextCompare.g_bCompareTextDataLoaded = false;
	};

	/**
	 * Changes the page to the next or previous page.
	 * @param {boolean} bNextPage - If we are changing to the next or previous page.
	 * @param {boolean} bCompareCanvas - If this is the compare canvas.
	 */
	TextCompare.prototype.changePage = function (bNextPage, bCompareCanvas) {

		// Get the canvas we are going to change the page of.
		var oArtworkCanvas = !bCompareCanvas ? g_oViewer.g_oArtworkViewer : g_oCompare.g_oCompareViewer;

		// Determine the page we will be changing to.
		var iNewPage = bNextPage ? oArtworkCanvas.g_iCurrentPage + 1 : oArtworkCanvas.g_iCurrentPage - 1;

		// Change to the new page.
		g_oTextCompare.changeToPage(iNewPage, bCompareCanvas);
	};

	/**
	 * Changes the page to an exact page.
	 * @param {boolean} iNewPage - The page we are going to change to.
	 * @param {boolean} bCompareCanvas - If this is the compare canvas.
	 */
	TextCompare.prototype.changeToPage = function (iNewPage, bCompareCanvas) {

		// Store any active text selection tool.
		g_oTextCompare.storeActiveTextSelectionTool();

		// If we have an active text highlight, end this.
		if (g_oTextTools.g_bHighlightInProgress) {
			g_oTextTools.endhighlight(g_oViewer.g_oArtworkViewer, g_oViewer.g_oAnnotationViewer, true);
		}

		// If the review screen is open, close this.
		if (g_oTextCompare.g_bTextCompareDivShown) {
			g_oTextCompare.toggleTextCompareDiv();
		}

		// Hide the text compare information.
		g_oTextCompare.hideTextCompareInformation();

		// Hide the separations.
		g_oTextCompare.hideTextCompareSeparations();

		// Store the active text selections.
		g_oTextCompare.storeActiveTextSelectionGroups();

		// Get the canvas we are going to update the page of.
		var oArtworkCanvas = !bCompareCanvas ? g_oViewer.g_oArtworkViewer : g_oCompare.g_oCompareViewer;
		var oAnnotationCanvas = !bCompareCanvas ? g_oViewer.g_oAnnotationViewer : g_oCompare.g_oCompareAnnotationViewer;

		// Ensure the new page exists.
		if (oArtworkCanvas.g_oDoc.pages[iNewPage - 1] == undefined) {
			return;
		}

		// Store the current page.
		var iCurrentPage = oArtworkCanvas.g_iCurrentPage;

		// Update the page number.
		oArtworkCanvas.g_iCurrentPage = iNewPage;

		// Determine if the two pages have the same resolution.
		if (oArtworkCanvas.g_oDoc.pages[iCurrentPage - 1].documentResolutionPPI == oArtworkCanvas.g_oDoc.pages[iNewPage - 1].documentResolutionPPI) {

			// Reset the canvas that is having its page changed.
			oArtworkCanvas.resetCanvas();

			// Update the combo control.
			var iControlIndex = comboControl_GetControlById((oArtworkCanvas.g_szCanvasId === "ArtworkViewer" ? "approvalNameComboControl" : "approvalCompareComboControl"), true);
			var iDocumentIndex = comboControl_GetDocumentById(iControlIndex, oArtworkCanvas.g_oDoc.documentId, true);
			var iPageIndex = comboControl_GetPageById(iControlIndex, iDocumentIndex, oArtworkCanvas.g_oDoc.pages[iNewPage - 1].documentPageId, true);
			comboControl_SelectOption(iControlIndex, iDocumentIndex, iPageIndex, false);

			// Update the canvas files.
			var iCanvasFileRequestId = Math.random();
			g_oCompare.g_iCanvasFileRequestId = iCanvasFileRequestId;
			oArtworkCanvas.getCanvasFiles(true, iCanvasFileRequestId);
		}
		// If the two pages do not have the same resolution.
		else {

			// Change the current page fully reloading both documents.
			oArtworkCanvas.changeCurrentPage();
		}

		// Publish the event to trigger a full reload of both documents.
		EventService.publish("changeCurrentPage", oArtworkCanvas, oAnnotationCanvas);
	};

	/**
	 * Initialises a text compare highlight.
	 * @param {boolean} bCompareCanvas - If this is the compare canvas.
	 * @param {int} iAddToExistingGroup - Int if we are adding to an existing group, null if we are not.
	 */
	TextCompare.prototype.initialiseTextCompareHighlight = function (bCompareCanvas, iAddToExistingGroup) {

		// Re-render both annotation canvas before starting the highlight.
		g_oTextCompare.renderTextCompareAnnotations();

		// Set that this will be a new annotation.
		g_oTextTools.g_iExistingAnnotationId = null;

		// Set if this will be added to an existing group.
		g_oTextCompare.g_iAddHighlightToExistingGroup = iAddToExistingGroup;

		// Set up the new annotation text.
		g_oTextTools.g_oAnnotationText = [];

		// Determine if this is at character level accuracy.
		var bCharacterLevelAccuracy = g_oTextCompare.g_iAccuracy == g_oTextCompare.eAccuracyLevels.Character;

		// Initialise the text highlight.
		g_oTextTools.initialiseTextTool(g_oTextTools.g_eToolTypes.TextHighlight, eAnnotationTypes.TextHighlight, bCharacterLevelAccuracy, bCompareCanvas, g_oTextCompare.g_szHighlightColour);
	};

	/**
	 * Initialises a text compare paragraph highlight.
	 * @param {boolean} bCompareCanvas - If this is the compare canvas.
	 * @param {int} iAddToExistingGroup - Int if we are adding to an existing group, null if we are not.
	 */
	TextCompare.prototype.initialiseTextCompareParagraphHighlight = function (bCompareCanvas, iAddToExistingGroup) {

		// Re-render both annotation canvas before starting the highlight.
		g_oTextCompare.renderTextCompareAnnotations();

		// Set that this will be a new annotation.
		g_oTextTools.g_iExistingAnnotationId = null;

		// Set if this will be added to an existing group.
		g_oTextCompare.g_iAddHighlightToExistingGroup = iAddToExistingGroup;

		// Set up the new annotation text.
		g_oTextTools.g_oAnnotationText = [];

		// Determine if this is at character level accuracy.
		var bCharacterLevelAccuracy = g_oTextCompare.g_iAccuracy == g_oTextCompare.eAccuracyLevels.Character;

		// Initialise the paragraph highlight.
		g_oTextTools.initialiseTextTool(g_oTextTools.g_eToolTypes.ParagraphHighlight, eAnnotationTypes.TextHighlight, bCharacterLevelAccuracy, bCompareCanvas, g_oTextCompare.g_szHighlightColour);
	};

	/**
	 * Initialises the text compare add highlight.
	 * @param {int} iSelectionGroup - The selection group.
	 * @param {int} iSelection - The selection.
	 * @param {boolean} bCompareCanvas - If this is the compare canvas.
	 */
	TextCompare.prototype.initialiseTextCompareAddHighlight = function (iSelectionGroup, iSelection, bParagraphHighlight, bCompareCanvas) {

		// Set that this will be an edit to an existing annotation.
		g_oTextTools.g_iExistingAnnotationId = {
			"selectionGroup": iSelectionGroup,
			"selection": iSelection
		};

		// Set that this will not be added to an existing group.
		g_oTextCompare.g_iAddHighlightToExistingGroup = null;

		// Get the text selection group we are going to add to.
		var oTextSelectionGroup = g_oTextCompare.getTextSelectionGroup(bCompareCanvas);

		// Set up the new annotation text.
		g_oTextTools.g_oAnnotationText = bCompareCanvas ? oTextSelectionGroup[iSelectionGroup].g_oTextSelections[iSelection].g_oTextData : oTextSelectionGroup[iSelectionGroup].g_oTextSelections[iSelection].g_oTextData;

		// Determine the highlight tool to be used.
		var eHighlightTool = !bParagraphHighlight ? g_oTextTools.g_eToolTypes.TextHighlight : g_oTextTools.g_eToolTypes.ParagraphHighlight;

		// Determine if this is at character level accuracy.
		var bCharacterLevelAccuracy = g_oTextCompare.g_iAccuracy == g_oTextCompare.eAccuracyLevels.Character;

		// Initialise the text highlight.
		g_oTextTools.initialiseTextTool(eHighlightTool, eAnnotationTypes.TextHighlight, bCharacterLevelAccuracy, bCompareCanvas, g_oTextCompare.g_szHighlightColour);
	};

	/**
	 * Initialises a text removal highlight.
	 * @param {int} iSelectionGroup - The selection group.
	 * @param {int} iSelection - The selection.
	 * @param {bool} bParagraphHighlight - If this is a paragraph highlight.
	 * @param {bool} bCompareCanvas - The compare canvas.
	 */
	TextCompare.prototype.initialiseTextCompareRemoveHighlight = function (iSelectionGroup, iSelection, bParagraphHighlight, bCompareCanvas) {

		// Set that this will be an edit to an existing annotation.
		g_oTextTools.g_iExistingAnnotationId = {
			"selectionGroup": iSelectionGroup,
			"selection": iSelection
		};

		// Get the text selection group we are going to remove from.
		var oTextSelectionGroup = g_oTextCompare.getTextSelectionGroup(bCompareCanvas);

		// Set up the new annotation text.
		g_oTextTools.g_oAnnotationText = bCompareCanvas ? oTextSelectionGroup[iSelectionGroup].g_oTextSelections[iSelection].g_oTextData : oTextSelectionGroup[iSelectionGroup].g_oTextSelections[iSelection].g_oTextData;

		// Determine if this is at character level accuracy.
		var bCharacterLevelAccuracy = g_oTextCompare.g_iAccuracy == g_oTextCompare.eAccuracyLevels.Character;

		// Initialise the text highlight removal.
		g_oTextTools.initialiseTextTool(g_oTextTools.g_eToolTypes.RemoveTextHighlight, eAnnotationTypes.TextHighlight, bCharacterLevelAccuracy, bCompareCanvas, g_oTextCompare.g_szHighlightColour);
	};

	/**
	 * Initialises the text compare area highlight tool.
	 * @param {bool} bCompareCanvas - If this is the compare canvas.
	 * @param {int} iAddToExistingGroup - Id of an existing group if we are adding to an existing group.
	 */
	TextCompare.prototype.initialiseTextCompareAreaHighlight = function (bCompareCanvas, iAddToExistingGroup) {

		// Re-render both annotation canvas before starting the highlight.
		g_oTextCompare.renderTextCompareAnnotations();

		// Set that this will be a new annotation.
		g_oTextTools.g_iExistingAnnotationId = null;

		// Set if this will be added to an existing group.
		g_oTextCompare.g_iAddHighlightToExistingGroup = iAddToExistingGroup;

		// Set up the new annotation text.
		g_oTextTools.g_oAnnotationText = [];

		// Determine if this is at character level accuracy.
		var bCharacterLevelAccuracy = g_oTextCompare.g_iAccuracy == g_oTextCompare.eAccuracyLevels.Character;

		// Initialise the text highlight.
		g_oTextTools.initialiseTextTool(g_oTextTools.g_eToolTypes.AreaTextHighlight, eAnnotationTypes.TextHighlight, bCharacterLevelAccuracy, bCompareCanvas, g_oTextCompare.g_szHighlightColour);
	};

	/**
	 * Initialises the text compare add tool.
	 * @param {int} iSelectionGroup - The selection group.
	 * @param {int} iSelection - The selection.
	 * @param {bool} bParagraphHighlight - If this is a paragraph highlight.
	 * @param {bool} bCompareCanvas - If this is the compare canvas.
	 */
	TextCompare.prototype.initialiseTextCompareAddArea = function (iSelectionGroup, iSelection, bParagraphHighlight, bCompareCanvas) {

		// Set that this will be an edit to an existing annotation.
		g_oTextTools.g_iExistingAnnotationId = {
			"selectionGroup": iSelectionGroup,
			"selection": iSelection
		};

		// Set that this will not be added to an existing group.
		g_oTextCompare.g_iAddHighlightToExistingGroup = null;

		// Get the text selection group we are going to add to.
		var oTextSelectionGroup = g_oTextCompare.getTextSelectionGroup(bCompareCanvas);

		// Set up the new annotation text.
		g_oTextTools.g_oAnnotationText = bCompareCanvas ? oTextSelectionGroup[iSelectionGroup].g_oTextSelections[iSelection].g_oTextData : oTextSelectionGroup[iSelectionGroup].g_oTextSelections[iSelection].g_oTextData;

		// Determine if this is at character level accuracy.
		var bCharacterLevelAccuracy = g_oTextCompare.g_iAccuracy == g_oTextCompare.eAccuracyLevels.Character;

		// Initialise the text highlight.
		g_oTextTools.initialiseTextTool(g_oTextTools.g_eToolTypes.AreaTextHighlight, eAnnotationTypes.TextHighlight, bCharacterLevelAccuracy, bCompareCanvas, g_oTextCompare.g_szHighlightColour);
	};

	/**
	 * Ends a text compare highlight.
	 */
	TextCompare.prototype.endTextCompareHighlight = function () {

		// Set that the next highlight will not be added to an existing group.
		g_oTextCompare.g_iAddHighlightToExistingGroup = null;

		// If we want to automatically activate the pan tool.
		if (g_oTextCompare.g_bAutoActivatePanTool) {

			// Set the user back to the pan tool.
			setTimeout(function () { $("#btnPan_Viewer_TextCompare").click(); }, 500);
		}
	};

	/**
	 * Gets a text selection group.
	 * @param {boolean} bCompare - If this is the compare canvas.
	 */
	TextCompare.prototype.getTextSelectionGroup = function (bCompare) {
		return bCompare ? g_oTextCompare.g_oCompareTextSelectionGroups : g_oTextCompare.g_oArtworkTextSelectionGroups;
	};

	/**
	 * Adds text data.
	 * @param {object} oTextData - The text data.
	 * @param {boolean} bCompareCanvas - If this is the compare canvas.
	 * @param {boolean} bAutoSelect - If auto selection is enabled.
	 * @param {boolean} bRenderAnnotations - If annotations should be rendered.
	 */
	TextCompare.prototype.addTextData = function (oTextData, bCompareCanvas, bAutoSelect, bRenderAnnotations) {

		// Unselect the highlight buttons.
		$("#btnTextHighlight_Viewer_TextCompare").removeClass("selectedButton");
		$("#btnTextHighlight_Compare_TextCompare").removeClass("selectedButton");

		// Ensure we have text data to be added.
		if (oTextData.length == 0) {

			// Close any open info bubbles.
			g_oGreenlightMessages.closeBubbles();

			// End the compare highlight.
			g_oTextCompare.endTextCompareHighlight();
			return;
		}

		// Get the artwork and annotation canvas.
		var oArtworkCanvas = !bCompareCanvas ? g_oViewer.g_oArtworkViewer : g_oCompare.g_oCompareViewer;
		var oAnnotationCanvas = !bCompareCanvas ? g_oViewer.g_oAnnotationViewer : g_oCompare.g_oCompareAnnotationViewer;

		// Get the text selection groups we are going to add to.
		var oTextSelectionGroups = g_oTextCompare.getTextSelectionGroup(bCompareCanvas);

		// Determine the group order to be used.
		var iGroupOrder = g_oTextCompare.getHighestTextSelectionGroup(!bCompareCanvas, bCompareCanvas) + 1;

		// If we are adding this to an existing group.
		if (g_oTextCompare.g_iAddHighlightToExistingGroup != null && g_oTextCompare.g_iAddHighlightToExistingGroup >= 0 && g_oTextCompare.g_iAddHighlightToExistingGroup < oTextSelectionGroups.length) {

			// Add the text selection to the existing group.
			oTextSelectionGroups[g_oTextCompare.g_iAddHighlightToExistingGroup].g_oTextSelections.push(new g_oTextCompare.textSelection(oTextData, oArtworkCanvas, oAnnotationCanvas));

			// If we have auto selection enabled, start the auto selection process.
			if (bAutoSelect) {
				var oCompareCanvas = bCompareCanvas ? g_oViewer.g_oArtworkViewer : g_oCompare.g_oCompareViewer;
				var szJoinedSelectionText = g_oTextCompare.getJoinedSelectionsText(oTextSelectionGroups[g_oTextCompare.g_iAddHighlightToExistingGroup].g_oTextSelections);
				g_oTextCompare_AutoSelection.startAutoSelection(szJoinedSelectionText, bCompareCanvas, oCompareCanvas, oArtworkCanvas, oTextSelectionGroups[g_oTextCompare.g_iAddHighlightToExistingGroup]);
			}

			// Dont add the next highlight to an existing group.
			g_oTextCompare.g_iAddHighlightToExistingGroup = null;
		}
		// If we are adding this to a new group.
		else {
			// Create a new selection group.
			var oNewSelectionGroup = new g_oTextCompare.textSelectionGroup(iGroupOrder);

			var selectionText = new g_oTextCompare.textSelection(oTextData, oArtworkCanvas, oAnnotationCanvas);

			// Add a new selection to the group.
			oNewSelectionGroup.g_oTextSelections.push(selectionText);

			// Add the new selection group to the list.
			oTextSelectionGroups.push(oNewSelectionGroup);

			// If we have auto selection enabled, start the auto selection process.
			if (bAutoSelect) {
				var oCompareCanvas = bCompareCanvas ? g_oViewer.g_oArtworkViewer : g_oCompare.g_oCompareViewer;
				var oSelectionText = g_oSettings.g_oUserSettings.TextComparisonAccuracy == 2 ? selectionText.g_oSelectionCharactersText : selectionText.g_oSelectionWordsText;
				g_oTextCompare_AutoSelection.startAutoSelection(oSelectionText, bCompareCanvas, oCompareCanvas, oArtworkCanvas, oNewSelectionGroup);
			}
		}

		// Render the text compare annotations if required.
		if (bRenderAnnotations) {
			g_oTextCompare.renderTextCompareAnnotations();
		}

		// End the compare highlight.
		g_oTextCompare.endTextCompareHighlight();
	};

	/**
	 * Updates the text data.
	 * @param {object} oTextData - The text data.
	 * @param {boolean} bCompareCanvas - If this is the compare canvas.
	 */
	TextCompare.prototype.updateTextData = function (oTextData, bCompareCanvas, autoSelect) {

		// Get the artwork and annotation canvas.
		var oArtworkCanvas = !bCompareCanvas ? g_oViewer.g_oArtworkViewer : g_oCompare.g_oCompareViewer;
		var oAnnotationCanvas = !bCompareCanvas ? g_oViewer.g_oAnnotationViewer : g_oCompare.g_oCompareAnnotationViewer;

		// Get the text selection groups for the canvas.
		var oTextSelectionGroups = g_oTextCompare.getTextSelectionGroup(bCompareCanvas);

		// Get the selection group we are going to update.
		var oTextSelectionGroup = oTextSelectionGroups[g_oTextTools.g_iExistingAnnotationId.selectionGroup];

		// Exit the function early if not found.
		if (!oTextSelectionGroup) {
			return;
		}

		// Get and update the selection.
		var selectionText = new g_oTextCompare.textSelection(oTextData, oArtworkCanvas, oAnnotationCanvas);		
		oTextSelectionGroup.g_oTextSelections[g_oTextTools.g_iExistingAnnotationId.selection] = selectionText;

		// If we have auto select enabled, start the auto selection if we are not a linked selection.
		if (autoSelect && (typeof oTextSelectionGroup.isAutoSelected === "undefined" || !oTextSelectionGroup.isAutoSelected)) {		
			var oCompareCanvas = bCompareCanvas ? g_oViewer.g_oArtworkViewer : g_oCompare.g_oCompareViewer;
			var szJoinedSelectionText = g_oTextCompare.getJoinedSelectionsText(oTextSelectionGroup.g_oTextSelections);
			g_oTextCompare_AutoSelection.startAutoSelection(szJoinedSelectionText, bCompareCanvas, oCompareCanvas, oArtworkCanvas, oTextSelectionGroup);
		}

		// Render the text compare annotations.
		g_oTextCompare.renderTextCompareAnnotations();

		// End the compare highlight if we are not going to automatically activate this again.
		if (g_oTextCompare.g_oAutoActivateTextHighlight == null) {
			g_oTextCompare.endTextCompareHighlight();
		}
	};

	/**
	* Joins the text selections into a single string.
	* @param {Array} g_oTextSelections - The array of text selections.
	* @returns {string} - The joined selection text.
	*/
	TextCompare.prototype.getJoinedSelectionsText = function (g_oTextSelections) {

		// Outline the string to store the joined selections text.
		var szJoinedSelectionText = "";

		// Loop through each of the selections and join the text.
		for (var i = 0; i < g_oTextSelections.length; i++) {

			// Extract the selection.
			var oSelection = g_oTextSelections[i];

			// Get the text based on the accuracy.
			var oSelectionText = g_oSettings.g_oUserSettings.TextComparisonAccuracy == 2 ? oSelection.g_oSelectionCharactersText : oSelection.g_oSelectionWordsText;

			// Add the text to the joined selection text.
			szJoinedSelectionText += oSelectionText;

			// Add a space if this is not the last selection.
			if (i !== g_oTextSelections.length - 1) {
				szJoinedSelectionText += " ";
			}
		}

		// Return the joined selection text.
		return szJoinedSelectionText;
	};

	/**
	 * Deletes the text data by group order.
	 * @param {int} iGroupOrder - The order of the group.
	 * @param {int} iSelection - The selection.
	 * @param {boolean} bCompareCanvas - If this is the compare canvas.
	 */
	TextCompare.prototype.deleteTextDataByGroupOrder = function (iGroupOrder, iSelection, bCompareCanvas) {

		// Get the group index from the order.
		var iGroupIndex = g_oTextCompare.getTextSelectionGroupIndexFromOrder(iGroupOrder, bCompareCanvas);

		// Delete the text data.
		g_oTextCompare.deleteTextData(iGroupIndex, iSelection, bCompareCanvas, false);
	};

	/**
	 * Deletes the text data.
	 * @param {int} iSelectionGroup - The selection group.
	 * @param {int} iSelection - The selection.
	 * @param {boolean} bCompareCanvas - The compare canvas.
	 */
	TextCompare.prototype.deleteTextData = function (iSelectionGroup, iSelection, bCompareCanvas, bEnableAutoSelect) {

		// Get the text selection group we are going to remove from.
		var oTextSelectionGroup = g_oTextCompare.getTextSelectionGroup(bCompareCanvas);

		// If we have auto select enabled.
		var bAutoSelect = bEnableAutoSelect && g_oTextCompare_AutoSelection.isAutoSelectionEnabled();

		// Remove the text from the selection group.
		oTextSelectionGroup[iSelectionGroup].g_oTextSelections.splice(iSelection, 1);

		// If this is the last text selection in the group, remove the selection.
		if (oTextSelectionGroup[iSelectionGroup].g_oTextSelections.length == 0) {
			g_oTextCompare_AutoSelection.deleteLinkedSelectionTextData(oTextSelectionGroup[iSelectionGroup], bCompareCanvas, true);
			g_oTextCompare.deleteTextGroup(iSelectionGroup, bCompareCanvas);			
			return;
		}

		// If we have auto select enabled, start the auto selection if we did not delete the full group.
		if (bAutoSelect && !oTextSelectionGroup[iSelectionGroup].isAutoSelected) {
			var oCompareCanvas = bCompareCanvas ? g_oViewer.g_oArtworkViewer : g_oCompare.g_oCompareViewer;
			var oArtworkCanvas = !bCompareCanvas ? g_oViewer.g_oArtworkViewer : g_oCompare.g_oCompareViewer;
			var szJoinedSelectionText = g_oTextCompare.getJoinedSelectionsText(oTextSelectionGroup[iSelectionGroup].g_oTextSelections);

			g_oTextCompare_AutoSelection.startAutoSelection(szJoinedSelectionText, bCompareCanvas, oCompareCanvas, oArtworkCanvas, oTextSelectionGroup[iSelectionGroup]);
		}

		// Render the text compare annotations.
		g_oTextCompare.renderTextCompareAnnotations();
	};

	/**
	 * Deletes a text group.
	 * @param {int} iSelectionGroup - The selection.
	 * @param {boolean} bCompareCanvas - If this is the compare canvas.
	 */
	TextCompare.prototype.deleteTextGroup = function (iSelectionGroup, bCompareCanvas) {

		// Get the text selection group we are going to delete from.
		var oTextSelectionGroup = g_oTextCompare.getTextSelectionGroup(bCompareCanvas);

		// Remove the group.
		oTextSelectionGroup.splice(iSelectionGroup, 1);

		// Drop any unused text groups.
		g_oTextCompare.dropUnusedTextGroups();

		// Render the text compare annotations.
		g_oTextCompare.renderTextCompareAnnotations();
	};

	/**
	 * Drops the unused text groups.
	 */
	TextCompare.prototype.dropUnusedTextGroups = function () {

		// Get the highest text selection group.
		var iHighestTextSelectionGroup = g_oTextCompare.getHighestTextSelectionGroup(true, true);

		// Loop through each of the sections up to the highest.
		for (var i = 1; i <= iHighestTextSelectionGroup; i++) {

			// Get the section for the approval and compare.
			var oApprovalSectionGroup = g_oTextCompare.getTextSelectionGroupFromOrder(i, false);
			var oCompareSectionGroup = g_oTextCompare.getTextSelectionGroupFromOrder(i, true);

			// If the group does not exist on either side.
			if (oApprovalSectionGroup == null && oCompareSectionGroup == null) {

				// Drop this from both the approval and compare.
				g_oTextCompare.dropUnusedTextGroup(i, false);
				g_oTextCompare.dropUnusedTextGroup(i, true);
			}
		}
	};

	/**
	 * Drops the unused text group.
	 * @param {int} iGroup - The group.
	 * @param {boolean} bCompareCanvas - If this is the compare canvas.
	 */
	TextCompare.prototype.dropUnusedTextGroup = function (iGroup, bCompareCanvas) {

		// Get the selection group we are going to drop from.
		var oTextSelectionGroup = g_oTextCompare.getTextSelectionGroup(bCompareCanvas);

		// Loop through each of the groups and drop these down if above the dropped group.
		for (var i = 0; i < oTextSelectionGroup.length; i++) {
			if (oTextSelectionGroup[i].g_iGroupOrder > iGroup) {
				oTextSelectionGroup[i].g_iGroupOrder--;
			}
		}
	};

	/**
	 * Clears the text data.
	 * @param {boolean} bApprovalCanvas - If we are clearing the approval canvas.
	 * @param {boolean} bCompareCanvas - If we are clearing the compare canvas.
	 * @param {boolean} bConfirmed - If we have confirmed the clear.
	 */
	TextCompare.prototype.clearTextData = function (bApprovalCanvas, bCompareCanvas, bConfirmed) {

		// If we have not yet confirmed, display the confirm dialog.
		if (!bConfirmed) {

			// Using in scope variables so these are not updated by references.
			var oCallback = (function (bApprovalCanvas, bCompareCanvas) {
				return function () {
					g_oTextCompare.clearTextData(bApprovalCanvas, bCompareCanvas, true);
				};
			})(bApprovalCanvas, bCompareCanvas);

			// Determine the confirmation text to be shown.
			var szConfirmationText = g_oTextCompare.c_szClearText_All;
			szConfirmationText = bApprovalCanvas && !bCompareCanvas ? g_oTextCompare.c_szClearText_Approval : szConfirmationText;
			szConfirmationText = !bApprovalCanvas && bCompareCanvas ? g_oTextCompare.c_szClearText_Compare : szConfirmationText;

			// Display the confirm bubble.
			g_oGreenlightMessages.displayConfirmBubble(szConfirmationText, oCallback, null);
			return;
		}

		// Keep track of if the linked selection has been deleted.
		var bLinkedSelectionDeleted = false;

		// Get the text selection groups
		var oApprovalTextSelectionGroup = g_oTextCompare.getTextSelectionGroup(false);
		var oCompareTextSelectionGroup = g_oTextCompare.getTextSelectionGroup(true);	

		// If we are clearing for the approval canvas.
		if (bApprovalCanvas) {

			// Loop through each of the approval text selection groups.
			for (var i = 0; i < oApprovalTextSelectionGroup.length; i++) {

				// Delete the linked selection text data if the selection group is not auto-selected and has a valid autoSelectLinkId.
				var bDeleteSuccess = g_oTextCompare_AutoSelection.deleteLinkedSelectionTextData(oApprovalTextSelectionGroup[i], false, false);		
				if (!bLinkedSelectionDeleted) {
					bLinkedSelectionDeleted = bDeleteSuccess;
				}
			}
		}

		// If we are clearing for the compare canvas.
		if (bCompareCanvas) {

			// Loop through each of the compare text selection groups.
			for (var i = 0; i < oCompareTextSelectionGroup.length; i++) {

				// Delete the linked selection text data if the selection group is not auto-selected and has a valid autoSelectLinkId.
				var bDeleteSuccess = g_oTextCompare_AutoSelection.deleteLinkedSelectionTextData(oCompareTextSelectionGroup[i], true, false);
				if (!bLinkedSelectionDeleted) {
					bLinkedSelectionDeleted = bDeleteSuccess;
				}
			}
		}

		// Clear the text data from the canvas.
		if (bApprovalCanvas) {
			oApprovalTextSelectionGroup.splice(0, oApprovalTextSelectionGroup.length);
		}

		// Clear the text data from the canvas.
		if (bCompareCanvas) {
			oCompareTextSelectionGroup.splice(0, oCompareTextSelectionGroup.length);
		}

		// Display a message to the user.
		if (bLinkedSelectionDeleted) {
			g_oGreenlightMessages.displaySuccessBubble(g_oTextCompare_AutoSelection.c_szAutoSelectionDeleted);
		}

		// If we are clearing the approval canvas, reorder the groups for the compare canvas.
		if (bApprovalCanvas && !bCompareCanvas) {
			for (var i = 0; i < oCompareTextSelectionGroup.length; i++) {
				oCompareTextSelectionGroup[i].g_iGroupOrder = i + 1;
			}
		}
		// If we are clearing the compare canvas, reorder the groups for the approval canvas.
		else if (!bApprovalCanvas && bCompareCanvas) {
			for (var i = 0; i < oApprovalTextSelectionGroup.length; i++) {
				oApprovalTextSelectionGroup[i].g_iGroupOrder = i + 1;
			}
		}

		// Render the text compare annotations.
		g_oTextCompare.renderTextCompareAnnotations();
	};

	/**
	 * Renders the text compare annotations.
	 */
	TextCompare.prototype.renderTextCompareAnnotations = function () {

		// Ensure the canvas data has been loaded for both canvas before rendering.
		if (!g_oTextCompare.g_bArtworkTextDataLoaded || !g_oTextCompare.g_bCompareTextDataLoaded) {
			return;
		}

		// If we have not calculated the differences.
		if (!g_oTextCompare.g_bDifferencesCalculated) {

			// If we are actively text highlighting on the approval canvas, call the mouse move event to re-draw the highlighted area.
			if (g_oTextTools.g_bHighlightInProgress && !g_oTextTools.g_bHighlightOnCompareCanvas) {
				$('#AnnotationViewer').trigger('pointermove');
			}
			// Render the text compare annotations for the approval canvas.
			else {
				g_oTextCompare.renderTextCompareCanvasAnnotations(g_oTextCompare.getTextSelectionGroup(false), g_oViewer.g_oArtworkViewer, g_oViewer.g_oAnnotationViewer);
			}

			// If we are actively text highlighting on the compare canvas, call the mouse move event to re-draw the highlighted area.
			if (g_oTextTools.g_bHighlightInProgress && g_oTextTools.g_bHighlightOnCompareCanvas) {
				$('#CompareViewer').trigger('pointermove');
			}
			// Render the text compare annotations for the compare canvas.
			else {
				g_oTextCompare.renderTextCompareCanvasAnnotations(g_oTextCompare.getTextSelectionGroup(true), g_oCompare.g_oCompareViewer, g_oCompare.g_oCompareAnnotationViewer);
			}
		}
		// If we have calculated the differences.
		else {

			// Render the text compare differences for both canvas.
			g_oTextCompare.renderTextCompareCanvasDifferences(g_oTextCompare.getTextSelectionGroup(false), g_oViewer.g_oArtworkViewer, g_oViewer.g_oAnnotationViewer);
			g_oTextCompare.renderTextCompareCanvasDifferences(g_oTextCompare.getTextSelectionGroup(true), g_oCompare.g_oCompareViewer, g_oCompare.g_oCompareAnnotationViewer);

			// Update the differences bar.
			g_oTextCompare.updateDifferencesBar();

			// Update the differences summary.
			g_oTextCompare.updateDifferencesSummary();
		}

		// If we have the text compare div shown.
		if (g_oTextCompare.g_bTextCompareDivShown) {

			// Render the text compare sections.
			g_oTextCompare.renderTextCompareSections();
		}
	};

	/**
	 * Renders the text compare differences.
	 * @param {object} oTextSelectionGroup - The text selection group.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextCompare.prototype.renderTextCompareCanvasDifferences = function (oTextSelectionGroup, oArtworkCanvas, oAnnotationCanvas) {

		// Remove any existing highlights.
		g_oTextTools.removeHighlights(oAnnotationCanvas);

		// Check if we have selections.
		if (oTextSelectionGroup.length == 0) {
			return;
		}

		// Loop through each of the text selections.
		for (var i = 0; i < oTextSelectionGroup.length; i++) {

			// Check we have differences.
			if (oTextSelectionGroup[i].g_oTextDifferences == null) {
				continue;
			}

			// Loop through each of the differences.
			for (var j = 0; j < oTextSelectionGroup[i].g_oTextDifferences.length; j++) {

				// Render the text compare difference.
				g_oTextCompare.renderTextCompareCanvasDifference(oTextSelectionGroup[i].g_oTextDifferences[j], oTextSelectionGroup[i].g_iGroupOrder, oTextSelectionGroup[i].g_oTextSelections, oArtworkCanvas, oAnnotationCanvas);
			}
		}
	};

	/**
	 * Renders the text compare canvas differences.
	 * @param {object} oDifference - The difference.
	 * @param {int} iGroup - The group.
	 * @param {object} oTextSelections - The text selections.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextCompare.prototype.renderTextCompareCanvasDifference = function (oDifference, iGroup, oTextSelections, oArtworkCanvas, oAnnotationCanvas) {

		// If we are stepping through differences and this is not the matching difference, skip this render.
		if (g_oTextCompare.g_iStepThroughDifference != null && oDifference.g_iDifferenceNumber != g_oTextCompare.g_iStepThroughDifference) {
			return;
		}

		// If we have calculated the differences to the word level.
		if (g_oTextCompare.g_iAccuracy == g_oTextCompare.eAccuracyLevels.Word) {

			// Render the difference words.
			g_oTextCompare.renderTextCompareCanvasDifference_Words(oDifference, iGroup, oTextSelections, oArtworkCanvas, oAnnotationCanvas);
		}
		// If we have calculated the differences to a character level.
		else {

			// Render the difference characters.
			g_oTextCompare.renderTextCompareCanvasDifference_Characters(oDifference, iGroup, oTextSelections, oArtworkCanvas, oAnnotationCanvas);
		}
	};

	/**
	 * Renders the text compare word canvas differences.
	 * @param {object} oDifference - The difference.
	 * @param {int} iGroup - The group.
	 * @param {object} oTextSelections - The text selections.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextCompare.prototype.renderTextCompareCanvasDifference_Words = function (oDifference, iGroup, oTextSelections, oArtworkCanvas, oAnnotationCanvas) {

		// Keep track of a list of words that are part of this difference.
		var oDifferenceWords = g_oTextCompare.getWordsForTextDifference(oTextSelections, oDifference.g_iDifferenceOrder, oArtworkCanvas.g_iCurrentPage);

		// Keep track of a list of markers that are part of this difference.
		var oDifferenceMarkers = g_oTextCompare.getWordMarkersForTextDifference(oTextSelections, oDifference.g_iDifferenceOrder, oArtworkCanvas.g_iCurrentPage);

		// Ensure we have words to highlight.
		if (oDifferenceWords.length == 0 && oDifferenceMarkers == 0) {
			return;
		}

		// If we have markers for this difference.
		if (oDifferenceMarkers.length > 0) {

			// Highlight only the marker.
			g_oTextCompare.highlightTextCompareMarker(oDifference, iGroup, oDifferenceMarkers, oArtworkCanvas, oAnnotationCanvas);
			return;
		}

		// Highlight each of the difference words.
		g_oTextCompare.highlightTextCompareWords(oDifferenceWords, oArtworkCanvas, oAnnotationCanvas);

		// Get the bounds of the word list for this difference.
		var oWordBounds = g_oTextTools.getWordListBounds(oDifferenceWords, oArtworkCanvas, oAnnotationCanvas);

		// Get the annotation context.
		var oAnnotationContext = oAnnotationCanvas.g_oContext;

		// Get the width and shift of the artwork canvas.
		var iImageObjW = oArtworkCanvas.g_iScaledImageWidth;
		var iImageObjH = oArtworkCanvas.g_iScaledImageHeight;
		var iCenterShift_x = oArtworkCanvas.g_iCenterShift_x;
		var iCenterShift_y = oArtworkCanvas.g_iCenterShift_y;

		// Calculate the compare offset on the artwork canvas.
		var co = g_oCompare.calculateCompareOffsetInPx(oArtworkCanvas);
		var compareOffset_x = co.x;
		var compareOffset_y = co.y;

		// Get the compare rotation of the canvas.
		var iCompareRotation = g_oCompare.getCompareRotation(oArtworkCanvas);

		// If we have a compare rotation of 90 or 270, swap the image object width and height.
		if (iCompareRotation == 90 || iCompareRotation == 270) {
			iImageObjW = oArtworkCanvas.g_iScaledImageHeight;
			iImageObjH = oArtworkCanvas.g_iScaledImageWidth;
		}

		// Determine the annotation colour.
		var annotationColour = g_oTextCompare.getAnnotationColourFromDifferenceStatus(oDifference.g_iDifferenceStatus);

		// Set the highlight colour.
		g_oTextTools.setHighlightColour(annotationColour);

		// Extract the annotation bound points.
		var newXBegin = ((oWordBounds.x1 * iImageObjW)) + iCenterShift_x + compareOffset_x;
		var newYBegin = ((oWordBounds.y1 * iImageObjH)) + iCenterShift_y + compareOffset_y;
		var newXEnd = ((oWordBounds.x2 * iImageObjW)) + iCenterShift_x + compareOffset_x;
		var newYEnd = ((oWordBounds.y2 * iImageObjH)) + iCenterShift_y + compareOffset_y;

		// Determine the begin points and the distance.
		var beginPoint_x = Math.min(newXBegin, newXEnd);
		var beginPoint_y = Math.min(newYBegin, newYEnd);
		var dist_x = Math.abs(newXBegin - newXEnd);
		var dist_y = Math.abs(newYBegin - newYEnd);

		// Create the path.
		oAnnotationContext.beginPath();
		oAnnotationContext.rect(beginPoint_x, beginPoint_y, dist_x, dist_y);
		oAnnotationContext.lineWidth = g_oAnnotations.g_iAnnotationWidth;
		oAnnotationContext.strokeStyle = annotationColour;
		oAnnotationContext.setLineDash([]);

		// Stroke.
		oAnnotationContext.stroke();

		// Render the annotation number.
		var szDifferenceText = g_oTextCompare.c_szTextCompareGroupText + " " + (iGroup).toString() + " (" + g_oTextCompare.c_szTextCompareDifferenceText + " " + (oDifference.g_iDifferenceOrder).toString() + ")";
		oAnnotationContext.font = g_oAnnotations.g_iAnnotationNumberFontSize + "px Varela Round";
		oAnnotationContext.textBaseline = "bottom";
		oAnnotationContext.textalign = "center";
		var areaTextSize = oAnnotationContext.measureText(szDifferenceText);

		// Set the drawing style.
		oAnnotationContext.fillStyle = annotationColour;
		oAnnotationContext.setLineDash([]);

		// Determine the rotation to draw the difference text.
		switch (oAnnotationCanvas.g_iRotDegrees) {

			case 180:
				// Add the number background.
				oAnnotationContext.beginPath();
				oAnnotationContext.rect(oWordBounds.x1 * iImageObjW + iCenterShift_x + compareOffset_x - g_oAnnotations.g_oAnnotationNumberBox.iBeginX, oWordBounds.y1 * iImageObjH + iCenterShift_y + compareOffset_y - g_oAnnotations.g_oAnnotationNumberBox.iBeginY, areaTextSize.width + g_oAnnotations.g_oAnnotationNumberBox.iWidth, g_oAnnotations.g_oAnnotationNumberBox.iHeight);
				oAnnotationContext.fill();
				oAnnotationContext.fillStyle = g_oTextCompare.getHandleFillStyle(annotationColour);

				// Save the context.
				oAnnotationContext.save();

				// Add the text.
				oAnnotationContext.translate(oWordBounds.x1 * iImageObjW + iCenterShift_x + compareOffset_x - g_oAnnotations.g_oAnnotationNumberBox.iBeginX, oWordBounds.y1 * iImageObjH + iCenterShift_y + compareOffset_y - g_oAnnotations.g_oAnnotationNumberBox.iBeginY);
				oAnnotationContext.scale(-1, -1);
				oAnnotationContext.fillText(szDifferenceText, -areaTextSize.width, 0);

				// Restore the context.
				oAnnotationContext.restore();
				break;

			default:
				// Add the number background.
				oAnnotationContext.beginPath();
				oAnnotationContext.rect(oWordBounds.x1 * iImageObjW + iCenterShift_x + compareOffset_x - g_oAnnotations.g_oAnnotationNumberBox.iBeginX, oWordBounds.y1 * iImageObjH + iCenterShift_y + compareOffset_y - g_oAnnotations.g_oAnnotationNumberBox.iBeginY, areaTextSize.width + g_oAnnotations.g_oAnnotationNumberBox.iWidth, g_oAnnotations.g_oAnnotationNumberBox.iHeight);
				oAnnotationContext.fill();
				oAnnotationContext.fillStyle = g_oTextCompare.getHandleFillStyle(annotationColour);

				// Add the text.
				oAnnotationContext.fillText(szDifferenceText, oWordBounds.x1 * iImageObjW + iCenterShift_x + compareOffset_x - g_oAnnotations.g_oAnnotationNumberText.iBeginX, oWordBounds.y1 * iImageObjH + iCenterShift_y + compareOffset_y - g_oAnnotations.g_oAnnotationNumberText.iBeginY);
				break;
		}
	};

	/**
	 * Renders the text compare character canvas differences.
	 * @param {object} oDifference - The difference.
	 * @param {int} iGroup - The group.
	 * @param {object} oTextSelections - The text selections.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextCompare.prototype.renderTextCompareCanvasDifference_Characters = function (oDifference, iGroup, oTextSelections, oArtworkCanvas, oAnnotationCanvas) {

		// Keep track of a list of characters that are part of this difference.
		var oDifferenceCharacters = g_oTextCompare.getCharactersForTextDifference(oTextSelections, oDifference.g_iDifferenceOrder, oArtworkCanvas.g_iCurrentPage);

		// Keep track of a list of markers that are part of this difference.
		var oDifferenceMarkers = g_oTextCompare.getCharacterMarkersForTextDifference(oTextSelections, oDifference.g_iDifferenceOrder, oArtworkCanvas.g_iCurrentPage);

		// Ensure we have characters to highlight.
		if (oDifferenceCharacters.length == 0 && oDifferenceMarkers.length == 0) {
			return;
		}

		// If these characters are markers.
		if (oDifferenceMarkers.length > 0) {

			// Highlight only the marker.
			g_oTextCompare.highlightTextCompareMarker(oDifference, iGroup, oDifferenceMarkers, oArtworkCanvas, oAnnotationCanvas);
			return;
		}

		// Highlight each of the difference characters.
		g_oTextCompare.highlightTextCompareCharacters(oDifferenceCharacters, oArtworkCanvas, oAnnotationCanvas);

		// Get the bounds of the character list for this difference.
		var oCharacterBounds = g_oTextTools.getCharacterListBounds(oDifferenceCharacters, oArtworkCanvas, oAnnotationCanvas);

		// Get the annotation context.
		var oAnnotationContext = oAnnotationCanvas.g_oContext;

		// Get the width and shift of the artwork canvas.
		var iImageObjW = oArtworkCanvas.g_iScaledImageWidth;
		var iImageObjH = oArtworkCanvas.g_iScaledImageHeight;
		var iCenterShift_x = oArtworkCanvas.g_iCenterShift_x;
		var iCenterShift_y = oArtworkCanvas.g_iCenterShift_y;

		// Calculate the compare offset on the artwork canvas.
		var co = g_oCompare.calculateCompareOffsetInPx(oArtworkCanvas);
		var compareOffset_x = co.x;
		var compareOffset_y = co.y;

		// Get the compare rotation of the canvas.
		var iCompareRotation = g_oCompare.getCompareRotation(oArtworkCanvas);

		// If we have a compare rotation of 90 or 270, swap the image object width and height.
		if (iCompareRotation == 90 || iCompareRotation == 270) {
			iImageObjW = oArtworkCanvas.g_iScaledImageHeight;
			iImageObjH = oArtworkCanvas.g_iScaledImageWidth;
		}

		// Determine the annotation colour.
		var annotationColour = g_oTextCompare.getAnnotationColourFromDifferenceStatus(oDifference.g_iDifferenceStatus);

		// Set the highlight colour.
		g_oTextTools.setHighlightColour(annotationColour);

		// Extract the annotation bound points.
		var newXBegin = ((oCharacterBounds.x1 * iImageObjW)) + iCenterShift_x + compareOffset_x;
		var newYBegin = ((oCharacterBounds.y1 * iImageObjH)) + iCenterShift_y + compareOffset_y;
		var newXEnd = ((oCharacterBounds.x2 * iImageObjW)) + iCenterShift_x + compareOffset_x;
		var newYEnd = ((oCharacterBounds.y2 * iImageObjH)) + iCenterShift_y + compareOffset_y;

		// Determine the begin points and the distance.
		var beginPoint_x = Math.min(newXBegin, newXEnd);
		var beginPoint_y = Math.min(newYBegin, newYEnd);
		var dist_x = Math.abs(newXBegin - newXEnd);
		var dist_y = Math.abs(newYBegin - newYEnd);

		// Create the path.
		oAnnotationContext.beginPath();
		oAnnotationContext.rect(beginPoint_x, beginPoint_y, dist_x, dist_y);
		oAnnotationContext.lineWidth = g_oAnnotations.g_iAnnotationWidth;
		oAnnotationContext.strokeStyle = annotationColour;
		oAnnotationContext.setLineDash([]);

		// Stroke.
		oAnnotationContext.stroke();

		// Render the annotation number.
		var szDifferenceText = g_oTextCompare.c_szTextCompareGroupText + " " + (iGroup).toString() + " (" + g_oTextCompare.c_szTextCompareDifferenceText + " " + (oDifference.g_iDifferenceOrder).toString() + ")";
		oAnnotationContext.font = g_oAnnotations.g_iAnnotationNumberFontSize + "px Varela Round";
		oAnnotationContext.textBaseline = "bottom";
		oAnnotationContext.textalign = "center";
		var areaTextSize = oAnnotationContext.measureText(szDifferenceText);

		// Start the drawing.
		oAnnotationContext.fillStyle = annotationColour;
		oAnnotationContext.setLineDash([]);

		// Determine the rotation to draw the difference text.
		switch (oAnnotationCanvas.g_iRotDegrees) {

			case 180:
				// Add the number background.
				oAnnotationContext.beginPath();
				oAnnotationContext.rect(oCharacterBounds.x1 * iImageObjW + iCenterShift_x + compareOffset_x - g_oAnnotations.g_oAnnotationNumberBox.iBeginX, oCharacterBounds.y1 * iImageObjH + iCenterShift_y + compareOffset_y - g_oAnnotations.g_oAnnotationNumberBox.iBeginY, areaTextSize.width + g_oAnnotations.g_oAnnotationNumberBox.iWidth, g_oAnnotations.g_oAnnotationNumberBox.iHeight);
				oAnnotationContext.fill();
				oAnnotationContext.fillStyle = g_oTextCompare.getHandleFillStyle(annotationColour);

				// Save the context.
				oAnnotationContext.save();

				// Add the text.
				oAnnotationContext.translate(oCharacterBounds.x1 * iImageObjW + iCenterShift_x + compareOffset_x - g_oAnnotations.g_oAnnotationNumberBox.iBeginX, oCharacterBounds.y1 * iImageObjH + iCenterShift_y + compareOffset_y - g_oAnnotations.g_oAnnotationNumberBox.iBeginY);
				oAnnotationContext.scale(-1, -1);
				oAnnotationContext.fillText(szDifferenceText, -areaTextSize.width, 0);

				// Restore the context.
				oAnnotationContext.restore();
				break;

			default:
				// Add the number background.
				oAnnotationContext.beginPath();
				oAnnotationContext.rect(oCharacterBounds.x1 * iImageObjW + iCenterShift_x + compareOffset_x - g_oAnnotations.g_oAnnotationNumberBox.iBeginX, oCharacterBounds.y1 * iImageObjH + iCenterShift_y + compareOffset_y - g_oAnnotations.g_oAnnotationNumberBox.iBeginY, areaTextSize.width + g_oAnnotations.g_oAnnotationNumberBox.iWidth, g_oAnnotations.g_oAnnotationNumberBox.iHeight);
				oAnnotationContext.fill();
				oAnnotationContext.fillStyle = g_oTextCompare.getHandleFillStyle(annotationColour);

				// Add the number text.
				oAnnotationContext.fillText(szDifferenceText, oCharacterBounds.x1 * iImageObjW + iCenterShift_x + compareOffset_x - g_oAnnotations.g_oAnnotationNumberText.iBeginX, oCharacterBounds.y1 * iImageObjH + iCenterShift_y + compareOffset_y - g_oAnnotations.g_oAnnotationNumberText.iBeginY);
				break;
		}
	};

	/**
	 * Highlights a text compare marker.
	 * @param {object} oDifference - The difference.
	 * @param {int} iGroup - The group.
	 * @param {object} oMarkerWords - The marker words.
	 * @param {object} oArtworkCanvas - The artwork canvcas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextCompare.prototype.highlightTextCompareMarker = function (oDifference, iGroup, oMarkerWords, oArtworkCanvas, oAnnotationCanvas) {

		// Get the bounds of the word list for this difference.
		var oWordBounds = g_oTextTools.getWordListBounds(oMarkerWords, oArtworkCanvas, oAnnotationCanvas);

		// Get the annotation context.
		var oAnnotationContext = oAnnotationCanvas.g_oContext;

		// Get the width and shift of the artwork canvas.
		var iImageObjW = oArtworkCanvas.g_iScaledImageWidth;
		var iImageObjH = oArtworkCanvas.g_iScaledImageHeight;
		var iCenterShift_x = oArtworkCanvas.g_iCenterShift_x;
		var iCenterShift_y = oArtworkCanvas.g_iCenterShift_y;

		// Calculate the compare offset on the artwork canvas.
		var co = g_oCompare.calculateCompareOffsetInPx(oArtworkCanvas);
		var compareOffset_x = co.x;
		var compareOffset_y = co.y;

		// Get the compare rotation of the canvas.
		var iCompareRotation = g_oCompare.getCompareRotation(oArtworkCanvas);

		// If we have a compare rotation of 90 or 270, swap the image object width and height.
		if (iCompareRotation == 90 || iCompareRotation == 270) {
			iImageObjW = oArtworkCanvas.g_iScaledImageHeight;
			iImageObjH = oArtworkCanvas.g_iScaledImageWidth;
		}

		// Set the annotation colour.
		var annotationColour = g_oTextCompare.getAnnotationColourFromDifferenceStatus(oDifference.g_iDifferenceStatus);

		// Set the fill and stroke style.
		oAnnotationContext.strokeStyle = annotationColour;
		oAnnotationContext.fillStyle = annotationColour;

		// Extract the annotation bound points.
		var newXBegin = ((oWordBounds.x1 * iImageObjW)) + iCenterShift_x + compareOffset_x;
		var newYBegin = ((oWordBounds.y1 * iImageObjH)) + iCenterShift_y + compareOffset_y;
		var newXEnd = ((oWordBounds.x2 * iImageObjW)) + iCenterShift_x + compareOffset_x;
		var newYEnd = ((oWordBounds.y2 * iImageObjH)) + iCenterShift_y + compareOffset_y;

		// Calculate the points to be used.
		var beginPoint_x = 0;
		var beginPoint_y = 0;

		// If this is a marker before, use the minumum.
		if (oMarkerWords[0].marker == g_oTextCompare.eMarkerTypes.MarkerBefore) {
			beginPoint_x = Math.min(newXBegin, newXEnd) - (g_oTextCompare.g_iMarkerSize / 2);
			beginPoint_y = Math.min(newYBegin, newYEnd) - (g_oTextCompare.g_iMarkerSize / 2);
		}
		// If this is a marker after, use the maximum.
		else {
			beginPoint_x = Math.max(newXBegin, newXEnd) - (g_oTextCompare.g_iMarkerSize / 2);
			beginPoint_y = Math.min(newYBegin, newYEnd) - (g_oTextCompare.g_iMarkerSize / 2);
		}

		// Create the marker triangle.
		oAnnotationContext.beginPath();
		oAnnotationContext.moveTo(beginPoint_x, beginPoint_y);
		oAnnotationContext.lineTo(beginPoint_x + g_oTextCompare.g_iMarkerSize, beginPoint_y);
		oAnnotationContext.lineTo(beginPoint_x + (g_oTextCompare.g_iMarkerSize / 2), beginPoint_y + (g_oTextCompare.g_iMarkerSize / 2));
		oAnnotationContext.lineTo(beginPoint_x, beginPoint_y);

		// Fill the triangle.
		oAnnotationContext.fill();

		// Render the annotation number.
		var szMarkerText = g_oTextCompare.c_szTextCompareGroupText + " " + (iGroup).toString() + " (" + g_oTextCompare.c_szTextCompareDifferenceText + " " + (oDifference.g_iDifferenceOrder).toString() + ")";
		oAnnotationContext.font = g_oAnnotations.g_iAnnotationNumberFontSize + "px Varela Round";
		oAnnotationContext.textBaseline = "bottom";
		oAnnotationContext.textalign = "center";
		var areaTextSize = oAnnotationContext.measureText(szMarkerText);

		// Start the drawing.
		oAnnotationContext.fillStyle = annotationColour;
		oAnnotationContext.setLineDash([]);

		// Determine the rotation to draw the difference text.
		switch (oAnnotationCanvas.g_iRotDegrees) {

			case 180:
				// Add the number background.
				oAnnotationContext.beginPath();
				oAnnotationContext.rect(beginPoint_x - g_oAnnotations.g_oAnnotationNumberBox.iBeginX, oWordBounds.y1 * iImageObjH + iCenterShift_y + compareOffset_y - g_oAnnotations.g_oAnnotationNumberBox.iBeginY, areaTextSize.width + g_oAnnotations.g_oAnnotationNumberBox.iWidth, g_oAnnotations.g_oAnnotationNumberBox.iHeight);
				oAnnotationContext.fill();
				oAnnotationContext.fillStyle = g_oTextCompare.getHandleFillStyle(annotationColour);

				// Save the context.
				oAnnotationContext.save();

				// Add the number text.
				oAnnotationContext.translate(beginPoint_x - g_oAnnotations.g_oAnnotationNumberText.iBeginX, oWordBounds.y1 * iImageObjH + iCenterShift_y + compareOffset_y - g_oAnnotations.g_oAnnotationNumberBox.iBeginY);
				oAnnotationContext.scale(-1, -1);
				oAnnotationContext.fillText(szMarkerText, -areaTextSize.width, 0);

				// Restore the context.
				oAnnotationContext.restore();
				break;

			default:
				// Add the number background.
				oAnnotationContext.beginPath();
				oAnnotationContext.rect(beginPoint_x - g_oAnnotations.g_oAnnotationNumberBox.iBeginX, oWordBounds.y1 * iImageObjH + iCenterShift_y + compareOffset_y - g_oAnnotations.g_oAnnotationNumberBox.iBeginY, areaTextSize.width + g_oAnnotations.g_oAnnotationNumberBox.iWidth, g_oAnnotations.g_oAnnotationNumberBox.iHeight);
				oAnnotationContext.fill();
				oAnnotationContext.fillStyle = g_oTextCompare.getHandleFillStyle(annotationColour);

				// Add the number text.
				oAnnotationContext.fillText(szMarkerText, beginPoint_x - g_oAnnotations.g_oAnnotationNumberText.iBeginX, oWordBounds.y1 * iImageObjH + iCenterShift_y + compareOffset_y - g_oAnnotations.g_oAnnotationNumberText.iBeginY);
				break;
		}
	};

	/**
	 * Gets the words for a text difference.
	 * @param {object} oTextSelections - The text selections.
	 * @param {int} iDifferece - The difference.
	 * @param {int} iPage - The page.
	 */
	TextCompare.prototype.getWordsForTextDifference = function (oTextSelections, iDifferece, iPage) {

		// Keep track of a list of words that are part of this difference.
		var oDifferenceWords = [];

		// Loop through each of the text selections.
		for (var i = 0; i < oTextSelections.length; i++) {

			// Ensure this selection is on the current page.
			if (iPage != null && oTextSelections[i].g_iPage != iPage) {
				continue;
			}

			// Loop through each of the words in the text selection.
			for (var j = 0; j < oTextSelections[i].g_oSelectionWords.length; j++) {

				// Add this word to the list if it is part of the difference.
				if (oTextSelections[i].g_oSelectionWords[j].difference == iDifferece) {
					oDifferenceWords.push(oTextSelections[i].g_oSelectionWords[j]);
				}
			}
		}

		// Return the difference words.
		return oDifferenceWords;
	};

	/**
	 * Gets the word markers for a text difference.
	 * @param {object} oTextSelections - The text selections.
	 * @param {int} iDifferece - The difference.
	 * @param {int} iPage - The page.
	 */
	TextCompare.prototype.getWordMarkersForTextDifference = function (oTextSelections, iDifferece, iPage) {

		// Keep track of a list of markers that are part of this difference.
		var oDifferenceMarkers = [];

		// Loop through each of the text selections.
		for (var i = 0; i < oTextSelections.length; i++) {

			// Ensure this selection is on the current page.
			if (oTextSelections[i].g_iPage != iPage) {
				continue;
			}

			// Loop through each of the words in the text selection.
			for (var j = 0; j < oTextSelections[i].g_oSelectionWords.length; j++) {

				// Add this marker word to the list if it is part of the difference.
				if (oTextSelections[i].g_oSelectionWords[j].markerDifference == iDifferece) {
					oDifferenceMarkers.push(oTextSelections[i].g_oSelectionWords[j]);
				}
			}
		}

		// Return the difference markers.
		return oDifferenceMarkers;
	};

	/**
	 * Gets the characters for a text difference.
	 * @param {object} oTextSelections - The text selections.
	 * @param {int} iDifferece - The difference.
	 * @param {int} iPage - The page.
	 */
	TextCompare.prototype.getCharactersForTextDifference = function (oTextSelections, iDifferece, iPage) {

		// Keep track of a list of characters that are part of this difference.
		var oDifferenceCharacters = [];

		// Loop through each of the text selections.
		for (var i = 0; i < oTextSelections.length; i++) {

			// Ensure this selection is on the current page.
			if (oTextSelections[i].g_iPage != iPage) {
				continue;
			}

			// Loop through each of the characters in the text selection.
			for (var j = 0; j < oTextSelections[i].g_oSelectionCharacters.length; j++) {

				// Add this character to the list if it is part of the difference.
				if (oTextSelections[i].g_oSelectionCharacters[j].difference == iDifferece) {
					oDifferenceCharacters.push(oTextSelections[i].g_oSelectionCharacters[j]);
				}
			}
		}

		// Return the difference characters.
		return oDifferenceCharacters;
	};

	/**
	 * Gets the character markers for a text difference.
	 * @param {object} oTextSelections - The text selections.
	 * @param {int} iDifferece - The difference.
	 * @param {int} iPage - The page.
	 */
	TextCompare.prototype.getCharacterMarkersForTextDifference = function (oTextSelections, iDifferece, iPage) {

		// Keep track of a list of marker characters that are part of this difference.
		var oMarkerCharacters = [];

		// Loop through each of the text selections.
		for (var i = 0; i < oTextSelections.length; i++) {

			// Ensure this selection is on the current page.
			if (oTextSelections[i].g_iPage != iPage) {
				continue;
			}

			// Loop through each of the characters in the text selection.
			for (var j = 0; j < oTextSelections[i].g_oSelectionCharacters.length; j++) {

				// Add this character to the list if it is a marker of the difference.
				if (oTextSelections[i].g_oSelectionCharacters[j].markerDifference == iDifferece) {
					oMarkerCharacters.push(oTextSelections[i].g_oSelectionCharacters[j]);
				}
			}
		}

		// Return the marker characters.
		return oMarkerCharacters;
	};

	/**
	 * Gets a word list from a character list.
	 * @param {object} oCharacterList - The character list.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextCompare.prototype.getWorldListFromCharacterList = function (oCharacterList, oArtworkCanvas, oAnnotationCanvas) {

		// Keep track of a list of words that are part of this difference.
		var oDifferenceWords = [];

		// Loop through each of the characters.
		for (var i = 0; i < oCharacterList.length; i++) {

			// Check if we already have this word in the list.
			var bWordInList = false;

			// Loop through each of the words, and check if this is already in the list.
			for (var j = 0; j < oDifferenceWords.length; j++) {
				if (oCharacterList[i].paragraph == oDifferenceWords[j].paragraph && oCharacterList[i].line == oDifferenceWords[j].line && oCharacterList[i].word == oDifferenceWords[j].word) {
					bWordInList = true;
					break;
				}
			}

			// If the word was not in the list.
			if (!bWordInList) {

				// Extract the word.
				var oWord = { paragraph: oCharacterList[i].paragraph, line: oCharacterList[i].line, word: oCharacterList[i].word };

				// Add the word to the list.
				oDifferenceWords.push(new g_oTextCompare.textSelectionWord(oWord, oArtworkCanvas, oAnnotationCanvas));
			}
		}

		// Return the difference words.
		return oDifferenceWords;
	};

	/**
	 * Renders the text compare canvas annotations.
	 * @param {object} oTextSelectionGroup - The text selection group.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextCompare.prototype.renderTextCompareCanvasAnnotations = function (oTextSelectionGroup, oArtworkCanvas, oAnnotationCanvas) {

		// Get the annotation context.
		var oAnnotationContext = oAnnotationCanvas.g_oContext;

		// Remove any existing highlights.
		g_oTextTools.removeHighlights(oAnnotationCanvas);

		// Check if we have selections.
		if (oTextSelectionGroup.length == 0) {
			return;
		}

		// Get the width and shift of the artwork canvas.
		var iImageObjW = oArtworkCanvas.g_iScaledImageWidth;
		var iImageObjH = oArtworkCanvas.g_iScaledImageHeight;
		var iCenterShift_x = oArtworkCanvas.g_iCenterShift_x;
		var iCenterShift_y = oArtworkCanvas.g_iCenterShift_y;

		// Calculate the compare offset on the artwork canvas.
		var co = g_oCompare.calculateCompareOffsetInPx(oArtworkCanvas);
		var compareOffset_x = co.x;
		var compareOffset_y = co.y;

		// Get the compare rotation of the canvas.
		var iCompareRotation = g_oCompare.getCompareRotation(oArtworkCanvas);

		// If we have a compare rotation of 90 or 270, swap the image object width and height.
		if (iCompareRotation == 90 || iCompareRotation == 270) {
			iImageObjW = oArtworkCanvas.g_iScaledImageHeight;
			iImageObjH = oArtworkCanvas.g_iScaledImageWidth;
		}

		// Render each of the text selection groups.
		for (var i = 0; i < oTextSelectionGroup.length; i++) {

			// Render each of the text selections.
			for (var j = 0; j < oTextSelectionGroup[i].g_oTextSelections.length; j++) {

				// Extract the selection.
				var oSelectionGroup = oTextSelectionGroup[i].g_oTextSelections[j];

				// Ensure the selection group is on the current page.
				if (oSelectionGroup.g_iPage != oArtworkCanvas.g_iCurrentPage) {
					continue;
				}

				// Set the highlight colour.
				g_oTextTools.setHighlightColour(g_oTextCompare.g_szHighlightColour);

				// Highlight the text content.
				if (g_oTextCompare.g_iAccuracy == g_oTextCompare.eAccuracyLevels.Word) {
					g_oTextCompare.highlightTextCompareWords(oSelectionGroup.g_oSelectionWords, oArtworkCanvas, oAnnotationCanvas);
				}
				else {
					g_oTextCompare.highlightTextCompareCharacters(oSelectionGroup.g_oSelectionCharacters, oArtworkCanvas, oAnnotationCanvas);
				}

				// Extract the bounds to be used.
				var oSelectionBounds = g_oTextCompare.g_iAccuracy == g_oTextCompare.eAccuracyLevels.Word ? oSelectionGroup.g_oSelectionWordsBounds : oSelectionGroup.g_oSelectionCharacterBounds;

				// Extract the annotation bound points.
				var newXBegin = ((oSelectionBounds.x1 * iImageObjW)) + iCenterShift_x + compareOffset_x;
				var newYBegin = ((oSelectionBounds.y1 * iImageObjH)) + iCenterShift_y + compareOffset_y;
				var newXEnd = ((oSelectionBounds.x2 * iImageObjW)) + iCenterShift_x + compareOffset_x;
				var newYEnd = ((oSelectionBounds.y2 * iImageObjH)) + iCenterShift_y + compareOffset_y;

				// Determine the begin points and the distance.
				var beginPoint_x = Math.min(newXBegin, newXEnd);
				var beginPoint_y = Math.min(newYBegin, newYEnd);
				var dist_x = Math.abs(newXBegin - newXEnd);
				var dist_y = Math.abs(newYBegin - newYEnd);

				// Create the path.
				oAnnotationContext.beginPath();
				oAnnotationContext.rect(beginPoint_x, beginPoint_y, dist_x, dist_y);
				oAnnotationContext.lineWidth = g_oAnnotations.g_iAnnotationWidth;
				oAnnotationContext.strokeStyle = g_oTextCompare.g_szHighlightColour;
				oAnnotationContext.setLineDash([]);

				// Stroke.
				oAnnotationContext.stroke();

				// Render the annotation number.
				var annText = (oTextSelectionGroup[i].g_iGroupOrder).toString();
				oAnnotationContext.font = g_oAnnotations.g_iAnnotationNumberFontSize + "px Varela Round";
				oAnnotationContext.textBaseline = "bottom";
				oAnnotationContext.textalign = "center";
				var areaTextSize = oAnnotationContext.measureText(annText);

				// Start the drawing.
				oAnnotationContext.fillStyle = g_oTextCompare.g_szHighlightColour;
				oAnnotationContext.setLineDash([]);
				oAnnotationContext.beginPath();

				// Add the number background.
				oAnnotationContext.rect(oSelectionBounds.x1 * iImageObjW + iCenterShift_x + compareOffset_x - g_oAnnotations.g_oAnnotationNumberBox.iBeginX, oSelectionBounds.y1 * iImageObjH + iCenterShift_y + compareOffset_y - g_oAnnotations.g_oAnnotationNumberBox.iBeginY, areaTextSize.width + g_oAnnotations.g_oAnnotationNumberBox.iWidth, g_oAnnotations.g_oAnnotationNumberBox.iHeight);
				oAnnotationContext.fill();
				oAnnotationContext.fillStyle = g_oTextCompare.getHandleFillStyle(g_oTextCompare.g_szHighlightColour);

				// Add the number text.
				oAnnotationContext.fillText(annText, oSelectionBounds.x1 * iImageObjW + iCenterShift_x + compareOffset_x - g_oAnnotations.g_oAnnotationNumberText.iBeginX, oSelectionBounds.y1 * iImageObjH + iCenterShift_y + compareOffset_y - g_oAnnotations.g_oAnnotationNumberText.iBeginY);

				// Draw each of the annotation handles.
				for (var iHandleType in Object.keys(g_oTextCompare.eTextCompareHandleTypes)) {

					// Calculate the coordinate.
					var handleXCoordinate = oSelectionBounds.x2 * iImageObjW + iCenterShift_x + compareOffset_x - g_oAnnotations.g_oAnnotationNumberBox.iBeginX;
					var handleYCoordinate = oSelectionBounds.y1 * iImageObjH + iCenterShift_y + compareOffset_y - g_oAnnotations.g_oAnnotationNumberBox.iBeginY;

					// Move right based on the handle number.
					handleXCoordinate += (parseInt(iHandleType) * (g_oAnnotationActions.g_iAnnotationDragRectSize + (g_oAnnotations.g_oAnnotationNumberBox.iBeginX / 2)));

					// Draw handle.
					oAnnotationContext.save();
					oAnnotationContext.beginPath();
					oAnnotationContext.fillStyle = g_oTextCompare.g_szHighlightColour;
					oAnnotationContext.strokeStyle = g_oTextCompare.g_szHighlightColour;
					oAnnotationContext.rect(handleXCoordinate, handleYCoordinate, g_oAnnotationActions.g_iAnnotationDragRectSize, g_oAnnotationActions.g_iAnnotationDragRectSize);
					oAnnotationContext.closePath();
					oAnnotationContext.fill();

					// Draw the handle image.
					oAnnotationContext.drawImage(g_oTextCompare.getHandleImage(iHandleType, g_oTextCompare.g_szHighlightColour, oAnnotationCanvas.g_iRotDegrees), handleXCoordinate + 4, handleYCoordinate + 4, g_oAnnotationActions.g_iAnnotationDragRectSize - 8, g_oAnnotationActions.g_iAnnotationDragRectSize - 8);
				}
			}
		}
	};

	/**
	 * Highlights the words for a text compare.
	 * @param {object} oWords - The words to highlight.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextCompare.prototype.highlightTextCompareWords = function (oWords, oArtworkCanvas, oAnnotationCanvas) {

		// Get the text for the document page.
		var oDocumentText = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage);

		// Ensure we have the text.
		if (oDocumentText == null) {
			return;
		}

		// Get the text data for the document page.
		var oTextData = oDocumentText.textData;

		// Keep track of the current and previous word statuses.
		var iPreviousWordStatus = g_oTextCompare.eDifferenceTypes.NoDifference;
		var iCurrentWordStatus = g_oTextCompare.eDifferenceTypes.NoDifference;

		// Highlight each of the words.
		for (var i = 0; i < oWords.length; i++) {

			// Extract the status of the current word.
			iCurrentWordStatus = oWords[i].status;

			// If we have highlighted the previous word on this paragraph line.
			if (i > 0 && oWords[i].word == oWords[i - 1].word + 1 && oWords[i].line == oWords[i - 1].line && oWords[i].paragraph == oWords[i - 1].paragraph) {

				// Get the previous word.
				var oWord = oTextData.Paragraphs[oWords[i].paragraph].Lines[oWords[i].line].Words[oWords[i].word];
				var oPreviousWord = oTextData.Paragraphs[oWords[i - 1].paragraph].Lines[oWords[i - 1].line].Words[oWords[i - 1].word];

				// Determine the coordinates to be used for the space based on the text direction.
				var iX1 = oPreviousWord.X2 < oWord.X1 ? oPreviousWord.X2 : oPreviousWord.X1;
				var iX2 = oPreviousWord.X2 < oWord.X1 ? oWord.X1 : oWord.X2;
				var iY1 = oPreviousWord.Y1 < oWord.Y2 ? oPreviousWord.Y2 : oPreviousWord.Y1;
				var iY2 = oPreviousWord.Y1 < oWord.Y2 ? oWord.Y1 : oWord.Y2;

				// If both this and the previous word have the same status, use this for the space.
				if (iCurrentWordStatus == iPreviousWordStatus) {
					g_oTextTools.setHighlightColour(g_oTextCompare.getAnnotationColourFromDifferenceType(iCurrentWordStatus));
				}
				// Otherwise use the highlight colour.
				else {
					g_oTextTools.setHighlightColour(g_oTextCompare.g_szHighlightColour);
				}

				// Highlight the space between the two words.
				g_oTextTools.highlightElement(iX1, iX2, iY1, iY2, oArtworkCanvas, oAnnotationCanvas);
			}

			// Highlight the word in the correct colour.
			g_oTextTools.setHighlightColour(g_oTextCompare.getAnnotationColourFromDifferenceType(iCurrentWordStatus));
			g_oTextTools.highlightWord(oWords[i].paragraph, oWords[i].line, oWords[i].word, oArtworkCanvas, oAnnotationCanvas);

			// Update the status of the previous word.
			iPreviousWordStatus = iCurrentWordStatus;
		}
	};

	/**
	 * Highlights the characters for a text compare.
	 * @param {object} oCharacters - The characters to highlight.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextCompare.prototype.highlightTextCompareCharacters = function (oCharacters, oArtworkCanvas, oAnnotationCanvas) {

		// Get the text for the document page.
		var oDocumentText = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage);

		// Ensure we have the text.
		if (oDocumentText == null) {
			return;
		}

		// Get the text data for the document page.
		var oTextData = oDocumentText.textData;

		// Keep track of the current and previous character statuses.
		var iPreviousCharacterStatus = g_oTextCompare.eDifferenceTypes.NoDifference;
		var iCurrentCharacterStatus = g_oTextCompare.eDifferenceTypes.NoDifference;

		// Highlight each of the characters.
		for (var i = 0; i < oCharacters.length; i++) {

			// Extract the status of the current character.
			iCurrentCharacterStatus = oCharacters[i].status;

			// If we have highlighted the previous character on this paragraph line.
			if (i > 0 && oCharacters[i].word == oCharacters[i - 1].word + 1 && oCharacters[i].line == oCharacters[i - 1].line && oCharacters[i].paragraph == oCharacters[i - 1].paragraph) {

				// Get the previous character.
				var oCharacter = oTextData.Paragraphs[oCharacters[i].paragraph].Lines[oCharacters[i].line].Words[oCharacters[i].word].Characters[oCharacters[i].character];
				var oPreviousCharacter = oTextData.Paragraphs[oCharacters[i - 1].paragraph].Lines[oCharacters[i - 1].line].Words[oCharacters[i - 1].word].Characters[oCharacters[i - 1].character];

				// Determine the coordinates to be used for the space based on the text direction.
				var iX1 = oPreviousCharacter.X2 < oCharacter.X1 ? oPreviousCharacter.X2 : oPreviousCharacter.X1;
				var iX2 = oPreviousCharacter.X2 < oCharacter.X1 ? oCharacter.X1 : oCharacter.X2;
				var iY1 = oPreviousCharacter.Y1 < oCharacter.Y2 ? oPreviousCharacter.Y2 : oPreviousCharacter.Y1;
				var iY2 = oPreviousCharacter.Y1 < oCharacter.Y2 ? oCharacter.Y1 : oCharacter.Y2;

				// If both this and the previous character have the same status, use this for the space.
				if (iCurrentCharacterStatus == iPreviousCharacterStatus) {
					g_oTextTools.setHighlightColour(g_oTextCompare.getAnnotationColourFromDifferenceType(iCurrentCharacterStatus));
				}
				// Otherwise use the highlight colour.
				else {
					g_oTextTools.setHighlightColour(g_oTextCompare.g_szHighlightColour);
				}

				// Highlight the space between the two characters.
				g_oTextTools.highlightElement(iX1, iX2, iY1, iY2, oArtworkCanvas, oAnnotationCanvas);
			}

			// Highlight the character in the correct colour.
			g_oTextTools.setHighlightColour(g_oTextCompare.getAnnotationColourFromDifferenceType(iCurrentCharacterStatus));
			g_oTextTools.highlightCharacter(oCharacters[i].paragraph, oCharacters[i].line, oCharacters[i].word, oCharacters[i].character, oArtworkCanvas, oAnnotationCanvas);

			// Update the status of the previous word.
			iPreviousCharacterStatus = iCurrentCharacterStatus;
		}
	};

	/**
	 * Gets the annotation colour from the difference type.
	 * @param {enum} eDifferenceType - The difference type.
	 */
	TextCompare.prototype.getAnnotationColourFromDifferenceType = function (eDifferenceType) {
		var szColour;

		switch (eDifferenceType) {
			case g_oTextCompare.eDifferenceTypes.Addition: szColour = "green"; break;
			case g_oTextCompare.eDifferenceTypes.Deletion: szColour = "red"; break;
			case g_oTextCompare.eDifferenceTypes.Substitution: szColour = "orange"; break;
			case g_oTextCompare.eDifferenceTypes.WeightSubstitution: szColour = "orange"; break;
			case g_oTextCompare.eDifferenceTypes.StyleSubstitution: szColour = "orange"; break;
			case g_oTextCompare.eDifferenceTypes.WeightStyleSubstitution: szColour = "orange"; break;
			default: szColour = g_oTextCompare.g_szHighlightColour; break;
		}

		return szColour;
	};

	/**
	 * Gets the annotation colour from the difference status.
	 * @param {enum} eDifferenceStatus - The difference status.
	 */
	TextCompare.prototype.getAnnotationColourFromDifferenceStatus = function (eDifferenceStatus) {
		var szColour;

		switch (eDifferenceStatus) {
			case g_oTextCompare.eDifferenceStatuses.Ignore: szColour = "grey"; break;
			case g_oTextCompare.eDifferenceStatuses.Accept: szColour = "green"; break;
			case g_oTextCompare.eDifferenceStatuses.Reject: szColour = "red"; break;
			default: szColour = "orange"; break;
		}

		return szColour;
	};

	/**
	 * Gets the handle fill style.
	 * @param {string} handleColour - The handle colour.
	 */
	TextCompare.prototype.getHandleFillStyle = function (handleColour) {
		return (handleColour === "yellow" || handleColour === "#FFFF00" || handleColour === "cyan" || handleColour === "#00FFFF") ? "#000000" : "#FFFFFF";
	};

	/**
	 * Gets the handle image.
	 * @param {int} handleType - The handle type.
	 * @param {int} handleColour - The handle colour.
	 * @param {int} rotation - The canvas rotation.
	 */
	TextCompare.prototype.getHandleImage = function (handleType, handleColour, rotation) {

		// Determine the colour.
		var bBrightColor = handleColour === "yellow" || handleColour === "#FFFF00" || handleColour === "cyan" || handleColour === "#00FFFF";

		// Determine the image.
		switch (parseInt(handleType)) {
			case g_oTextCompare.eTextCompareHandleTypes.AddText:
				switch (rotation) {
					case 90: return bBrightColor ? g_oAnnotationActions.g_oAddTextIconBlk270 : g_oAnnotationActions.g_oAddTextIcon270; break;
					case 180: return bBrightColor ? g_oAnnotationActions.g_oAddTextIconBlk180 : g_oAnnotationActions.g_oAddTextIcon180; break;
					case 270: return bBrightColor ? g_oAnnotationActions.g_oAddTextIconBlk90 : g_oAnnotationActions.g_oAddTextIcon90; break;
					default: return bBrightColor ? g_oAnnotationActions.g_oAddTextIconBlk : g_oAnnotationActions.g_oAddTextIcon; break;
				}
				break;
			case g_oTextCompare.eTextCompareHandleTypes.AddParagraph:
				switch (rotation) {
					case 90: return bBrightColor ? g_oAnnotationActions.g_oAddParagraphIconBlk270 : g_oAnnotationActions.g_oAddParagraphIcon270; break;
					case 180: return bBrightColor ? g_oAnnotationActions.g_oAddParagraphIconBlk180 : g_oAnnotationActions.g_oAddParagraphIcon180; break;
					case 270: return bBrightColor ? g_oAnnotationActions.g_oAddParagraphIconBlk90 : g_oAnnotationActions.g_oAddParagraphIcon90; break;
					default: return bBrightColor ? g_oAnnotationActions.g_oAddParagraphIconBlk : g_oAnnotationActions.g_oAddParagraphIcon; break;
				}
				break;
			case g_oTextCompare.eTextCompareHandleTypes.AddArea:
				switch (rotation) {
					case 90: return bBrightColor ? g_oAnnotationActions.g_oAddAreaIconBlk270 : g_oAnnotationActions.g_oAddAreaIcon270; break;
					case 180: return bBrightColor ? g_oAnnotationActions.g_oAddAreaIconBlk180 : g_oAnnotationActions.g_oAddAreaIcon180; break;
					case 270: return bBrightColor ? g_oAnnotationActions.g_oAddAreaIconBlk90 : g_oAnnotationActions.g_oAddAreaIcon90; break;
					default: return bBrightColor ? g_oAnnotationActions.g_oAddAreaIconBlk : g_oAnnotationActions.g_oAddAreaIcon; break;
				}
				break;
			case g_oTextCompare.eTextCompareHandleTypes.RemoveText:
				switch (rotation) {
					case 90: return bBrightColor ? g_oAnnotationActions.g_oRemoveTextIconBlk270 : g_oAnnotationActions.g_oRemoveTextIcon270; break;
					case 180: return bBrightColor ? g_oAnnotationActions.g_oRemoveTextIconBlk180 : g_oAnnotationActions.g_oRemoveTextIcon180; break;
					case 270: return bBrightColor ? g_oAnnotationActions.g_oRemoveTextIconBlk90 : g_oAnnotationActions.g_oRemoveTextIcon90; break;
					default: return bBrightColor ? g_oAnnotationActions.g_oRemoveTextIconBlk : g_oAnnotationActions.g_oRemoveTextIcon; break;
				}
				break;
			case g_oTextCompare.eTextCompareHandleTypes.Delete:
				switch (rotation) {
					case 90: return bBrightColor ? g_oAnnotationActions.g_oDeleteIconBlk270 : g_oAnnotationActions.g_oDeleteIcon270; break;
					case 180: return bBrightColor ? g_oAnnotationActions.g_oDeleteIconBlk180 : g_oAnnotationActions.g_oDeleteIcon180; break;
					case 270: return bBrightColor ? g_oAnnotationActions.g_oDeleteIconBlk90 : g_oAnnotationActions.g_oDeleteIcon90; break;
					default: return bBrightColor ? g_oAnnotationActions.g_oDeleteIconBlk : g_oAnnotationActions.g_oDeleteIcon; break;
				}
				break;
		}
	};

	/**
	 * Cheks if a handle has been clicked.
	 * @param {object} event - The click event.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextCompare.prototype.checkHandleClicked = function (event, oArtworkCanvas, oAnnotationCanvas) {

		// Get the click coordinates.
		var oClick = g_oAnnotations.getClick(event, oArtworkCanvas);

		// Transform the click for the rotation.
		var oTransformedClick = g_oAnnotations.transformClickForRotation(oClick[0], oClick[1], oArtworkCanvas);

		// Convert the clicks to percentages.
		var iClickPercentage_X = oTransformedClick[0] / oArtworkCanvas.g_iScaledImageWidth;
		var iClickPercentage_Y = oTransformedClick[1] / oArtworkCanvas.g_iScaledImageHeight;

		// Calculate the compare offset on the artwork canvas.
		var co = g_oCompare.calculateCompareOffsetInPx(oArtworkCanvas);
		var compareOffset_x = co.x;
		var compareOffset_y = co.y;

		// Get the text selection group we are going to check.
		var oTextSelectionGroup = g_oTextCompare.getTextSelectionGroup(oArtworkCanvas.g_szCanvasId == "CompareViewer");

		// Check if we have selections.
		if (oTextSelectionGroup.length == 0) {
			return;
		}

		// Loop through each of the selection groups.
		for (var i = 0; i < oTextSelectionGroup.length; i++) {

			// Loop through each of the text selections.
			for (var j = 0; j < oTextSelectionGroup[i].g_oTextSelections.length; j++) {

				// If this text selecction is not on this page, ignore it.
				if (oTextSelectionGroup[i].g_oTextSelections[j].g_iPage != oArtworkCanvas.g_iCurrentPage) {
					continue;
				}

				// Extract the selection group.
				var oSelectionGroup = oTextSelectionGroup[i].g_oTextSelections[j];

				// Detemine the selection bounds to be used.
				var oSelectionBounds = g_oTextCompare.g_iAccuracy == g_oTextCompare.eAccuracyLevels.Word ? oSelectionGroup.g_oSelectionWordsBounds : oSelectionGroup.g_oSelectionCharacterBounds;

				// Loop through each of the annotation handles.
				for (var iHandleType in Object.keys(g_oTextCompare.eTextCompareHandleTypes)) {

					// Extract the handle positions.
					var iHandlePosition_X = oSelectionBounds.x2 * oArtworkCanvas.g_iScaledImageWidth + compareOffset_x - g_oAnnotations.g_oAnnotationNumberBox.iBeginX;
					var iHandlePosition_Y = oSelectionBounds.y1 * oArtworkCanvas.g_iScaledImageHeight + compareOffset_y - g_oAnnotations.g_oAnnotationNumberBox.iBeginY;

					// Move the handle right by the handle type.
					iHandlePosition_X += (parseInt(iHandleType) * (g_oAnnotationActions.g_iAnnotationDragRectSize + (g_oAnnotations.g_oAnnotationNumberBox.iBeginX / 2)));

					// Convert the handle positions to percentages.
					var iHandlePercentageStart_X = (iHandlePosition_X) / oArtworkCanvas.g_iScaledImageWidth;
					var iHandlePercentageStart_Y = (iHandlePosition_Y) / oArtworkCanvas.g_iScaledImageHeight;
					var iHandlePercentageEnd_X = (iHandlePosition_X + g_oAnnotationActions.g_iAnnotationDragRectSize) / oArtworkCanvas.g_iScaledImageWidth;
					var iHandlePercentageEnd_Y = (iHandlePosition_Y + g_oAnnotationActions.g_iAnnotationDragRectSize) / oArtworkCanvas.g_iScaledImageHeight;

					// Check the click is within the handle.
					if (iClickPercentage_X > iHandlePercentageStart_X && iClickPercentage_X < iHandlePercentageEnd_X && iClickPercentage_Y > iHandlePercentageStart_Y && iClickPercentage_Y < iHandlePercentageEnd_Y) {

						// Call to click the annotation handle.
						g_oTextCompare.clickAnnotationHandle(i, j, iHandleType, oArtworkCanvas.g_szCanvasId == "CompareViewer");
						return;
					}
				}
			}
		}
	};

	/**
	 * Clicks an annotation handle.
	 * @param {int} iSelectionGroup - The selection group.
	 * @param {int} iSelection - The selection.
	 * @param {int} iHandle - The handle.
	 * @param {boolean} bCompareCanvas - The compare canvas.
	 */
	TextCompare.prototype.clickAnnotationHandle = function (iSelectionGroup, iSelection, iHandle, bCompareCanvas) {

		// Call the corret annotation handle click event.
		switch (parseInt(iHandle)) {
			case g_oTextCompare.eTextCompareHandleTypes.AddText:
				g_oTextCompare.initialiseTextCompareAddHighlight(iSelectionGroup, iSelection, false, bCompareCanvas);
				break;
			case g_oTextCompare.eTextCompareHandleTypes.AddParagraph:
				g_oTextCompare.initialiseTextCompareAddHighlight(iSelectionGroup, iSelection, true, bCompareCanvas);
				break;
			case g_oTextCompare.eTextCompareHandleTypes.AddArea:
				g_oTextCompare.initialiseTextCompareAddArea(iSelectionGroup, iSelection, true, bCompareCanvas);
				break;
			case g_oTextCompare.eTextCompareHandleTypes.RemoveText:
				g_oTextCompare.initialiseTextCompareRemoveHighlight(iSelectionGroup, iSelection, false, bCompareCanvas);
				break;
			case g_oTextCompare.eTextCompareHandleTypes.Delete:
				g_oTextCompare.deleteTextData(iSelectionGroup, iSelection, bCompareCanvas, true);
				break;
		}
	};

	/**
	 * Renders the text compare sections.
	 */
	TextCompare.prototype.renderTextCompareSections = function () {

		// Get the highest text selection group.
		var iHighestTextSelectionGroup = g_oTextCompare.getHighestTextSelectionGroup(true, true);

		// If we have no text selection groups, show this.
		if (iHighestTextSelectionGroup == 0) {
			$("#divTextCompareSections").html('<div class="divCompareSection divCompareSection--none">' + g_oTextCompare.c_szTextCompareNoTextSelections + '</div>');
			return;
		}

		// Build up the HTML.
		var szHTML = '';

		// Add the top drag scroll zone.
		szHTML += '<div class="divScrollZoneTop" style="display: none;" ondragover="g_oTextCompare.moveTextDrag(event, true);"></div>';

		// Loop through each of the sections up to the highest.
		for (var i = 1; i <= iHighestTextSelectionGroup; i++) {

			// Get the difference class for this group.
			var szDifferencesClass = g_oTextCompare.getGroupDifferencesClass(i);

			// Add the section.
			szHTML += '<div id="divCompareSection_' + i + '" class="divCompareSection' + (szDifferencesClass == null ? '' : ' ' + szDifferencesClass) + '">';

			// Render the text compare section.
			szHTML += g_oTextCompare.renderTextCompareSection(i);

			// Close the section.
			szHTML += '</div>';
		}

		// Add the hidden new group section for drag and drop.
		szHTML += '<div id="divDragDropNewGroup" class="divCompareSection" style="display: none;">';
		szHTML += '<div class="divCompareSection__title divCompareSection__title--margin">New Group</div>';

		// Render the text selection approval drop zone.
		szHTML += '<div class="divCompareSection__column">';
		szHTML += '<div id="divTextCompareDropZoneWrapperNewApproval_' + (iHighestTextSelectionGroup + 1) + '_1" class="textCompareDropZoneWrapper textCompareDropZoneWrapper--approval">';
		szHTML += '<div id="divTextCompareDropZoneNewApproval_' + (iHighestTextSelectionGroup + 1) + '_1" class="textCompareDropZone" ondragover="g_oTextCompare.textDragOver(event);" ondragleave="g_oTextCompare.textDragOut(event);" ondrop="g_oTextCompare.textDrop(event);"></div>';
		szHTML += '</div>';
		szHTML += '</div>';

		// Render the text selection compare drop zone.
		szHTML += '<div class="divCompareSection__column">';
		szHTML += '<div id="divTextCompareDropZoneWrapperNewCompare_' + (iHighestTextSelectionGroup + 1) + '_1" class="textCompareDropZoneWrapper textCompareDropZoneWrapper--compare">';
		szHTML += '<div id="divTextCompareDropZoneNewCompare_' + (iHighestTextSelectionGroup + 1) + '_1" class="textCompareDropZone" ondragover="g_oTextCompare.textDragOver(event);" ondragleave="g_oTextCompare.textDragOut(event);" ondrop="g_oTextCompare.textDrop(event);"></div>';
		szHTML += '</div>';
		szHTML += '</div>';

		// Close the new group for the drag and drop.
		szHTML += '</div>';

		// Add the bottom drag scroll zone.
		szHTML += '<div class="divScrollZoneBottom" style="display: none;" ondragover="g_oTextCompare.moveTextDrag(event, false);"></div>';

		// Apply the HTML.
		$("#divTextCompareSections").html(szHTML);
	};

	/**
	 * Renders the text compare section.
	 * @param {int} iGroupOrder - The group order.
	 */
	TextCompare.prototype.renderTextCompareSection = function (iGroupOrder) {

		// Outline the HTML to be returned.
		var szHTML = '';

		// Determine the collapsed state of the group.
		var bCollapsed = g_oTextCompare.g_oCollapsedSectionOrders.indexOf(iGroupOrder) != -1;

		// Get the section for the approval and compare.
		var oApprovalSectionGroup = g_oTextCompare.getTextSelectionGroupFromOrder(iGroupOrder, false);
		var oCompareSectionGroup = g_oTextCompare.getTextSelectionGroupFromOrder(iGroupOrder, true);

		// Add the section title.
		szHTML += '<div class="divCompareSection__title">';
		szHTML += '<img id="divCompareSectionIcon_' + iGroupOrder + '" src="' + (bCollapsed ? 'img/Arrow_Right_16.png' : 'img/Arrow_Down_16.png') + '" class="divCompareSection__icon" onclick="g_oTextCompare.toggleTextSection(' + iGroupOrder + ');" />';
		szHTML += g_oTextCompare.c_szTextCompareGroupText + ' ' + iGroupOrder.toString();

		// If we have calculated differences.
		if (g_oTextCompare.g_bDifferencesCalculated) {

			// If we failed to calculate for this group.
			if (oApprovalSectionGroup == null || oCompareSectionGroup == null) {
				szHTML += ' (' + g_oTextCompare.c_szTextCompareFailedToCalculateDifferencesText + ')';
			}
			// If we successfully calculated for this group.
			else {
				var iDifferences = g_oTextCompare.countDifferences(iGroupOrder, null, null);
				switch (iDifferences) {
					case 0: szHTML += ' (' + g_oTextCompare.c_szTextCompareNoDifferencesText + ')'; break;
					case 1: szHTML += ' (' + iDifferences + ' ' + g_oTextCompare.c_szTextCompareDifferenceText + ')'; break;
					default: szHTML += ' (' + iDifferences + ' ' + g_oTextCompare.c_szTextCompareDifferencesText + ')'; break;
				}
			}
		}

		// Close the section title.
		szHTML += '</div>';

		// Add the columns collapsable wrapper.
		szHTML += '<div id="divCompareSections_' + iGroupOrder + '" class="divCompareSections__columns" style="display: ' + (bCollapsed ? 'none' : 'block') + ';">';

		// Add the approval section.
		szHTML += '<div class="divCompareSection__column">';
		szHTML += g_oTextCompare.renderTextSelections(oApprovalSectionGroup, iGroupOrder, false);
		szHTML += '</div>';

		// Add the compare section.
		szHTML += '<div class="divCompareSection__column">';
		szHTML += g_oTextCompare.renderTextSelections(oCompareSectionGroup, iGroupOrder, true);
		szHTML += '</div>';

		// Add the differences section.
		if (g_oTextCompare.g_bDifferencesCalculated) {
			szHTML += '<div class="divCompareSection__column divCompareSection__column--large">';
			szHTML += g_oTextCompare.renderTextDifferences(oApprovalSectionGroup);
			szHTML += '</div>';
		}

		// Close the columns collapsable wrapper.
		szHTML += '</div>';

		// Return the HTML.
		return szHTML;
	};

	/**
	 * Renders the text selections.
	 * @param {object} oSelectionGroup - The selection group.
	 * @param {int} iGroupOrder - The group order.
	 * @param {boolean} bCompare - If this is the compare canvas.
	 */
	TextCompare.prototype.renderTextSelections = function (oSelectionGroup, iGroupOrder, bCompare) {

		// Outline the HTML to be returned.
		var szHTML = '';

		// Renders the text selection words.
		if (g_oTextCompare.g_iAccuracy == g_oTextCompare.eAccuracyLevels.Word) {
			szHTML += g_oTextCompare.renderTextSelections_Words(oSelectionGroup, iGroupOrder, bCompare);
		}
		// Renders the text selection characters.
		else {
			szHTML += g_oTextCompare.renderTextSelections_Characters(oSelectionGroup, iGroupOrder, bCompare);
		}

		// Return the HTML.
		return szHTML;
	};

	/**
	 * Renders the text selection words.
	 * @param {object} oSelectionGroup - The selection group.
	 * @param {int} iGroupOrder - The group order.
	 * @param {boolean} bCompare - If this is the compare canvas.
	 */
	TextCompare.prototype.renderTextSelections_Words = function (oSelectionGroup, iGroupOrder, bCompare) {

		// Outline the HTML to be returned.
		var szHTML = '';

		// Determine the drop zone class to be used.
		var szDropZoneClass = bCompare ? "textCompareDropZoneWrapper--compare" : "textCompareDropZoneWrapper--approval";

		// If this selection group does not exist, return that this is an orphaned statement.
		if (oSelectionGroup == null) {

			// Render the text selection orphaned statement.
			szHTML += '<div class="divTextSection divTextSection--error">';
			szHTML += g_oTextCompare.c_szTextCompareOrphanedStatement;
			szHTML += '</div>';

			// Render the text selection drop zone.
			szHTML += '<div id="divTextCompareDropZoneWrapper_' + iGroupOrder + '_1" class="textCompareDropZoneWrapper ' + szDropZoneClass + '">';
			szHTML += '<div id="divTextCompareDropZone_' + iGroupOrder + '_1" class="textCompareDropZone" ondragover="g_oTextCompare.textDragOver(event);" ondragleave="g_oTextCompare.textDragOut(event);" ondrop="g_oTextCompare.textDrop(event);"></div>';
			szHTML += '</div>';

			// Return the orphaned statement html.
			return szHTML;
		}

		// Render the text selection drop zone.
		szHTML += '<div id="divTextCompareDropZoneWrapper_' + iGroupOrder + '_' + 0 + '" class="textCompareDropZoneWrapper textCompareDropZoneWrapper--first ' + szDropZoneClass + '">';
		szHTML += '<div id="divTextCompareDropZone_' + iGroupOrder + '_' + 0 + '" class="textCompareDropZone" ondragover="g_oTextCompare.textDragOver(event);" ondragleave="g_oTextCompare.textDragOut(event);" ondrop="g_oTextCompare.textDrop(event);"></div>';
		szHTML += '</div>';

		// Loop through each of the text selections.
		for (var i = 0; i < oSelectionGroup.g_oTextSelections.length; i++) {

			// Keep track of the current and previous word status.
			var iPreviousWordStatus = g_oTextCompare.eDifferenceTypes.NoDifference;
			var iCurrentWordStatus = g_oTextCompare.eDifferenceTypes.NoDifference;

			// Determine if we need a drag event for this div.
			var szDragEvent = !g_oTextCompare.g_bDifferencesCalculated ? 'draggable="true" ondragstart="g_oTextCompare.startTextDrag(event, ' + bCompare + ', false);" ondragend="g_oTextCompare.endTextDrag(event);"' : '';

			// Render the text selection.
			szHTML += '<div id="divTextSelection_' + iGroupOrder + '_' + i + '_' + bCompare + '" class="divTextSection' + (g_oTextCompare.g_bDifferencesCalculated ? '' : ' divTextSection--padding') + '" ' + szDragEvent + ' dir="auto">';

			// Render the selection text.
			for (var j = 0; j < oSelectionGroup.g_oTextSelections[i].g_oSelectionWords.length; j++) {

				// Ensure we have content for the word.
				if (oSelectionGroup.g_oTextSelections[i].g_oSelectionWords[j].content == "") {
					continue;
				}

				// Determine the current word status.
				iCurrentWordStatus = oSelectionGroup.g_oTextSelections[i].g_oSelectionWords[j].status;

				// Add a space if needed.
				if (j > 0) {

					// Determine the class to be used for the space.
					var szSpaceClass = "divTextSection__word";
					if (iPreviousWordStatus == g_oTextCompare.eDifferenceTypes.Addition && iCurrentWordStatus == g_oTextCompare.eDifferenceTypes.Addition) {
						szSpaceClass += " divTextSection__word--addition";
					}
					else if (iPreviousWordStatus == g_oTextCompare.eDifferenceTypes.Deletion && iCurrentWordStatus == g_oTextCompare.eDifferenceTypes.Deletion) {
						szSpaceClass += " divTextSection__word--deletion";
					}

					// Add the space.
					szHTML += '<span class="' + szSpaceClass + '"> </span>';
				}

				// Determine the class to be used for the text.
				var szWordClass = "divTextSection__word";
				if (iCurrentWordStatus == g_oTextCompare.eDifferenceTypes.Addition) {
					szWordClass += " divTextSection__word--addition";
				}
				else if (iCurrentWordStatus == g_oTextCompare.eDifferenceTypes.Deletion) {
					szWordClass += " divTextSection__word--deletion";
				}

				// Determine if we are in bold or italic, and detecting this.
				if (g_oTextCompare.g_iBoldItalicDetection == g_oTextCompare.eBoldItalicDetectionLevels.On && oSelectionGroup.g_oTextSelections[i].g_oSelectionWords[j].bold) {
					szWordClass += " divTextSection__word--bold";
				}
				if (g_oTextCompare.g_iBoldItalicDetection == g_oTextCompare.eBoldItalicDetectionLevels.On && oSelectionGroup.g_oTextSelections[i].g_oSelectionWords[j].italic) {
					szWordClass += " divTextSection__word--italic";
				}

				// Extract the word content to be added based on the case sensitivity.
				var szWordContent = g_oTextCompare.g_iCaseSensitivity == g_oTextCompare.eCaseSensitivityLevels.On ? oSelectionGroup.g_oTextSelections[i].g_oSelectionWords[j].content : oSelectionGroup.g_oTextSelections[i].g_oSelectionWords[j].content.toLowerCase();

				// Add the word.
				szHTML += '<span class="' + szWordClass + '">' + szWordContent + '</span>';

				// Update the previous word status for the next loop.
				iPreviousWordStatus = iCurrentWordStatus;
			}

			// Render the options if we are not in differences mode.
			if (!g_oTextCompare.g_bDifferencesCalculated) {
				szHTML += '<img class="divTextSection__icon" src="img/Delete_col_24.png" onclick="g_oTextCompare.deleteTextDataByGroupOrder(' + iGroupOrder + ', ' + i + ', ' + bCompare + ');" />';
				szHTML += '<img class="divTextSection__icon divTextSection__icon--second" src="img/Pan_col-01_48.png" />';
			}

			// Close the text selection.
			szHTML += '</div>';

			// Render the text selection drop zone.
			szHTML += '<div id="divTextCompareDropZoneWrapper_' + iGroupOrder + '_' + (i + 1) + '" class="textCompareDropZoneWrapper' + (i + 1 == oSelectionGroup.g_oTextSelections.length ? ' textCompareDropZoneWrapper--last' : '') + ' ' + szDropZoneClass + '">';
			szHTML += '<div id="divTextCompareDropZone_' + iGroupOrder + '_' + (i + 1) + '" class="textCompareDropZone" ondragover="g_oTextCompare.textDragOver(event);" ondragleave="g_oTextCompare.textDragOut(event);" ondrop="g_oTextCompare.textDrop(event);"></div>';
			szHTML += '</div>';
		}

		// Return the HTML.
		return szHTML;
	};

	/**
	 * Renders the text selection characters.
	 * @param {object} oSelectionGroup - The selection group.
	 * @param {int} iGroupOrder - The group order.
	 * @param {boolean} bCompare - If this is the compare canvas.
	 */
	TextCompare.prototype.renderTextSelections_Characters = function (oSelectionGroup, iGroupOrder, bCompare) {

		// Outline the HTML to be returned.
		var szHTML = '';

		// Determine the drop zone class to be used.
		var szDropZoneClass = bCompare ? "textCompareDropZoneWrapper--compare" : "textCompareDropZoneWrapper--approval";

		// If this selection group does not exist, return that this is an orphaned statement.
		if (oSelectionGroup == null) {

			// Render the text selection orphaned statement.
			szHTML += '<div class="divTextSection divTextSection--error">';
			szHTML += g_oTextCompare.c_szTextCompareOrphanedStatement;
			szHTML += '</div>';

			// Render the text selection drop zone.
			szHTML += '<div id="divTextCompareDropZoneWrapper_' + iGroupOrder + '_1" class="textCompareDropZoneWrapper ' + szDropZoneClass + '">';
			szHTML += '<div id="divTextCompareDropZone_' + iGroupOrder + '_1" class="textCompareDropZone" ondragover="g_oTextCompare.textDragOver(event);" ondragleave="g_oTextCompare.textDragOut(event);" ondrop="g_oTextCompare.textDrop(event);"></div>';
			szHTML += '</div>';

			// Return the orphaned statement html.
			return szHTML;
		}

		// Render the text selection drop zone.
		szHTML += '<div id="divTextCompareDropZoneWrapper_' + iGroupOrder + '_' + 0 + '" class="textCompareDropZoneWrapper textCompareDropZoneWrapper--first ' + szDropZoneClass + '">';
		szHTML += '<div id="divTextCompareDropZone_' + iGroupOrder + '_' + 0 + '" class="textCompareDropZone" ondragover="g_oTextCompare.textDragOver(event);" ondragleave="g_oTextCompare.textDragOut(event);" ondrop="g_oTextCompare.textDrop(event);"></div>';
		szHTML += '</div>';

		// Loop through each of the text selections.
		for (var i = 0; i < oSelectionGroup.g_oTextSelections.length; i++) {

			// Keep track of the current and previous character status.
			var iPreviousCharacterStatus = g_oTextCompare.eDifferenceTypes.NoDifference;
			var iCurrentCharacterStatus = g_oTextCompare.eDifferenceTypes.NoDifference;

			// Determine if we need a drag event for this div.
			var szDragEvent = !g_oTextCompare.g_bDifferencesCalculated ? 'draggable="true" ondragstart="g_oTextCompare.startTextDrag(event, ' + bCompare + ', false);" ondragend="g_oTextCompare.endTextDrag(event);"' : '';

			// Render the text selection.
			szHTML += '<div id="divTextSelection_' + iGroupOrder + '_' + i + '_' + bCompare + '" class="divTextSection' + (g_oTextCompare.g_bDifferencesCalculated ? '' : ' divTextSection--padding') + '" ' + szDragEvent + ' dir="auto">';

			// Render the selection text.
			for (var j = 0; j < oSelectionGroup.g_oTextSelections[i].g_oSelectionCharacters.length; j++) {

				// Ensure we have content for the character.
				if (oSelectionGroup.g_oTextSelections[i].g_oSelectionCharacters[j].content == "") {
					continue;
				}

				// Determine the current character status.
				iCurrentCharacterStatus = oSelectionGroup.g_oTextSelections[i].g_oSelectionCharacters[j].status;

				// Add a space if needed.
				if (oSelectionGroup.g_oTextSelections[i].g_oSelectionCharacters[j].character == 0 && j > 0) {

					// Determine the class to be used for the space.
					var szSpaceClass = "divTextSection__word";
					if (iPreviousCharacterStatus == g_oTextCompare.eDifferenceTypes.Addition && iCurrentCharacterStatus == g_oTextCompare.eDifferenceTypes.Addition) {
						szSpaceClass += " divTextSection__word--addition";
					}
					else if (iPreviousCharacterStatus == g_oTextCompare.eDifferenceTypes.Deletion && iCurrentCharacterStatus == g_oTextCompare.eDifferenceTypes.Deletion) {
						szSpaceClass += " divTextSection__word--deletion";
					}

					// Add the space.
					szHTML += '<span class="' + szSpaceClass + '"> </span>';
				}

				// Determine the class to be used for the text.
				var szCharacterClass = "divTextSection__word";
				if (iCurrentCharacterStatus == g_oTextCompare.eDifferenceTypes.Addition) {
					szCharacterClass += " divTextSection__word--addition";
				}
				else if (iCurrentCharacterStatus == g_oTextCompare.eDifferenceTypes.Deletion) {
					szCharacterClass += " divTextSection__word--deletion";
				}

				// Determine if we are in bold or italic, and detecting this.
				if (g_oTextCompare.g_iBoldItalicDetection == g_oTextCompare.eBoldItalicDetectionLevels.On && oSelectionGroup.g_oTextSelections[i].g_oSelectionCharacters[j].bold) {
					szCharacterClass += " divTextSection__word--bold";
				}
				if (g_oTextCompare.g_iBoldItalicDetection == g_oTextCompare.eBoldItalicDetectionLevels.On && oSelectionGroup.g_oTextSelections[i].g_oSelectionCharacters[j].italic) {
					szCharacterClass += " divTextSection__word--italic";
				}

				// Extract the character content to be added based on the case sensitivity.
				var szCharacterContent = g_oTextCompare.g_iCaseSensitivity == g_oTextCompare.eCaseSensitivityLevels.On ? oSelectionGroup.g_oTextSelections[i].g_oSelectionCharacters[j].content : oSelectionGroup.g_oTextSelections[i].g_oSelectionCharacters[j].content.toLowerCase();

				// Add the character.
				szHTML += '<span class="' + szCharacterClass + '">' + szCharacterContent + '</span>';

				// Update the previous character status for the next loop.
				iPreviousCharacterStatus = iCurrentCharacterStatus;
			}

			// Render the options if we are not in differences mode.
			if (!g_oTextCompare.g_bDifferencesCalculated) {
				szHTML += '<img class="divTextSection__icon" src="img/Delete_col_24.png" onclick="g_oTextCompare.deleteTextDataByGroupOrder(' + iGroupOrder + ', ' + i + ', ' + bCompare + ');" />';
				szHTML += '<img class="divTextSection__icon divTextSection__icon--second" src="img/Pan_col-01_48.png" />';
			}

			// Close the text selection.
			szHTML += '</div>';

			// Render the text selection drop zone.
			szHTML += '<div id="divTextCompareDropZoneWrapper_' + iGroupOrder + '_' + (i + 1) + '" class="textCompareDropZoneWrapper' + (i + 1 == oSelectionGroup.g_oTextSelections.length ? ' textCompareDropZoneWrapper--last' : '') + ' ' + szDropZoneClass + '">';
			szHTML += '<div id="divTextCompareDropZone_' + iGroupOrder + '_' + (i + 1) + '" class="textCompareDropZone" ondragover="g_oTextCompare.textDragOver(event);" ondragleave="g_oTextCompare.textDragOut(event);" ondrop="g_oTextCompare.textDrop(event);"></div>';
			szHTML += '</div>';
		}

		// Return the HTML.
		return szHTML;
	};

	/**
	 * Renders the text differences.
	 * @param {object} oSelectionGroup - The selection group.
	 */
	TextCompare.prototype.renderTextDifferences = function (oSelectionGroup) {

		// Outline the HTML to be returned.
		var szHTML = '';

		// If we have failed to calcuate the differences.
		if (g_oTextCompare.g_bDifferencesCalculated && (oSelectionGroup == null || oSelectionGroup.g_oTextDifferences == null)) {
			szHTML += '<div class="divTextSection divTextSection--error">' + g_oTextCompare.c_szTextCompareUnableToCalculateDifferences + '</div>';
			return szHTML;
		}

		// If we have no differences.
		if (oSelectionGroup.g_oTextDifferences.length == 0) {
			szHTML += '<div class="divTextSection divTextSection--success">' + g_oTextCompare.c_szTextCompareNoDifferencesText + '</div>';
			return szHTML;
		}

		// Loop through each of the differences.
		for (var i = 0; i < oSelectionGroup.g_oTextDifferences.length; i++) {

			// Render the difference.
			szHTML += g_oTextCompare.renderTextDifference(oSelectionGroup.g_oTextDifferences[i]);
		}

		// Return the HTML.
		return szHTML;
	};

	/**
	 * Renders a text difference.
	 * @param {object} oDifference - The difference.
	 */
	TextCompare.prototype.renderTextDifference = function (oDifference) {

		// Outline the HTML to be returned.
		var szHTML = '';

		// Determine the class to be used.
		var szStatusClass = "divTextSection";
		switch (oDifference.g_iDifferenceStatus) {
			case g_oTextCompare.eDifferenceStatuses.Initial: szStatusClass += " divTextSection--initial"; break;
			case g_oTextCompare.eDifferenceStatuses.Ignore: szStatusClass += " divTextSection--ignore"; break;
			case g_oTextCompare.eDifferenceStatuses.Accept: szStatusClass += " divTextSection--accept"; break;
			case g_oTextCompare.eDifferenceStatuses.Reject: szStatusClass += " divTextSection--reject"; break;
		}

		// Render the text difference.
		szHTML += '<div class="' + szStatusClass + '">';

		// Add the difference order.
		szHTML += '<div class="divTextSection__counter">' + oDifference.g_iDifferenceOrder + '</div>';

		// Add the difference step to.
		szHTML += '<img class="divTextSection__step tooltip" src="img/step_through_black_24.png" title="Step to Difference" onclick="g_oTextCompare.stepThroughToDifference(' + oDifference.g_iDifferenceNumber + ');" />';

		// Add the status dropdown.
		szHTML += '<div class="divTextSection__status">';
		szHTML += '<select id="selDifferenceStatus_' + oDifference.g_iDifferenceNumber + '" onchange="g_oTextCompare.updateDifferenceStatus(' + oDifference.g_iDifferenceNumber + ');">';
		szHTML += '<option value="' + g_oTextCompare.eDifferenceStatuses.Initial + '"' + (oDifference.g_iDifferenceStatus == g_oTextCompare.eDifferenceStatuses.Initial ? ' selected' : '') + '>' + g_oTextCompare.c_szTextCompareDifferenceStatusInitial + '</option>';
		szHTML += '<option value="' + g_oTextCompare.eDifferenceStatuses.Ignore + '"' + (oDifference.g_iDifferenceStatus == g_oTextCompare.eDifferenceStatuses.Ignore ? ' selected' : '') + '>' + g_oTextCompare.c_szTextCompareDifferenceStatusIgnore + '</option>';
		szHTML += '<option value="' + g_oTextCompare.eDifferenceStatuses.Accept + '"' + (oDifference.g_iDifferenceStatus == g_oTextCompare.eDifferenceStatuses.Accept ? ' selected' : '') + '>' + g_oTextCompare.c_szTextCompareDifferenceStatusAccept + '</option>';
		szHTML += '<option value="' + g_oTextCompare.eDifferenceStatuses.Reject + '"' + (oDifference.g_iDifferenceStatus == g_oTextCompare.eDifferenceStatuses.Reject ? ' selected' : '') + '>' + g_oTextCompare.c_szTextCompareDifferenceStatusReject + '</option>';
		szHTML += '</select>';
		szHTML += '</div>';

		// Render the difference type.
		szHTML += '<div class="divTextSection__title">' + g_oTextCompare.getDifferenceText(oDifference.g_eDifferenceType) + '</div>';

		// If this is a substitution, add the compare heading.
		if (g_oTextCompare.differenceTypeIsSubstitution(oDifference.g_eDifferenceType)) {
			szHTML += '<div class="divTextSection__heading">' + g_oTextCompare.c_szTextCompareCompareDocument + ':</div>';
		}

		// Add the difference text.
		szHTML += '<div dir="auto">' + oDifference.g_oFormattedDifferenceText[0] + '</div>';

		// If this is a substitution, add the approval text.
		if (g_oTextCompare.differenceTypeIsSubstitution(oDifference.g_eDifferenceType)) {
			szHTML += '<div class="divTextSection__heading">' + g_oTextCompare.c_szTextCompareApprovalDocument + ':</div>';
			szHTML += '<div dir="auto">' + oDifference.g_oFormattedDifferenceText[1] + '</div>';
		}

		// Close the text difference.
		szHTML += '</div>';

		// Return the result.
		return szHTML;
	};

	/**
	 * Gets the difference text.
	 * @param {enum} eDifferenceType - The difference type.
	 */
	TextCompare.prototype.getDifferenceText = function (eDifferenceType) {
		var szDifferenceText = "";

		switch (eDifferenceType) {
			case g_oTextCompare.eDifferenceTypes.Addition: szDifferenceText = g_oTextCompare.c_szTextCompareDifferenceTypeAddition; break;
			case g_oTextCompare.eDifferenceTypes.Deletion: szDifferenceText = g_oTextCompare.c_szTextCompareDifferenceTypeDeletion; break;
			case g_oTextCompare.eDifferenceTypes.Substitution: szDifferenceText = g_oTextCompare.c_szTextCompareDifferenceTypeSubstitution; break;
			case g_oTextCompare.eDifferenceTypes.WeightSubstitution: szDifferenceText = g_oTextCompare.c_szTextCompareDifferenceTypeSubstitution + " (" + g_oTextCompare.c_szTextCompareDifferenceTypeFontWeight + ")"; break;
			case g_oTextCompare.eDifferenceTypes.StyleSubstitution: szDifferenceText = g_oTextCompare.c_szTextCompareDifferenceTypeSubstitution + " (" + g_oTextCompare.c_szTextCompareDifferenceTypeFontStyle + ")"; break;
			case g_oTextCompare.eDifferenceTypes.WeightStyleSubstitution: szDifferenceText = g_oTextCompare.c_szTextCompareDifferenceTypeSubstitution + " (" + g_oTextCompare.c_szTextCompareDifferenceTypeFontWeightAndStyle + ")"; break;
		}

		return szDifferenceText;
	};

	/**
	 * Gets the text selection group from the order.
	 * @param {int} iGroupOrder - The group order.
	 * @param {boolean} bCompare - If this is the compare canvas.
	 */
	TextCompare.prototype.getTextSelectionGroupFromOrder = function (iGroupOrder, bCompare) {

		// Get the approval text selections.
		var oApprovalTextSelections = g_oTextCompare.getTextSelectionGroup(bCompare);

		// Loop through each of the text selections.
		for (var i = 0; i < oApprovalTextSelections.length; i++) {

			// If this is the correct group, return it.
			if (oApprovalTextSelections[i].g_iGroupOrder == iGroupOrder) {
				return oApprovalTextSelections[i];
			}
		}

		// If not found, return null.
		return null;
	};

	/**
	 * Gets the text selection group index from the order.
	 * @param {int} iGroupOrder - The group order.
	 * @param {boolean} bCompare - If this is the compare canvas.
	 */
	TextCompare.prototype.getTextSelectionGroupIndexFromOrder = function (iGroupOrder, bCompare) {

		// Get the approval text selections.
		var oApprovalTextSelections = g_oTextCompare.getTextSelectionGroup(bCompare);

		// Loop through each of the text selections.
		for (var i = 0; i < oApprovalTextSelections.length; i++) {

			// If this is the correct group, return the index.
			if (oApprovalTextSelections[i].g_iGroupOrder == iGroupOrder) {
				return i;
			}
		}

		// If not found, return -1.
		return -1;
	};

	/**
	 * Gets the highest text selection group.
	 * @param {boolean} bApproval - If we are including the approval canvas.
	 * @param {boolean} bCompare - If we are including the compare canvas.
	 */
	TextCompare.prototype.getHighestTextSelectionGroup = function (bApproval, bCompare) {

		// Outline the highest text selection group to be tracked.
		var iHighestTextSelectionGroup = 0;

		// If we want to include the approval canvas.
		if (bApproval) {

			// Get the approval text selections.
			var oApprovalTextSelections = g_oTextCompare.getTextSelectionGroup(false);

			// Loop through each of the approval text selections.
			for (var i = 0; i < oApprovalTextSelections.length; i++) {

				// Update the highest if required.
				iHighestTextSelectionGroup = oApprovalTextSelections[i].g_iGroupOrder > iHighestTextSelectionGroup ? oApprovalTextSelections[i].g_iGroupOrder : iHighestTextSelectionGroup;
			}
		}

		// If we want to include the compare canvas.
		if (bCompare) {

			// Get the compare text selections.
			var oCompareTextSelections = g_oTextCompare.getTextSelectionGroup(true);

			// Loop through each of the compare text selections.
			for (var i = 0; i < oCompareTextSelections.length; i++) {

				// Update the highest if required.
				iHighestTextSelectionGroup = oCompareTextSelections[i].g_iGroupOrder > iHighestTextSelectionGroup ? oCompareTextSelections[i].g_iGroupOrder : iHighestTextSelectionGroup;
			}
		}

		// Return the result.
		return iHighestTextSelectionGroup;
	};

	/**
	 * Gets the group differences class for an order.
	 * @param {int} iGroupOrder - The group order.
	 */
	TextCompare.prototype.getGroupDifferencesClass = function (iGroupOrder) {

		// Check if we have calculated differences.
		if (!g_oTextCompare.g_bDifferencesCalculated) {
			return null;
		}

		// Get the groups for the order.
		var oApprovalSectionGroup = g_oTextCompare.getTextSelectionGroupFromOrder(iGroupOrder, false);
		var oCompareSectionGroup = g_oTextCompare.getTextSelectionGroupFromOrder(iGroupOrder, true);

		// If we have an orphaned statement, return rejected.
		if (oApprovalSectionGroup == null || oCompareSectionGroup == null) {
			return "divCompareSection--rejected";
		}

		// If we have no differences, return accepted.
		if (oApprovalSectionGroup.g_oTextDifferences.length == 0) {
			return "divCompareSection--accepted";
		}

		// Keep track of the worst status.
		var szDifferenceClass = "divCompareSection--ignore";

		// Loop through each of the differences.
		for (var i = 0; i < oApprovalSectionGroup.g_oTextDifferences.length; i++) {

			// If we have a status of rejected.
			if (oApprovalSectionGroup.g_oTextDifferences[i].g_iDifferenceStatus == g_oTextCompare.eDifferenceStatuses.Reject) {

				// Set to the rejected status and stop.
				szDifferenceClass = "divCompareSection--rejected";
				break;
			}
			// If we have an initial status.
			else if (oApprovalSectionGroup.g_oTextDifferences[i].g_iDifferenceStatus == g_oTextCompare.eDifferenceStatuses.Initial) {

				// Set to the initial status.
				szDifferenceClass = "divCompareSection--initial";
			}
			// If we have a status of accepted, and we have not updated to an initial status.
			else if (oApprovalSectionGroup.g_oTextDifferences[i].g_iDifferenceStatus == g_oTextCompare.eDifferenceStatuses.Accept && szDifferenceClass != "divCompareSection--initial") {

				// Set to the accepted status.
				szDifferenceClass = "divCompareSection--accepted";
			}
		}

		// Return the difference class.
		return szDifferenceClass;
	};

	/**
	 * Toggles the text compare information.
	 */
	TextCompare.prototype.toggleTextCompareInformation = function () {

		// If the info bubble does not exist.
		if ($(".speechbubble__paragraph:contains(" + g_oTextCompare.c_szTextCompareInfo_Intro + ")").length == 0) {

			// Show the text alignment info bubble.
			g_oTextCompare.showTextCompareInformation();
		}
		// If the info bubble already exists.
		else {

			// Hide the text alignment info bubble.
			g_oTextCompare.hideTextCompareInformation();
		}
	};

	/**
	 * Toggles the text compare separations.
	 */
	TextCompare.prototype.toggleTextCompareSeparations = function () {

		// If the drop down is currently hidden.
		if ($("#divTextCompareSeparationsDropDown").css("display") == "none") {

			// Show the separations.
			g_oTextCompare.showTextCompareSeparations();
		}
		// If the drop down is currently shown.
		else {

			// Hide the separations.
			g_oTextCompare.hideTextCompareSeparations();
		}
	};

	/**
	 * Shows the text compare separations.
	 */
	TextCompare.prototype.showTextCompareSeparations = function () {
		// Hide the auto auto options and highlight colour.
		g_oTextCompare.hideTextCompareFullDocumentSelectionOptions();
		g_oTextCompare.hideTextCompareHighlightColour();

		// Render the text compare separations.
		g_oTextCompare.renderTextCompareSeparations();

		// Show the drop down.
		$("#divTextCompareSeparationsDropDown").css("display", "block");
	};

	/**
	 * Hides the text compare separations.
	 */
	TextCompare.prototype.hideTextCompareSeparations = function () {

		// Hide the drop down.
		$("#divTextCompareSeparationsDropDown").css("display", "none");
	};

	/**
	 * Renders the text compare separations.
	 */
	TextCompare.prototype.renderTextCompareSeparations = function () {

		// Outline the HTML to be built.
		var szHTML = '';

		// Get the canvas separations.
		var oSeparationList = g_oSeparations.GetSeparationElements(g_oViewer.g_oArtworkViewer, g_oCompare.getSyncCanvas(g_oViewer.g_oArtworkViewer), []);

		// Create the table for the separations.
		szHTML += '<table cellpadding="0" cellspacing="6">';

		// Loop through each of the separations.
		for (var i = 0; i < oSeparationList.length; i++) {

			// Create the separation row.
			szHTML += '<tr>';

			// Add the separation square.
			szHTML += '<td>' + '<div class="swatch" style="cursor: default; background-color: rgb(' + oSeparationList[i].Red + ', ' + oSeparationList[i].Green + ', ' + oSeparationList[i].Blue + ');"></div>' + '</td>';

			// Add the separation name.
			szHTML += '<td>' + oSeparationList[i].SeparationName + '</td>';

			// Add the separation checkbox.
			szHTML += '<td>' + '<input id="chkSeparation_' + i + '" type="checkbox" data-SeparationName="' + oSeparationList[i].SeparationName + '" onclick="g_oTextCompare.updateTextCompareSeparationVisibility(' + i + ');" ' + (oSeparationList[i].Visible ? 'checked' : '') + '/>' + '</td>';

			// Close the separation row.
			szHTML += '</tr>';
		}

		// Close the table for the separations.
		szHTML += '</table>';

		// Apply the HTML.
		$("#divTextCompareSeparationsDropDown").html(szHTML);

		// Update the div position.
		$("#divTextCompareSeparationsDropDown").css("margin-left", ("-" + (($("#divTextCompareSeparationsDropDown").width() / 2) - ($("#btnSeparations_Common_TextCompare").width() / 2) + 2) + "px"));
	};

	/**
	 * Toggles the text compare auto auto options.
	 */
	TextCompare.prototype.toggleTextCompareFullDocumentSelectionOptions = function () {

		// If the drop down is currently hidden.
		if ($("#divTextCompareFullDocumentSelectionDropDown").css("display") == "none") {

			// Show the auto auto options.
			g_oTextCompare.showTextCompareFullDocumentSelectionOptions();
		}
		// If the drop down is currently shown.
		else {

			// Hide the auto auto options.
			g_oTextCompare.hideTextCompareFullDocumentSelectionOptions();
		}
	};

	/**
	 * Shows the text compare auto auto options.
	 */
	TextCompare.prototype.showTextCompareFullDocumentSelectionOptions = function () {
		// Hide the separations and highlight colour.
		g_oTextCompare.hideTextCompareSeparations();
		g_oTextCompare.hideTextCompareHighlightColour();

		// Render the text compare auto auto options.
		g_oTextCompare.renderTextCompareFullDocumentSelectionOptions();

		// Show the drop down.
		$("#divTextCompareFullDocumentSelectionDropDown").css("display", "block");
	};

	/**
	 * Hides the text compare auto auto options.
	 */
	TextCompare.prototype.hideTextCompareFullDocumentSelectionOptions = function () {

		// Hide the drop down.
		$("#divTextCompareFullDocumentSelectionDropDown").css("display", "none");
	};

	/**
	 * Handles the change event for the minimum match words input field.
	 * @param {HTMLElement} element - The input element that triggered the change event.
	 */
	TextCompare.prototype.inputMinMatchWordsOnChange = function (element) {
		var value = parseInt($(element).val(), 10);

		// Ensure the value is between 0 and 100
		if (isNaN(value) || value < 2) {
			value = 2;
		} else if (value > 99) {
			value = 99;
		}

		$(this).val(value);

		// Update the fuzzy threshold setting
		g_oSettings.g_oUserSettings.TextComparisonFullDocumentSelectionMinMatchWords = value;
		
		g_oSettings.updateSettingTextComparisonMinMatchWords(true);
	}

	/**
	 * Handles the change event for the "Match From Document" select element.
	 * @param {HTMLElement} element - The select element that triggered the change event.
	 */
	TextCompare.prototype.MatchFromDocumentOnChange = function (element) {
		var value = parseInt($(element).val(), 10);

		// Update the fuzzy threshold setting
		g_oSettings.g_oUserSettings.TextComparisonFullDocumentSelectionMatchFromDocument = value;

		g_oSettings.updateSettingTextComparisonMatchFromDocument(true);
	}

	/**
	 * Renders the text auto auto options.
	 */
	TextCompare.prototype.renderTextCompareFullDocumentSelectionOptions = function () {
		// Retreive the user settings.
		var iMatchWords = (g_oSettings.g_oUserSettings.TextComparisonFullDocumentSelectionMinMatchWords !== undefined) 
			? g_oSettings.g_oUserSettings.TextComparisonFullDocumentSelectionMinMatchWords 
			: 2;

		var iSelectedMatchFromDocument = (g_oSettings.g_oUserSettings.TextComparisonFullDocumentSelectionMatchFromDocument !== undefined) 
			? g_oSettings.g_oUserSettings.TextComparisonFullDocumentSelectionMatchFromDocument 
			: 1;

		var szHTML = '';
		szHTML += '<table cellpadding="0" cellspacing="6">';

		// Create the row for the Selection Document.
		szHTML += '<tr>';
		szHTML += '<td>Match From (Document):</td>';
		szHTML += '<td><select onchange="g_oTextCompare.MatchFromDocumentOnChange(this);" id="textComparison_FullDocumentSelection_selMatchFromDocument" class="colinput opacityDropdown settings__select">';
		szHTML += '<option value="0"' + (iSelectedMatchFromDocument === 0 ? ' selected' : '') + '>Approval</option>';
		szHTML += '<option value="1"' + (iSelectedMatchFromDocument === 1 ? ' selected' : '') + '>Compare</option>';
		szHTML += '</select></td>';
		szHTML += '</tr>';

		// Create the row for the Minimum Words.
		szHTML += '<tr>';
		szHTML += '<td>Min. Match Words:</td>';
		szHTML += '<td class="settings__column">';
		szHTML += '<input onchange="g_oTextCompare.inputMinMatchWordsOnChange(this);" id="textComparison_FullDocumentSelection_inputMinMatchWords" type="number" class="colinput opacityDropdown settings__select" min="2" max="99" value="' + iMatchWords + '" style="width: 38px !important;">';
		szHTML += '</td>';
		szHTML += '</tr>';

		// Create the row for the Confirm button.
		szHTML += '<tr>';
		szHTML += '<td colspan="4" style="text-align: center;">';
		szHTML += '<div onclick="g_oTextCompare_FullDocumentSelection.ConfirmToStartFullDocumentSelection();" class="alignment__button alignment__button--blue">Perform Match</div>';
		szHTML += '</td>';
		szHTML += '</tr>';

		szHTML += '</table>';

		// Apply the HTML.
		$("#divTextCompareFullDocumentSelectionDropDown").html(szHTML);

		// Update the div position.
		$("#divTextCompareFullDocumentSelectionDropDown").css("margin-left", ("-" + (($("#divTextCompareFullDocumentSelectionDropDown").width() / 2) - ($("#btnFullDocumentSelection_Common_TextCompare").width() / 2) + 2) + "px"));
	};

	/**
	 * Toggles the text compare highlight colour.
	 */
	TextCompare.prototype.toggleTextCompareHighlightColour = function () {

		// If the drop down is currently hidden.
		if ($("#divTextCompareHighlightColourDropDown").css("display") == "none") {

			// Show the highlight colour.
			g_oTextCompare.showTextCompareHighlightColour();
		}
		// If the drop down is currently shown.
		else {

			// Hide the highlight colour.
			g_oTextCompare.hideTextCompareHighlightColour();
		}
	};

	/**
	 * Shows the text compare highlight colour.
	 */
	TextCompare.prototype.showTextCompareHighlightColour = function () {
		// Hide the auto auto options and separations.
		g_oTextCompare.hideTextCompareFullDocumentSelectionOptions();
		g_oTextCompare.hideTextCompareSeparations();

		// Render the text compare highlight colours.
		g_oTextCompare.renderTextCompareHighlightColour();

		// Show the drop down.
		$("#divTextCompareHighlightColourDropDown").css("display", "block");
	};

	/**
	 * Hides the text compare highlight colour.
	 */
	TextCompare.prototype.hideTextCompareHighlightColour = function () {

		// Hide the drop down.
		$("#divTextCompareHighlightColourDropDown").css("display", "none");
	};

	/**
	 * Renders the text compare highlight colour.
	 */
	TextCompare.prototype.renderTextCompareHighlightColour = function () {

		// Outline the HTML to be built.
		var szHTML = '';

		// Create the table for the highlight colours.
		szHTML += '<table id="dropDownTextComparisonHighlightColourPopup" cellpadding="0" cellspacing="4">';

		// Add the colours.
		szHTML += '<tr>';
		szHTML += '<td><div class="annotationColorSelection blue" style="border-color: transparent;" data-annotationcolour="blue"></div></td>';
		szHTML += '<td><div class="annotationColorSelection red" style="border-color: transparent;" data-annotationcolour="red"></div></td>';
		szHTML += '<td><div class="annotationColorSelection green" style="border-color: transparent;" data-annotationcolour="green"></div></td>';
		szHTML += '</tr>';
		szHTML += '<tr>';
		szHTML += '<td><div class="annotationColorSelection orange" style="border-color: transparent;" data-annotationcolour="orange"></div></td>';
		szHTML += '<td><div class="annotationColorSelection purple" style="border-color: transparent;" data-annotationcolour="purple"></div></td>';
		szHTML += '<td><div class="annotationColorSelection cyan" style="border-color: transparent;" data-annotationcolour="cyan"></div></td>';
		szHTML += '</tr>';
		szHTML += '<tr>';
		szHTML += '<td><div class="annotationColorSelection yellow" style="border-color: transparent;" data-annotationcolour="yellow"></div></td>';
		szHTML += '<td><div class="annotationColorSelection magenta" style="border-color: transparent;" data-annotationcolour="magenta"></div></td>';
		szHTML += '<td><div class="annotationColorSelection grey" style="border-color: transparent;" data-annotationcolour="grey"></div></td>';
		szHTML += '</tr>';

		// Close the table for the separations.
		szHTML += '</table>';

		// Apply the HTML.
		$("#divTextCompareHighlightColourDropDown").html(szHTML);

		// Update the div position.
		$("#divTextCompareHighlightColourDropDown").css("margin-left", ("-" + (($("#divTextCompareHighlightColourDropDown").width() / 2) - ($("#btnColour_Common_TextCompare").width() / 2) + 6) + "px"));

		// Set the selected colour.
		$("#dropDownTextComparisonHighlightColourPopup").find(".annotationColorSelection").css("border-color", "transparent");
		$("#dropDownTextComparisonHighlightColourPopup").find(".annotationColorSelection." + g_oAnnotations.getStringColour(g_oSettings.g_oUserSettings.TextComparisonHighlightColour)).css("border-color", g_oSettings.c_iSkinType == 0 ? "white" : "black");

		// Update the click event of the colour tiles.
		$("#dropDownTextComparisonHighlightColourPopup").find(".annotationColorSelection").off('click').on('click', function () {
			g_oTextCompare.updateTextCompareHighlightColour(event.target.getAttribute('data-annotationcolour'));
		});
	};

	/**
	 * Updates the text compare highlight colour.
	 * @param {any} szHighlightColour - The highlight colour.
	 */
	TextCompare.prototype.updateTextCompareHighlightColour = function (szHighlightColour) {

		// Update the text comparison highlight colour.
		g_oTextCompare.g_szHighlightColour = g_oAnnotations.getStringColour(szHighlightColour);

		// Update the text comparison highlight colour setting and sync.
		g_oSettings.g_oUserSettings.TextComparisonHighlightColour = g_oAnnotations.getHexColour(szHighlightColour);
		g_oSettings.updateSettingTextComparisonHighlightColour(true);

		// Hide any shown highlight colour panels.
		g_oTextCompare.hideTextCompareHighlightColour();
		$("#settingTextComparisonHighlightColourPopup").remove();

		// If a text compare is in progress, render any text compare annotations.
		if (g_oTextCompare.g_bTextCompareInProgress) {
			g_oTextCompare.renderTextCompareAnnotations();
		}
	};

	/**
	 * Updates the text compare separation visibility.
	 * @param {string} szSeparationName - The separation name.
	 * @param {int} iCheckboxId - The checkbox id.
	 */
	TextCompare.prototype.updateTextCompareSeparationVisibility = function (iCheckboxId) {

		// Determine the visibility from the checkbox.
		var bVisible = $('#chkSeparation_' + iCheckboxId).is(":checked");

		// Determine the separation name.
		var szSeparationName = $('#chkSeparation_' + iCheckboxId).attr("data-SeparationName");

		// If we have turned off the visibility, check we have at least one separation visible, otherwise turn this back on.
		if (!bVisible && $('#divTextCompareSeparationsDropDown input:checked').length == 0) {
			$('#chkSeparation_' + iCheckboxId).prop('checked', true);
			g_oGreenlightMessages.displayWarningBubble(g_oTextCompare.c_szTextCompareOneSeparation);
			return;
		}

		// Set the separation visbility.
		g_oSeparations.SetSeparationVisibility(g_oViewer.g_oArtworkViewer, g_oCompare.getSyncCanvas(g_oViewer.g_oArtworkViewer), szSeparationName, bVisible);

		// Update the stored hidden separations.
		g_oTextCompare.updateTextCompareHiddenSeparations();
	};

	/**
	 * Resets the text compare hidden separations.
	 */
	TextCompare.prototype.resetTextCompareHiddenSeparations = function () {

		// Reset the list of hidden separations.
		g_oTextCompare.g_oHiddenSeparations = [];
	};

	/**
	 * Updates the text compare hidden separations.
	 */
	TextCompare.prototype.updateTextCompareHiddenSeparations = function () {

		// Reset the list of hidden separations.
		g_oTextCompare.resetTextCompareHiddenSeparations();

		// Get a list of the hidden separatins.
		var oHiddenSeparations = $('#divTextCompareSeparationsDropDown input:not(:checked)');

		// Add each of the hidden separations to the list.
		for (var i = 0; i < oHiddenSeparations.length; i++) {
			g_oTextCompare.g_oHiddenSeparations.push($(oHiddenSeparations[i]).attr("data-SeparationName"));
		}
	};

	/**
	 * Shows the text compare information.
	 */
	TextCompare.prototype.showTextCompareInformation = function () {

		// Show the text compare info bubble.
		g_oGreenlightMessages.displayInfoBubble(
			'<div class="speechbubble__paragraph speechbubble__paragraph--large">' + g_oTextCompare.c_szTextCompareInfo_Intro + '</div>' +
			'<div class="speechbubble__paragraph speechbubble__paragraph--large">' + g_oTextCompare.c_szTextCompareInfo_Add + ' <img class="speechbubble__image speechbubble__image--small" src="img/TextCursor_24 blue.png" /></div>' +
			'<div class="speechbubble__paragraph speechbubble__paragraph--large">' + g_oTextCompare.c_szTextCompareInfo_Remove + ' <img class="speechbubble__image speechbubble__image--small" src="img/Delete_col_24.png" /></div>' +
			'<div class="speechbubble__paragraph speechbubble__paragraph--large">' + g_oTextCompare.c_szTextCompareInfo_Review + '</div>' +
			'<div class="speechbubble__paragraph speechbubble__paragraph--large">' + g_oTextCompare.c_szTextCompareInfo_Exit + '</div>'
			, false);
	};

	/**
	 * Hides the text compare information.
	 */
	TextCompare.prototype.hideTextCompareInformation = function () {

		// Hide the alignment info bubble.
		g_oGreenlightMessages.closeBubbles("Info");
	};

	/**
	 * Starts the text drag.
	 * @param {object} event - The event.
	 * @param {boolean} bCompare - If this is the compare canvas.
	 */
	TextCompare.prototype.startTextDrag = function (event, bCompare) {

		// Get the id of the dragged div.
		var szDivId = event.target.id == "" ? event.target.parentNode.id : event.target.id;

		// Set the data of the drag.
		event.dataTransfer.setData("text", szDivId);

		// Give the div the dragging class.
		$("#" + szDivId).addClass("divTextSection--dragging");

		// Have to delay the call to initialise the drag function as Chrome fires the event before the initialisation.
		setTimeout(function () {

			// Determine if we are showing the drop zones for the approval or compare.
			var szCanvasDropZoneClass = bCompare ? "textCompareDropZoneWrapper--compare" : "textCompareDropZoneWrapper--approval";

			// Activate the drop zones and wrappers.
			$("." + szCanvasDropZoneClass).addClass("textCompareDropZoneWrapper--active");

			// Activate the drag drop new group.
			$("#divDragDropNewGroup").show();

			// Activate the drag scroll zones.
			$(".divScrollZoneTop").show();
			$(".divScrollZoneBottom").show();
		}, 10);
	};

	/**
	 * Move text drag event.
	 * @param {object} event - The event.
	 * @param {boolean} moveUp - If we are moving up or down.
	 */
	TextCompare.prototype.moveTextDrag = function (event, moveUp) {

		// Ensure we are not already drag scrolling.
		if (g_oTextCompare.g_bDragScrolling) {
			return;
		}

		// Set that we are drag scrolling.
		g_oTextCompare.g_bDragScrolling = true;

		// Get the current scroll position of the sections div.
		var iCurrentScrollTop = $("#divTextCompareSections").scrollTop();

		// Determine the amount to be scrolled.
		var iScrollAmount = (moveUp ? -100 : 100);

		// Scroll the div.
		$("#divTextCompareSections").animate({ scrollTop: iCurrentScrollTop + iScrollAmount }, 200, function () { g_oTextCompare.g_bDragScrolling = false; });
	};

	/**
	 * End the text drag.
	 * @param {object} event - The event.
	 */
	TextCompare.prototype.endTextDrag = function (event) {

		// Remove any dragging classes.
		$(".divTextSection--dragging").removeClass("divTextSection--dragging");

		// Deactivate the drag drop new group.
		$("#divDragDropNewGroup").hide();

		// Deactivates the drop zones.
		$(".textCompareDropZoneWrapper--active").removeClass("textCompareDropZoneWrapper--active");

		// Deactivate the drag scroll zones.
		$(".divScrollZoneTop").hide();
		$(".divScrollZoneBottom").hide();
	};

	/**
	 * Text drag over event.
	 * @param {object} event - The event.
	 */
	TextCompare.prototype.textDragOver = function (event) {
		event.preventDefault();
		event.stopPropagation();
		$(event.target).addClass("textCompareDropZone--hover");
	};

	/**
	 * Text drag out event.
	 * @param {object} event - The event.
	 */
	TextCompare.prototype.textDragOut = function (event) {
		event.preventDefault();
		event.stopPropagation();
		$(event.target).removeClass("textCompareDropZone--hover");
	};

	/**
	 * Text drop event.
	 * @param {object} event - The event.
	 */
	TextCompare.prototype.textDrop = function (event) {
		event.preventDefault();
		event.stopPropagation();

		// End the text drag.
		g_oTextCompare.endTextDrag();

		// Get the dragged item and destination ids.
		var oDraggedItemId = event.dataTransfer.getData("text");
		var oDragDestination = event.target.id;

		// Split these into the individual properties.
		var iDestinationGroupOrder = parseInt(oDragDestination.split("_")[1]);
		var iDestinationSelectionOrder = parseInt(oDragDestination.split("_")[2]);
		var iDraggedGroupOrder = parseInt(oDraggedItemId.split("_")[1]);
		var iDraggedTextData = parseInt(oDraggedItemId.split("_")[2]);
		var bCompareCanvas = (oDraggedItemId.split("_")[3] == "true");

		// Get the group indexes from the group order.
		var iDestinationGroupIndex = g_oTextCompare.getTextSelectionGroupIndexFromOrder(iDestinationGroupOrder, bCompareCanvas);
		var iDraggedGroupIndex = g_oTextCompare.getTextSelectionGroupIndexFromOrder(iDraggedGroupOrder, bCompareCanvas);

		// Move the text data.
		g_oTextCompare.moveTextData(iDestinationGroupOrder, iDestinationGroupIndex, iDestinationSelectionOrder, iDraggedGroupIndex, iDraggedTextData, bCompareCanvas);
	};

	/**
	 * Moves the text data.
	 * @param {int} iTargetGroupOrder - The target group order.
	 * @param {int} iTargetGroup - The target group.
	 * @param {int} iTargetGroupSelection - The target group selection.
	 * @param {int} iSelectionGroup - The selection group.
	 * @param {int} iSelection - The selection.
	 * @param {boolean} bCompareCanvas - If this is the compare canvas.
	 */
	TextCompare.prototype.moveTextData = function (iTargetGroupOrder, iTargetGroup, iTargetGroupSelection, iSelectionGroup, iSelection, bCompareCanvas) {

		// Get the text selection group we are going to add to.
		var oTextSelectionGroup = g_oTextCompare.getTextSelectionGroup(bCompareCanvas);

		// Take a copy of the text data.
		var oCopiedTextData = deepCopyArray(oTextSelectionGroup[iSelectionGroup].g_oTextSelections[iSelection]);

		// If the target group does not exist, add this.
		if (iTargetGroup == -1) {
			oTextSelectionGroup.push(new g_oTextCompare.textSelectionGroup(iTargetGroupOrder));
			iTargetGroup = oTextSelectionGroup.length - 1;
		}

		// Add the text data to the group at the required position.
		oTextSelectionGroup[iTargetGroup].g_oTextSelections.splice(iTargetGroupSelection, 0, oCopiedTextData);

		// If we have moved this text data into the same group before the existing item, update the index being removed.
		if (iTargetGroup == iSelectionGroup && iTargetGroupSelection < iSelection) {
			iSelection++;
		}

		// Delete the text data.
		g_oTextCompare.deleteTextData(iSelectionGroup, iSelection, bCompareCanvas, false);

		// Drop any unused text groups.
		g_oTextCompare.dropUnusedTextGroups();

		// Render the text compare annotations.
		g_oTextCompare.renderTextCompareAnnotations();
	};

	/**
	 * Toggles the text section.
	 * @param {int} iSelectionGroup - The section to toggle.
	 */
	TextCompare.prototype.toggleTextSection = function (iSelectionGroup) {

		// Clear the filter box input value.
		$("#txtTextComparison_FilterBox").val("");

		// If this group is already collapsed.
		if (g_oTextCompare.g_oCollapsedSectionOrders.indexOf(iSelectionGroup) != -1) {

			// Expand the group.
			g_oTextCompare.expandTextSection(iSelectionGroup);
		}
		// If this group is not already collapsed.
		else {

			// Collapse the group.
			g_oTextCompare.collapseTextSection(iSelectionGroup);
		}
	};

	/**
	 * Collapses the text section.
	 * @param {int} iSelectionGroup - The text section.
	 */
	TextCompare.prototype.collapseTextSection = function (iSelectionGroup) {

		// Add the group to the collapsed list.
		g_oTextCompare.g_oCollapsedSectionOrders.push(iSelectionGroup);

		// Update the group toggle icon.
		$("#divCompareSectionIcon_" + iSelectionGroup).attr("src", "img/Arrow_Right_16.png");

		// Collapse the group.
		$("#divCompareSections_" + iSelectionGroup).slideToggle();
	};

	/**
	 * Expands the text section.
	 * @param {int} iSelectionGroup - The text section.
	 */
	TextCompare.prototype.expandTextSection = function (iSelectionGroup) {

		// Remove the group from the collapsed list.
		var iIndex = g_oTextCompare.g_oCollapsedSectionOrders.indexOf(iSelectionGroup);
		if (iIndex > -1) {
			g_oTextCompare.g_oCollapsedSectionOrders.splice(iIndex, 1);
		}

		// Update the group toggle icon.
		$("#divCompareSectionIcon_" + iSelectionGroup).attr("src", "img/Arrow_Down_16.png");

		// Expand the group.
		$("#divCompareSections_" + iSelectionGroup).slideToggle();
	};

	/**
	 * Toggles the filter.
	 */
	TextCompare.prototype.toggleFilter = function () {

		// If the filter is currently hidden.
		if ($('#txtTextComparison_FilterBox').css("opacity") == "0") {

			// Show the filter box.
			$('#txtTextComparison_FilterBox').css("display", "block")

			// Animate in the filter box.
			$('#txtTextComparison_FilterBox').animate({
				"opacity": '1',
				"margin-right": '16px',
			});
		}
		// If the filter is currently shown.
		else {

			// Clear the filter box input value.
			$("#txtTextComparison_FilterBox").val("");

			// Animate out the filter box.
			$('#txtTextComparison_FilterBox').animate({
				"opacity": '0',
				"margin-right": '0px',
			}, "", function () { $('#txtTextComparison_FilterBox').css("display", "none") });
		}

		// Apply the filter.
		g_oTextCompare.applyFilter();
	};

	/**
	 * Applies the filter.
	 */
	TextCompare.prototype.applyFilter = function () {

		// Clear any collapsed sections as these will be collapsed based on the filter.
		g_oTextCompare.g_oCollapsedSectionOrders = [];

		// Get the filter value.
		var szFilter = $("#txtTextComparison_FilterBox").val();

		// If we have a filter.
		if (szFilter.length > 0) {

			// Get the highest text selection group.
			var iHighestTextSelectionGroup = g_oTextCompare.getHighestTextSelectionGroup(true, true);

			// Loop through each text selection group.
			for (var i = 1; i <= iHighestTextSelectionGroup; i++) {

				// If this group does not match our filter.
				if (!g_oTextCompare.groupMatchesFilter(i, szFilter)) {

					// Add the group to the collapsed list.
					g_oTextCompare.g_oCollapsedSectionOrders.push(i);
				}
			}
		}

		// Render the text compare annotations.
		g_oTextCompare.renderTextCompareAnnotations();
	};

	/**
	 * Checks if the group matches the filter.
	 * @param {int} iGroupOrder - The group order.
	 * @param {string} szFilter - The filter.
	 */
	TextCompare.prototype.groupMatchesFilter = function (iGroupOrder, szFilter) {

		// Get the approval group for the order.
		var oApprovalSectionGroup = g_oTextCompare.getTextSelectionGroupFromOrder(iGroupOrder, false);

		// If we have an approval selection group.
		if (oApprovalSectionGroup != null) {

			// Loop through each of the text selections.
			for (var i = 0; i < oApprovalSectionGroup.g_oTextSelections.length; i++) {

				// Determine the text to be compared.
				var oSelectionText = g_oTextCompare.g_iAccuracy == g_oTextCompare.eAccuracyLevels.Word ? oApprovalSectionGroup.g_oTextSelections[i].g_oSelectionWordsText : oApprovalSectionGroup.g_oTextSelections[i].g_oSelectionCharactersText;

				// If this text selection matches the filter.
				if (oSelectionText.toLowerCase().indexOf(szFilter.toLowerCase()) != -1) {

					// Return that we have a match.
					return true;
				}
			}
		}

		// Get the compare group for the order.
		var oCompareSectionGroup = g_oTextCompare.getTextSelectionGroupFromOrder(iGroupOrder, true);

		// If we have a compare selection group.
		if (oCompareSectionGroup != null) {

			// Loop through each of the text selections.
			for (var i = 0; i < oCompareSectionGroup.g_oTextSelections.length; i++) {

				// Determine the text to be compared.
				var oSelectionText = g_oTextCompare.g_iAccuracy == g_oTextCompare.eAccuracyLevels.Word ? oCompareSectionGroup.g_oTextSelections[i].g_oSelectionWordsText : oCompareSectionGroup.g_oTextSelections[i].g_oSelectionCharactersText;

				// If this text selection matches the filter.
				if (oSelectionText.toLowerCase().indexOf(szFilter.toLowerCase()) != -1) {

					// Return that we have a match.
					return true;
				}
			}
		}

		// Return no match was found.
		return false;
	};

	/**
	 * Calculates the differences.
	 */
	TextCompare.prototype.calculateDifferences = function () {

		// If we have already calculated the differences, do nothing.
		if (g_oTextCompare.g_bDifferencesCalculated) {
			return;
		}

		// Get the highest text selection group.
		var iHighestTextSelectionGroup = g_oTextCompare.getHighestTextSelectionGroup(true, true);

		// Ensure we have at least one selection group to calculate.
		if (iHighestTextSelectionGroup < 1) {
			g_oGreenlightMessages.displayWarningBubble(g_oTextCompare.c_szTextCompareDifferenceNoSelections);
			return;
		}

		// Set that we have calculated differences.
		g_oTextCompare.g_bDifferencesCalculated = true;

		// Set the approval combo control to read only.
		var iApprovalComboControlId = comboControl_GetControlById("approvalNameComboControl", true);
		comboControl_UpdateReadOnly(iApprovalComboControlId, true);

		// Set the compare combo control to read only.
		var iCompareComboControlId = comboControl_GetControlById("approvalCompareComboControl", true);
		comboControl_UpdateReadOnly(iCompareComboControlId, true);

		// Get the accuracy to calculate the differences to.
		g_oTextCompare.g_iAccuracy = parseInt($("#divTextComparisonAccuracy").val());

		// Get the case sensitivity to calculate the differences to.
		g_oTextCompare.g_iCaseSensitivity = parseInt($("#divTextComparisonCaseSensitivity").val());

		// Get the bold italic detection to calculate differences to.
		g_oTextCompare.g_iBoldItalicDetection = parseInt($("#divTextComparisonBoldItalicDetection").val());

		// Get the whitespace detection setting to calculate differences to.
		g_oTextCompare.g_iWhitespaceDetection = parseInt($("#divTextComparisonWhitespaceDetection").val());

		// Get the auto selection setting to calculate differences to.
		g_oTextCompare.g_iAutoSelection = parseInt($("#divTextComparisonAutoSelection").val());

		// Update the active tab.
		$("#divTextComparisonTitle_Review").removeClass("divTextComparisonTitle__Text--active");
		$("#divTextComparisonTitle_Comparison").addClass("divTextComparisonTitle__Text--active");

		// Show the differences bottom bar.
		$("#divTextDifferences").show();

		// Disable the icons that edit text selections.
		$("#btnTextHighlight_Viewer_TextCompare").addClass("ToolbarImage--disabled");
		$("#btnTextHighlight_Compare_TextCompare").addClass("ToolbarImage--disabled");
		$("#btnParagraphHighlight_Viewer_TextCompare").addClass("ToolbarImage--disabled");
		$("#btnParagraphHighlight_Compare_TextCompare").addClass("ToolbarImage--disabled");
		$("#btnAreaTextHighlight_Viewer_TextCompare").addClass("ToolbarImage--disabled");
		$("#btnAreaTextHighlight_Compare_TextCompare").addClass("ToolbarImage--disabled");
		$("#btnTextClear_Viewer_TextCompare").addClass("ToolbarImage--disabled");
		$("#btnTextClear_Compare_TextCompare").addClass("ToolbarImage--disabled");
		$("#btnTextClear_Common_TextCompare").addClass("ToolbarImage--disabled");

		// Enable the step through icon.
		$("#imgTextComparison_StepThrough").removeClass("divTextComparisonTitle__Option--disabled");

		// Disable the select lists.
		$("#divTextComparisonAccuracy").prop("disabled", "disabled");
		$("#divTextComparisonCaseSensitivity").prop("disabled", "disabled");
		$("#divTextComparisonBoldItalicDetection").prop("disabled", "disabled");
		$("#divTextComparisonWhitespaceDetection").prop("disabled", "disabled");
		$("#divTextComparisonAutoSelection").prop("disabled", "disabled");

		// Reset the collpased sections.
		g_oTextCompare.g_oCollapsedSectionOrders = [];

		// Update the difference calculations.
		g_oTextCompare.updateDifferenceCalculations();

		// Render the text compare annotations.
		g_oTextCompare.renderTextCompareAnnotations();

		// Collapse the sections with no differences.
		for (var i = 1; i <= iHighestTextSelectionGroup; i++) {
			if (g_oTextCompare.countDifferences(i, null, null) == 0) {
				$("#divCompareSectionIcon_" + i).click();
			}
		}
	};

	/**
	 * Updates the difference calculations.
	 */
	TextCompare.prototype.updateDifferenceCalculations = function () {

		// Reset the difference counter.
		g_oTextCompare.g_iNumberOfDifferences = 0;

		// Get the highest text selection group.
		var iHighestTextSelectionGroup = g_oTextCompare.getHighestTextSelectionGroup(true, true);

		// Loop through each of the sections up to the highest.
		for (var i = 1; i <= iHighestTextSelectionGroup; i++) {

			// Calculate the differences for this group.
			g_oTextCompare.calculateGroupDifferences(i);
		}

		// Outline the summary icon to be added.
		var szSummaryIcon = '<img class="textCompareSummaryIcon" src="img/Info-Icon_black_48.png" onmouseover="g_oTextCompare.showDifferencesSummary();" onmouseout="g_oTextCompare.hideDifferencesSummary();" />';

		// Outline the print annotations icon to be added.
		var szPrintIcon = '<img class="textPrintDifferencesReportIcon tooltip" src="img/approval_print24.png" onclick="g_oTextCompareReport.printDifferencesReport();" title="Print Differences Report" />';

		// Update the differences heading.
		var iDifferencesCount = g_oTextCompare.countDifferences(null, null, null);
		$("#divTextCompareHeadingDifferences").html(iDifferencesCount.toString() + ' ' + (iDifferencesCount == 1 ? g_oTextCompare.c_szTextCompareDifferenceLocated : g_oTextCompare.c_szTextCompareDifferencesLocated) + szSummaryIcon + szPrintIcon);
	};

	/**
	 * Resets the differences.
	 */
	TextCompare.prototype.resetDifferences = function () {

		// If we have not calculated the differences, do nothing.
		if (!g_oTextCompare.g_bDifferencesCalculated) {
			return;
		}

		// Set that we have not calculated differences.
		g_oTextCompare.g_bDifferencesCalculated = false;

		// Set the approval combo control to not read only.
		var iApprovalComboControlId = comboControl_GetControlById("approvalNameComboControl", true);
		comboControl_UpdateReadOnly(iApprovalComboControlId, false);

		// Set the compare combo control to not read only.
		var iCompareComboControlId = comboControl_GetControlById("approvalCompareComboControl", true);
		comboControl_UpdateReadOnly(iCompareComboControlId, false);

		// Update the active tab.
		$("#divTextComparisonTitle_Review").addClass("divTextComparisonTitle__Text--active");
		$("#divTextComparisonTitle_Comparison").removeClass("divTextComparisonTitle__Text--active");

		// Clear the collapsed sections.
		g_oTextCompare.g_oCollapsedSectionOrders = [];

		// Reset the differences heading.
		$("#divTextCompareHeadingDifferences").html(g_oTextCompare.c_szTextCompareDifferencesNotCalculated);

		// Hide the differences bottom bar.
		$("#divTextDifferences").hide();

		// Enable the icons that edit text selections.
		$("#btnTextHighlight_Viewer_TextCompare").removeClass("ToolbarImage--disabled");
		$("#btnTextHighlight_Compare_TextCompare").removeClass("ToolbarImage--disabled");
		$("#btnParagraphHighlight_Viewer_TextCompare").removeClass("ToolbarImage--disabled");
		$("#btnParagraphHighlight_Compare_TextCompare").removeClass("ToolbarImage--disabled");
		$("#btnAreaTextHighlight_Viewer_TextCompare").removeClass("ToolbarImage--disabled");
		$("#btnAreaTextHighlight_Compare_TextCompare").removeClass("ToolbarImage--disabled");
		$("#btnTextClear_Viewer_TextCompare").removeClass("ToolbarImage--disabled");
		$("#btnTextClear_Compare_TextCompare").removeClass("ToolbarImage--disabled");
		$("#btnTextClear_Common_TextCompare").removeClass("ToolbarImage--disabled");

		// Disable the step through icon.
		$("#imgTextComparison_StepThrough").addClass("divTextComparisonTitle__Option--disabled");

		// Enable the select lists if these are not locked by the consumer.
		$("#divTextComparisonAccuracy").prop("disabled", g_oSettings.g_oConsumerSettings.LockTextComparisonAccuracy ? "disabled" : "");
		$("#divTextComparisonCaseSensitivity").prop("disabled", g_oSettings.g_oConsumerSettings.LockTextComparisonCaseSensitivity ? "disabled" : "");
		$("#divTextComparisonBoldItalicDetection").prop("disabled", g_oSettings.g_oConsumerSettings.LockTextComparisonBoldItalicDetection ? "disabled" : "");
		$("#divTextComparisonWhitespaceDetection").prop("disabled", g_oSettings.g_oConsumerSettings.LockTextComparisonWhitespaceDetection ? "disabled" : "");
		$("#divTextComparisonAutoSelection").prop("disabled", g_oSettings.g_oConsumerSettings.LockTextComparisonAutoSelection ? "disabled" : "");

		// Reset all approval section differences.
		for (var i = 0; i < g_oTextCompare.g_oArtworkTextSelectionGroups.length; i++) {
			g_oTextCompare.g_oArtworkTextSelectionGroups[i].g_oTextModifications = [];
			g_oTextCompare.g_oArtworkTextSelectionGroups[i].g_oTextDifferences = [];
			g_oTextCompare.resetListStatuses(g_oTextCompare.g_oArtworkTextSelectionGroups[i]);
		}

		// Reset all compare section differences.
		for (var j = 0; j < g_oTextCompare.g_oCompareTextSelectionGroups.length; j++) {
			g_oTextCompare.g_oCompareTextSelectionGroups[j].g_oTextModifications = [];
			g_oTextCompare.g_oCompareTextSelectionGroups[j].g_oTextDifferences = [];
			g_oTextCompare.resetListStatuses(g_oTextCompare.g_oCompareTextSelectionGroups[j]);
		}

		// Render the text compare annotations.
		g_oTextCompare.renderTextCompareAnnotations();
	};

	/**
	 * Counts the differences.
	 * @param {int} iGroupOrder - The group order.
	 * @param {enum} eDifferenceType - The difference type to limit to.
	 * @param {enum} eDifferenceStatuses - The status type to limit to.
	 */
	TextCompare.prototype.countDifferences = function (iGroupOrder, eDifferenceType, eDifferenceStatus) {

		// Keep track of the number of differences.
		var iDifferences = 0;

		// Get the highest text selection group.
		var iHighestTextSelectionGroup = g_oTextCompare.getHighestTextSelectionGroup(true, true);

		// Loop through each of the sections up to the highest.
		for (var i = 1; i <= iHighestTextSelectionGroup; i++) {

			// If we have passed a group order and this does not match.
			if (iGroupOrder != null && iGroupOrder != i) {
				continue;
			}

			// Get the approval group for the order.
			var oApprovalSectionGroup = g_oTextCompare.getTextSelectionGroupFromOrder(i, false);

			// If the group exists and differences have been calculated.
			if (oApprovalSectionGroup != null && oApprovalSectionGroup.g_oTextDifferences != null) {

				// Loop through each of the differences.
				for (var j = 0; j < oApprovalSectionGroup.g_oTextDifferences.length; j++) {

					// If we have passed a difference type and this does not match.
					if (eDifferenceType != null && eDifferenceType != oApprovalSectionGroup.g_oTextDifferences[j].g_eDifferenceType) {
						continue;
					}

					// If we have passed a status type and this does not match.
					if (eDifferenceStatus != null && eDifferenceStatus != oApprovalSectionGroup.g_oTextDifferences[j].g_iDifferenceStatus) {
						continue;
					}

					// Add the difference.
					iDifferences++;
				}
			}
		}

		// Return the number of differences.
		return iDifferences;
	};

	/**
	 * Calculates the group differences.
	 * @param {int} iGroupOrder - The group order.
	 */
	TextCompare.prototype.calculateGroupDifferences = function (iGroupOrder) {

		// Get the approval and compare selection group for the order.
		var oApprovalSectionGroup = g_oTextCompare.getTextSelectionGroupFromOrder(iGroupOrder, false);
		var oCompareSectionGroup = g_oTextCompare.getTextSelectionGroupFromOrder(iGroupOrder, true);

		// If we do not have both sections.
		if (oApprovalSectionGroup == null || oCompareSectionGroup == null) {

			// Set that differences could not be calculated.
			if (oApprovalSectionGroup != null) {
				oApprovalSectionGroup.g_oTextModifications = null;
				oApprovalSectionGroup.g_oTextDifferences = null;
				g_oTextCompare.resetListStatuses(oApprovalSectionGroup);
			}

			// Set that differences could not be calculated.
			if (oCompareSectionGroup != null) {
				oCompareSectionGroup.g_oTextModifications = null;
				oCompareSectionGroup.g_oTextDifferences = null;
				g_oTextCompare.resetListStatuses(oCompareSectionGroup);
			}

			// Do not process the differences.
			return;
		}

		// Get the approval and compare combined text.
		var szApprovalText = g_oTextCompare.combineTextSelections(oApprovalSectionGroup.g_oTextSelections);
		var szCompareText = g_oTextCompare.combineTextSelections(oCompareSectionGroup.g_oTextSelections);

		// If we are performing a case insensitive match, replace any text with lower case.
		if (g_oTextCompare.g_iCaseSensitivity == g_oTextCompare.eCaseSensitivityLevels.Off) {
			szApprovalText = szApprovalText.toLowerCase();
			szCompareText = szCompareText.toLowerCase();
		}

		// Calculate the text comparisons to the correct level.
		var oTextComparisons = g_oTextCompare.calculateTextComparison(szCompareText, szApprovalText);

		// Convert the text comparisons to text modifications.
		var oTextModifications = g_oTextCompare.convertTextComparisonsToModifications(oTextComparisons);

		// Remove any text equivalents from the modifications.
		g_oTextCompare_Equivalents.removeTextEquivalentsFromModifications(oTextModifications);

		// Convert the text modifications to text differences.
		var oTextDifferences = g_oTextCompare.convertTextModificationsToDifferences(oTextModifications);

		// Update the approval group.
		oApprovalSectionGroup.g_oTextModifications = oTextModifications;
		oApprovalSectionGroup.g_oTextDifferences = oTextDifferences;

		// Update the compare group.
		oCompareSectionGroup.g_oTextModifications = oTextModifications;
		oCompareSectionGroup.g_oTextDifferences = oTextDifferences;

		// Update the list statuses.
		g_oTextCompare.updateListStatuses(oApprovalSectionGroup, false);
		g_oTextCompare.updateListStatuses(oCompareSectionGroup, true);

		// If we are calculating the bold italic differences.
		if (g_oTextCompare.g_iBoldItalicDetection == g_oTextCompare.eBoldItalicDetectionLevels.On) {
			g_oTextCompare.calculateBoldItalicDifferences(oApprovalSectionGroup.g_oTextSelections, oCompareSectionGroup.g_oTextSelections, oApprovalSectionGroup.g_oTextDifferences);
		}

		// Generate the formatted differences text.
		g_oTextCompare.generateFormattedDifferenceText(oApprovalSectionGroup, oCompareSectionGroup, oApprovalSectionGroup.g_oTextDifferences);
	};

	/**
	 * Resets the word list statuses.
	 * @param {object} oSelectionGroup - The text selection group.
	 */
	TextCompare.prototype.resetListStatuses = function (oSelectionGroup) {

		// Loop through each of the text selections.
		for (var i = 0; i < oSelectionGroup.g_oTextSelections.length; i++) {

			// Loop through each of the words in the selection.
			for (var j = 0; j < oSelectionGroup.g_oTextSelections[i].g_oSelectionWords.length; j++) {

				// Update the status of the word.
				oSelectionGroup.g_oTextSelections[i].g_oSelectionWords[j].status = g_oTextCompare.eDifferenceTypes.NoDifference;
				oSelectionGroup.g_oTextSelections[i].g_oSelectionWords[j].difference = -1;

				// Update the marker of the word.
				oSelectionGroup.g_oTextSelections[i].g_oSelectionWords[j].marker = g_oTextCompare.eMarkerTypes.NoMarker;
				oSelectionGroup.g_oTextSelections[i].g_oSelectionWords[j].markerDifference = -1;
			}

			// Loop through each of the characters in the selection.
			for (var j = 0; j < oSelectionGroup.g_oTextSelections[i].g_oSelectionCharacters.length; j++) {

				// Update the status of the word.
				oSelectionGroup.g_oTextSelections[i].g_oSelectionCharacters[j].status = g_oTextCompare.eDifferenceTypes.NoDifference;
				oSelectionGroup.g_oTextSelections[i].g_oSelectionCharacters[j].difference = -1;

				// Update the marker of the word.
				oSelectionGroup.g_oTextSelections[i].g_oSelectionCharacters[j].marker = g_oTextCompare.eMarkerTypes.NoMarker;
				oSelectionGroup.g_oTextSelections[i].g_oSelectionCharacters[j].markerDifference = -1;
			}
		}
	};

	/**
	 * Updates the list statuses.
	 * @param {object} oSelectionGroup - The selection group.
	 * @param {bool} bCompare - If this on the compare document.
	 */
	TextCompare.prototype.updateListStatuses = function (oSelectionGroup, bCompare) {

		// If our accuracy is at a word level.
		if (g_oTextCompare.g_iAccuracy == g_oTextCompare.eAccuracyLevels.Word) {
			g_oTextCompare.updateWordListStatuses(oSelectionGroup, bCompare);
		}
		// If our accuracy is at a character level.
		else {
			g_oTextCompare.updateCharacterListStatuses(oSelectionGroup, bCompare);
		}
	};

	/**
	 * Updates the word list statuses.
	 * @param {object} oSelectionGroup - The selection group.
	 * @param {boolean} bCompare - If this is the compare canvas.
	 */
	TextCompare.prototype.updateWordListStatuses = function (oSelectionGroup, bCompare) {

		// Keep track of the difference we are on.
		var iDifference = 1;

		// Get a list of all words and their modifications.
		var oModificationWords = [];

		// Keep track of any words that need to show markers.
		var oMarkerWords = [];

		// Loop through each of the text modifications.
		for (var i = 0; i < oSelectionGroup.g_oTextModifications.length; i++) {

			// Ensure this modification is not specifically mapped to the other canvas.
			if ((!bCompare && oSelectionGroup.g_oTextModifications[i].g_eDifferenceMapping == g_oTextCompare.g_eDifferenceMapping.CompareOnly) ||
				(bCompare && oSelectionGroup.g_oTextModifications[i].g_eDifferenceMapping == g_oTextCompare.g_eDifferenceMapping.ApprovalOnly)) {

				// Skip this modification.
				continue;
			}

			// Ensure this modification is relevant for the compare setting.
			if ((!bCompare && oSelectionGroup.g_oTextModifications[i].g_eDifferenceType == g_oTextCompare.eDifferenceTypes.Deletion) ||
				(bCompare && oSelectionGroup.g_oTextModifications[i].g_eDifferenceType == g_oTextCompare.eDifferenceTypes.Addition)) {

				// If this non-compare addition is not part of a substitution.
				if (bCompare && oSelectionGroup.g_oTextModifications[i].g_eDifferenceType == g_oTextCompare.eDifferenceTypes.Addition && (i == 0 || i > 0 && oSelectionGroup.g_oTextModifications[i - 1].g_eDifferenceType != g_oTextCompare.eDifferenceTypes.Deletion)) {

					// Set that the previous and next words needs to be markers.
					oMarkerWords.push({
						startWord: oModificationWords.length - 1,
						endWord: oModificationWords.length,
						difference: iDifference
					});

					// We need to increment the difference as we skip as this canvas has no text for the difference.
					iDifference++;
				}
				// If this compare deletion is not part of a substitution.
				else if (!bCompare && oSelectionGroup.g_oTextModifications[i].g_eDifferenceType == g_oTextCompare.eDifferenceTypes.Deletion && (i + 1 == oSelectionGroup.g_oTextModifications.length || oSelectionGroup.g_oTextModifications[i + 1].g_eDifferenceType != g_oTextCompare.eDifferenceTypes.Addition)) {

					// Set that the previous and next words needs to be markers.
					oMarkerWords.push({
						startWord: oModificationWords.length - 1,
						endWord: oModificationWords.length,
						difference: iDifference
					});

					// We need to increment the difference as we skip as this canvas has no text for the difference.
					iDifference++;
				}

				// Skip this modification.
				continue;
			}

			// Split the modification into its individual words.
			var oSplitWords = oSelectionGroup.g_oTextModifications[i].g_szModificationText.split(' ');

			// Loop through each of the split words.
			for (var j = 0; j < oSplitWords.length; j++) {

				// Ensure this split word has content.
				if (oSplitWords[j] == "") {
					continue;
				}

				// Add the split word and its modification to the list.
				oModificationWords.push({
					content: oSplitWords[j],
					status: oSelectionGroup.g_oTextModifications[i].g_eDifferenceType,
					difference: oSelectionGroup.g_oTextModifications[i].g_eDifferenceType != g_oTextCompare.eDifferenceTypes.NoDifference ? iDifference : -1,
					marker: g_oTextCompare.eMarkerTypes.NoMarker,
					markerDifference: -1,
					equivalentDifference: (oSelectionGroup.g_oTextModifications[i].g_eDifferenceMapping == g_oTextCompare.g_eDifferenceMapping.ApprovalOnly || oSelectionGroup.g_oTextModifications[i].g_eDifferenceMapping == g_oTextCompare.g_eDifferenceMapping.CompareOnly)
				});
			}

			// Increment the difference count if this is a valid difference.
			if (oSelectionGroup.g_oTextModifications[i].g_eDifferenceType != g_oTextCompare.eDifferenceTypes.NoDifference) {
				iDifference++;
			}
		}

		// Loop through each of the markers.
		for (var i = 0; i < oMarkerWords.length; i++) {

			// If both words exist, or only the first word exists.
			if ((oModificationWords[oMarkerWords[i].startWord] != undefined && oModificationWords[oMarkerWords[i].endWord] != undefined) ||
				(oModificationWords[oMarkerWords[i].startWord] != undefined && oModificationWords[oMarkerWords[i].endWord] == undefined)) {

				// Set that the start word has a marker after.
				oModificationWords[oMarkerWords[i].startWord].marker = g_oTextCompare.eMarkerTypes.MarkerAfter;
				oModificationWords[oMarkerWords[i].startWord].markerDifference = oMarkerWords[i].difference;
			}
			// If only the end word exists.
			else if (oModificationWords[oMarkerWords[i].startWord] == undefined && oModificationWords[oMarkerWords[i].endWord] != undefined) {

				// Set that the end word has a marker before.
				oModificationWords[oMarkerWords[i].endWord].marker = g_oTextCompare.eMarkerTypes.MarkerBefore;
				oModificationWords[oMarkerWords[i].endWord].markerDifference = oMarkerWords[i].difference;
			}
		}

		// Keep track of the current modification word.
		var iModificationWord = 0;

		// Loop through each of the text selections.
		for (var i = 0; i < oSelectionGroup.g_oTextSelections.length; i++) {

			// Loop through each of the words in the selection.
			for (var j = 0; j < oSelectionGroup.g_oTextSelections[i].g_oSelectionWords.length; j++) {

				// If this is a blank word, skip.
				if (oSelectionGroup.g_oTextSelections[i].g_oSelectionWords[j].content == "") {
					continue;
				}

				// Update the status, difference and marker of the word.
				oSelectionGroup.g_oTextSelections[i].g_oSelectionWords[j].status = oModificationWords[iModificationWord].status;
				oSelectionGroup.g_oTextSelections[i].g_oSelectionWords[j].difference = oModificationWords[iModificationWord].difference;
				oSelectionGroup.g_oTextSelections[i].g_oSelectionWords[j].marker = oModificationWords[iModificationWord].marker;
				oSelectionGroup.g_oTextSelections[i].g_oSelectionWords[j].markerDifference = oModificationWords[iModificationWord].markerDifference;
				oSelectionGroup.g_oTextSelections[i].g_oSelectionWords[j].equivalentDifference = oModificationWords[iModificationWord].equivalentDifference;

				// Increment the modification word.
				iModificationWord++;
			}
		}
	};

	/**
	 * Update the character list statuses.
	 * @param {object} oSelectionGroup - The selection group.
	 * @param {boolean} bCompare - If this is the compare canvas.
	 */
	TextCompare.prototype.updateCharacterListStatuses = function (oSelectionGroup, bCompare) {

		// Keep track of the difference we are on.
		var iDifference = 1;

		// Get a list of all characters and their modifications.
		var oModificationCharacters = [];

		// Keep track of any characters that need to show markers.
		var oMarkerCharacters = [];

		// Loop through each of the text modifications.
		for (var i = 0; i < oSelectionGroup.g_oTextModifications.length; i++) {

			// Ensure this modification is not specifically mapped to the other canvas.
			if ((!bCompare && oSelectionGroup.g_oTextModifications[i].g_eDifferenceMapping == g_oTextCompare.g_eDifferenceMapping.CompareOnly) ||
				(bCompare && oSelectionGroup.g_oTextModifications[i].g_eDifferenceMapping == g_oTextCompare.g_eDifferenceMapping.ApprovalOnly)) {

				// Skip this modification.
				continue;
			}

			// Ensure this modification is relevant for the compare setting.
			if ((!bCompare && oSelectionGroup.g_oTextModifications[i].g_eDifferenceType == g_oTextCompare.eDifferenceTypes.Deletion) ||
				(bCompare && oSelectionGroup.g_oTextModifications[i].g_eDifferenceType == g_oTextCompare.eDifferenceTypes.Addition)) {

				// If this non-compare addition is not part of a substitution.
				if (bCompare && oSelectionGroup.g_oTextModifications[i].g_eDifferenceType == g_oTextCompare.eDifferenceTypes.Addition && (i == 0 || i > 0 && oSelectionGroup.g_oTextModifications[i - 1].g_eDifferenceType != g_oTextCompare.eDifferenceTypes.Deletion)) {

					// Set that the previous and next characters needs to be markers.
					oMarkerCharacters.push({
						startCharacter: oModificationCharacters.length - 1,
						endCharacter: oModificationCharacters.length,
						difference: iDifference
					});

					// We need to increment the difference as we skip as this canvas has no text for the difference.
					iDifference++;
				}
				// If this compare deletion is not part of a substitution.
				else if (!bCompare && oSelectionGroup.g_oTextModifications[i].g_eDifferenceType == g_oTextCompare.eDifferenceTypes.Deletion && (i + 1 == oSelectionGroup.g_oTextModifications.length || oSelectionGroup.g_oTextModifications[i + 1].g_eDifferenceType != g_oTextCompare.eDifferenceTypes.Addition)) {

					// Set that the previous and next characters needs to be markers.
					oMarkerCharacters.push({
						startCharacter: oModificationCharacters.length - 1,
						endCharacter: oModificationCharacters.length,
						difference: iDifference
					});

					// We need to increment the difference as we skip as this canvas has no text for the difference.
					iDifference++;
				}

				// Skip this modification.
				continue;
			}

			// Split the modification into its individual characters.
			var oSplitCharacters = oSelectionGroup.g_oTextModifications[i].g_szModificationText;

			// Loop through each of the split characters.
			for (var j = 0; j < oSplitCharacters.length; j++) {

				// Ensure this split character has content.
				if (oSplitCharacters[j] == " ") {
					continue;
				}

				// Add the split character and its modification to the list.
				oModificationCharacters.push({
					content: oSplitCharacters[j],
					status: oSelectionGroup.g_oTextModifications[i].g_eDifferenceType,
					difference: oSelectionGroup.g_oTextModifications[i].g_eDifferenceType != g_oTextCompare.eDifferenceTypes.NoDifference ? iDifference : -1,
					marker: g_oTextCompare.eMarkerTypes.NoMarker,
					markerDifference: -1,
					equivalentDifference: (oSelectionGroup.g_oTextModifications[i].g_eDifferenceMapping == g_oTextCompare.g_eDifferenceMapping.ApprovalOnly || oSelectionGroup.g_oTextModifications[i].g_eDifferenceMapping == g_oTextCompare.g_eDifferenceMapping.CompareOnly)
				});
			}

			// Increment the difference count if this is a valid difference.
			if (oSelectionGroup.g_oTextModifications[i].g_eDifferenceType != g_oTextCompare.eDifferenceTypes.NoDifference) {
				iDifference++;
			}
		}

		// Loop through each of the markers.
		for (var i = 0; i < oMarkerCharacters.length; i++) {

			// If both characters exist, or only the first character exists.
			if ((oModificationCharacters[oMarkerCharacters[i].startCharacter] != undefined && oModificationCharacters[oMarkerCharacters[i].endCharacter] != undefined) ||
				(oModificationCharacters[oMarkerCharacters[i].startCharacter] != undefined && oModificationCharacters[oMarkerCharacters[i].endCharacter] == undefined)) {

				// Set that the start character has a marker after.
				oModificationCharacters[oMarkerCharacters[i].startCharacter].marker = g_oTextCompare.eMarkerTypes.MarkerAfter;
				oModificationCharacters[oMarkerCharacters[i].startCharacter].markerDifference = oMarkerCharacters[i].difference;
			}
			// If only the end character exists.
			else if (oModificationCharacters[oMarkerCharacters[i].startCharacter] == undefined && oModificationCharacters[oMarkerCharacters[i].endCharacter] != undefined) {

				// Set that the end character has a marker before.
				oModificationCharacters[oMarkerCharacters[i].endCharacter].marker = g_oTextCompare.eMarkerTypes.MarkerBefore;
				oModificationCharacters[oMarkerCharacters[i].endCharacter].markerDifference = oMarkerCharacters[i].difference;
			}
		}

		// Keep track of the current modification character.
		var iModificationCharacter = 0;

		// Loop through each of the text selections.
		for (var i = 0; i < oSelectionGroup.g_oTextSelections.length; i++) {

			// Loop through each of the text selection characters.
			for (var j = 0; j < oSelectionGroup.g_oTextSelections[i].g_oSelectionCharacters.length; j++) {

				// If we have run out of modification words.
				if (oModificationCharacters[iModificationCharacter] == undefined) {
					oSelectionGroup.g_oTextSelections[i].g_oSelectionCharacters[j].status = 0;
					oSelectionGroup.g_oTextSelections[i].g_oSelectionCharacters[j].difference = -1;
					continue;
				}

				// Update the status and difference of the word.
				oSelectionGroup.g_oTextSelections[i].g_oSelectionCharacters[j].status = oModificationCharacters[iModificationCharacter].status;
				oSelectionGroup.g_oTextSelections[i].g_oSelectionCharacters[j].difference = oModificationCharacters[iModificationCharacter].difference;
				oSelectionGroup.g_oTextSelections[i].g_oSelectionCharacters[j].marker = oModificationCharacters[iModificationCharacter].marker;
				oSelectionGroup.g_oTextSelections[i].g_oSelectionCharacters[j].markerDifference = oModificationCharacters[iModificationCharacter].markerDifference;
				oSelectionGroup.g_oTextSelections[i].g_oSelectionCharacters[j].equivalentDifference = oModificationCharacters[iModificationCharacter].equivalentDifference;

				// Increment the modification word.
				iModificationCharacter++;
			}
		}
	};

	/**
	 * Calculates the text comparison.
	 * @param {string} text1 - The first text.
	 * @param {string} text2 - The second text.
	 */
	TextCompare.prototype.calculateTextComparison = function (text1, text2) {

		// Outline the differences to be returned.
		var oDifferences = [];

		// If we are calculating differences to a word level.
		if (g_oTextCompare.g_iAccuracy == g_oTextCompare.eAccuracyLevels.Word) {
			oDifferences = g_oTextCompare.calculateTextComparisonWords(text1, text2);
		}
		// If we are calculating differences to a character level.
		else {
			oDifferences = g_oTextCompare.calculateTextComparisonCharacters(text1, text2);
		}

		// Return the differences.
		return oDifferences;
	};

	/**
	 * Calculates the text comparison words.
	 * @param {string} text1 - The first text.
	 * @param {string} text2 - The second text.
	 */
	TextCompare.prototype.calculateTextComparisonWords = function (text1, text2) {

		// Initialise the diff match patch.
		var oDiffMatchPath = new diff_match_patch();

		// Convert the lines to words and extract the text.
		var oLinesToWords = oDiffMatchPath.diff_linesToWords_(text1, text2);
		var oLineText1 = oLinesToWords.chars1;
		var oLineText2 = oLinesToWords.chars2;
		var oLineArray = oLinesToWords.lineArray;

		// Calculate the differences.
		var oDifferences = oDiffMatchPath.diff_main(oLineText1, oLineText2, false);

		// Convert the difference characters to lines.
		oDiffMatchPath.diff_charsToLines_(oDifferences, oLineArray);

		// Cleanup semantic.
		oDiffMatchPath.diff_cleanupSemantic(oDifferences);

		// Cleanup blanks.
		oDiffMatchPath.diff_cleanupBlankValues(oDifferences);

		// Return the differences.
		return oDifferences;
	};

	/**
	 * Calculates the text comparison characters.
	 * @param {string} text1 - The first text.
	 * @param {string} text2 - The second text.
	 */
	TextCompare.prototype.calculateTextComparisonCharacters = function (text1, text2) {

		// Initialise the diff match patch.
		var oDiffMatchPatch = new diff_match_patch();

		// Calculate the differences.
		var oDifferences = oDiffMatchPatch.diff_main(text1, text2);

		// Cleanup semantic.
		oDiffMatchPatch.diff_cleanupSemantic(oDifferences);

		// Cleanup character blanks.
		oDiffMatchPatch.diff_cleanupBlankValuesCharacters(oDifferences);

		// Return the differences.
		return oDifferences;
	};

	/**
	 * Converts text comparisons to modifications.
	 * @param {object} oComparisons - The comparisons.
	 */
	TextCompare.prototype.convertTextComparisonsToModifications = function (oComparisons) {

		// Outline the modifications to be returned.
		var oModifications = [];

		// Loop through each of the comparisons and convert these to modifications.
		for (var i = 0; i < oComparisons.length; i++) {
			oModifications.push(new g_oTextCompare.textModification(oComparisons[i]));
		}

		// Return the modifications list.
		return oModifications;
	};

	/**
	 * Converts text modifications to differences.
	 * @param {object} oModifications - The modifications.
	 */
	TextCompare.prototype.convertTextModificationsToDifferences = function (oModifications) {

		// Keep track of the difference order.
		var iDifferenceOrder = 0;

		// Outline the differences to be returned.
		var oDifferences = [];

		// Loop through each of the modifications.
		for (var i = 0; i < oModifications.length; i++) {

			// If this is a no difference section, skip.
			if (oModifications[i].g_eDifferenceType == g_oTextCompare.eDifferenceTypes.NoDifference) {
				continue;
			}

			// Increment the difference number.
			g_oTextCompare.g_iNumberOfDifferences++;

			// Increment the difference order.
			iDifferenceOrder++;

			// If this is a deletion followed by an addition, this is a substitution.
			if (oModifications[i].g_eDifferenceType == g_oTextCompare.eDifferenceTypes.Deletion && (i + 1) < oModifications.length && oModifications[i + 1].g_eDifferenceType == g_oTextCompare.eDifferenceTypes.Addition) {

				// Add the difference as a substitution.
				oDifferences.push(new g_oTextCompare.textDifference([oModifications[i].g_szModificationText, oModifications[i + 1].g_szModificationText], g_oTextCompare.eDifferenceTypes.Substitution, g_oTextCompare.g_iNumberOfDifferences, iDifferenceOrder));

				// Skip the next modification as this is included in the substitution.
				i++;
			}
			// If this is a deletion.
			else if (oModifications[i].g_eDifferenceType == g_oTextCompare.eDifferenceTypes.Deletion) {

				// Add the difference as a deletion.
				oDifferences.push(new g_oTextCompare.textDifference([oModifications[i].g_szModificationText], g_oTextCompare.eDifferenceTypes.Deletion, g_oTextCompare.g_iNumberOfDifferences, iDifferenceOrder));
			}
			// If this is an addition.
			else if (oModifications[i].g_eDifferenceType == g_oTextCompare.eDifferenceTypes.Addition) {

				// Add the difference as an addition.
				oDifferences.push(new g_oTextCompare.textDifference([oModifications[i].g_szModificationText], g_oTextCompare.eDifferenceTypes.Addition, g_oTextCompare.g_iNumberOfDifferences, iDifferenceOrder));
			}
		}

		// Return the differences.
		return oDifferences;
	};

	/**
	 * Combines the text selections.
	 * @param {object} oTextSelections - The text selections.
	 */
	TextCompare.prototype.combineTextSelections = function (oTextSelections) {

		// Keep track of the combined text.
		var szCombinedText = "";

		// Loop through and each of the text selections.
		for (var i = 0; i < oTextSelections.length; i++) {

			// If this is not the first selection text, add a space.
			if (i > 0) {
				szCombinedText += " ";
			}

			// Add the selection text.
			szCombinedText += g_oTextCompare.g_iAccuracy == g_oTextCompare.eAccuracyLevels.Word ? oTextSelections[i].g_oSelectionWordsText : oTextSelections[i].g_oSelectionCharactersText;
		}

		// Return the combined text.
		return szCombinedText;
	};

	/**
	 * Gets the text selection words.
	 * @param {object} oTextData - The text data.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextCompare.prototype.getTextSelectionWords = function (oTextData, oArtworkCanvas, oAnnotationCanvas) {

		// Get the selection words.
		var oWords = g_oTextTools.getSectionsWords(oTextData, oArtworkCanvas, oAnnotationCanvas);

		// Sort the words.
		oWords = oWords.sort(g_oTextTools.sortWords);

		// Remove the duplicates.
		oWords = g_oTextTools.distinctWords(oWords);

		// Convert these to selection words.
		var oSelectionWords = [];
		for (var i = 0; i < oWords.length; i++) {
			oSelectionWords.push(new g_oTextCompare.textSelectionWord(oWords[i], oArtworkCanvas, oAnnotationCanvas));
		}

		// Return the list of selection words.
		return oSelectionWords;
	};

	/**
	 * Gets the text selection characters.
	 * @param {object} oTextData - The text data.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextCompare.prototype.getTextSelectionCharacters = function (oTextData, oArtworkCanvas, oAnnotationCanvas) {

		// Get the selection characters.
		var oCharacters = g_oTextTools.getSectionsCharacters(oTextData, oArtworkCanvas, oAnnotationCanvas);

		// Sort the characters.
		oCharacters = oCharacters.sort(g_oTextTools.sortCharacters);

		// Remove the duplicates.
		oCharacters = g_oTextTools.distinctCharacters(oCharacters);

		// Convert these to selection characters.
		var oSelectionCharacters = [];
		for (var i = 0; i < oCharacters.length; i++) {
			oSelectionCharacters.push(new g_oTextCompare.textSelectionCharacter(oCharacters[i], oArtworkCanvas, oAnnotationCanvas));
		}

		// Return the list of selection characters.
		return oSelectionCharacters;
	};

	/**
	 * Updates the difference status.
	 * @param {int} iDifferenceNumber - The difference number.
	 */
	TextCompare.prototype.updateDifferenceStatus = function (iDifferenceNumber) {

		// Get the new value for the difference.
		var eDifferenceStatus = parseInt($("#selDifferenceStatus_" + iDifferenceNumber).val());

		// Update the difference status for both canvas.
		g_oTextCompare.updateDifferenceStatusValue(iDifferenceNumber, eDifferenceStatus, false);
		g_oTextCompare.updateDifferenceStatusValue(iDifferenceNumber, eDifferenceStatus, true);

		// Render the text compare annotations.
		g_oTextCompare.renderTextCompareAnnotations();
	};

	/**
	 * Updates the step difference status.
	 */
	TextCompare.prototype.updateStepDifferenceStatus = function () {

		// Get the new value for the difference.
		var eDifferenceStatus = parseInt($("#selTextDifferenceStatus").val());

		// Get the difference number from the step through.
		var iDifferenceNumber = g_oTextCompare.g_iStepThroughDifference;

		// Update the difference status for both canvas.
		g_oTextCompare.updateDifferenceStatusValue(iDifferenceNumber, eDifferenceStatus, false);
		g_oTextCompare.updateDifferenceStatusValue(iDifferenceNumber, eDifferenceStatus, true);

		// Render the text compare annotations.
		g_oTextCompare.renderTextCompareAnnotations();
	};

	/**
	 * Updates the difference status value.
	 * @param {int} iDifferenceNumber - The difference number.
	 * @param {int} eDifferenceStatus - The difference status.
	 * @param {boolean} bCompare - If this is the compare canvas.
	 */
	TextCompare.prototype.updateDifferenceStatusValue = function (iDifferenceNumber, eDifferenceStatus, bCompare) {

		// Get the text selections.
		var oTextSelections = g_oTextCompare.getTextSelectionGroup(bCompare);

		// Loop through each of the text selections.
		for (var i = 0; i < oTextSelections.length; i++) {

			// Loop through each of the differences.
			for (var j = 0; j < oTextSelections[i].g_oTextDifferences.length; j++) {

				// If this is the difference to update.
				if (oTextSelections[i].g_oTextDifferences[j].g_iDifferenceNumber == iDifferenceNumber) {

					// Update the status and stop.
					oTextSelections[i].g_oTextDifferences[j].g_iDifferenceStatus = eDifferenceStatus;
					return;
				}
			}
		}
	};

	/**
	 * Generates the formatted text difference.
	 * @param {object} oApprovalSectionGroup - The approval selection group.
	 * @param {object} oCompareSectionGroup - The compare selection group.
	 * @param {object} oDifferences - The differences.
	 */
	TextCompare.prototype.generateFormattedDifferenceText = function (oApprovalSectionGroup, oCompareSectionGroup, oDifferences) {

		// Loop through each of the differences.
		for (var i = 0; i < oDifferences.length; i++) {

			// Generate the formatted difference text based on the difference type.
			switch (oDifferences[i].g_eDifferenceType) {
				case g_oTextCompare.eDifferenceTypes.Addition:
					oDifferences[i].g_oFormattedDifferenceText = [g_oTextCompare.getFormattedTextForDifference(oDifferences[i].g_iDifferenceOrder, oApprovalSectionGroup)];
					break;
				case g_oTextCompare.eDifferenceTypes.Deletion:
					oDifferences[i].g_oFormattedDifferenceText = [g_oTextCompare.getFormattedTextForDifference(oDifferences[i].g_iDifferenceOrder, oCompareSectionGroup)];
					break;
				case g_oTextCompare.eDifferenceTypes.Substitution:
				case g_oTextCompare.eDifferenceTypes.WeightSubstitution:
				case g_oTextCompare.eDifferenceTypes.StyleSubstitution:
				case g_oTextCompare.eDifferenceTypes.WeightStyleSubstitution:
					oDifferences[i].g_oFormattedDifferenceText = [g_oTextCompare.getFormattedTextForDifference(oDifferences[i].g_iDifferenceOrder, oCompareSectionGroup), g_oTextCompare.getFormattedTextForDifference(oDifferences[i].g_iDifferenceOrder, oApprovalSectionGroup)];
					break;
			}

			// Ensure we have been able to extract the formatted difference text.
			if (oDifferences[i].g_oFormattedDifferenceText[0] == "") {
				oDifferences[i].g_oFormattedDifferenceText[0] = oDifferences[i].g_oDifferenceText[0];
			}

			// Ensure we have been able to extract the formatted substitution text if required.
			if (oDifferences[i].g_oFormattedDifferenceText[1] == "") {
				oDifferences[i].g_oFormattedDifferenceText[1] = oDifferences[i].g_oDifferenceText[1];
			}
		}
	};

	/**
	 * Gets the formatted text for a difference.
	 * @param {int} iDifference - The difference.
	 * @param {object} oSectionGroup - The selection group.
	 */
	TextCompare.prototype.getFormattedTextForDifference = function (iDifference, oSectionGroup) {

		// Get the text for the difference based on the accuracy.
		return g_oTextCompare.g_iAccuracy == g_oTextCompare.eAccuracyLevels.Word ? g_oTextCompare.getFormattedTextForDifference_Words(iDifference, oSectionGroup) : g_oTextCompare.getFormattedTextForDifference_Characters(iDifference, oSectionGroup);
	};

	/**
	 * Gets the formatted text for a difference at word level.
	 * @param {int} iDifference - The difference.
	 * @param {object} oSectionGroup - The selection group.
	 */
	TextCompare.prototype.getFormattedTextForDifference_Words = function (iDifference, oSectionGroup) {

		// Outline the difference text to be returned.
		var szDifferenceText = "";

		// Loop through each of the words in the selection.
		for (var i = 0; i < oSectionGroup.g_oTextSelections.length; i++) {
			for (var j = 0; j < oSectionGroup.g_oTextSelections[i].g_oSelectionWords.length; j++) {

				// Extract the word.
				var oWord = oSectionGroup.g_oTextSelections[i].g_oSelectionWords[j];

				// If this word is part of the difference.
				if (oWord.difference == iDifference) {

					// Add a space if required.
					if (szDifferenceText.length > 0) {
						szDifferenceText += " ";
					}

					// Outline the text content to be added.
					var szWordContent = oWord.content;

					// If we are not showing case sensitivity.
					if (g_oTextCompare.g_iCaseSensitivity == g_oTextCompare.eCaseSensitivityLevels.Off) {
						szWordContent = szWordContent.toLowerCase();
					}

					// If we are showing bold italic differences, wrap with the appropriate styling.
					if (g_oTextCompare.g_iBoldItalicDetection == g_oTextCompare.eBoldItalicDetectionLevels.On) {
						if (oWord.bold) {
							szWordContent = "<b>" + szWordContent + "</b>";
						}
						if (oWord.italic) {
							szWordContent = "<i>" + szWordContent + "</i>";
						}
					}

					// Add the word in the correct styling.
					szDifferenceText += szWordContent;
				}
			}
		}

		// Return the difference text.
		return szDifferenceText;
	};

	/**
	 * Gets the formatted text for a difference at character level.
	 * @param {int} iDifference - The difference.
	 * @param {object} oSectionGroup - The selection group.
	 */
	TextCompare.prototype.getFormattedTextForDifference_Characters = function (iDifference, oSectionGroup) {

		// Outline the difference text to be returned.
		var szDifferenceText = "";

		// Keep track of if the previous character was the last in the word.
		var bPreviousCharacterLastInWord = false;

		// Loop through each of the characters in the selection.
		for (var i = 0; i < oSectionGroup.g_oTextSelections.length; i++) {
			for (var j = 0; j < oSectionGroup.g_oTextSelections[i].g_oSelectionCharacters.length; j++) {

				// Extract the character.
				var oCharacter = oSectionGroup.g_oTextSelections[i].g_oSelectionCharacters[j];

				// If this character is part of the difference.
				if (oCharacter.difference == iDifference) {

					// Add a space if required.
					if (bPreviousCharacterLastInWord) {
						szDifferenceText += " ";
					}

					// Outline the text content to be added.
					var szCharacterContent = oCharacter.content;

					// If we are not showing case sensitivity.
					if (g_oTextCompare.g_iCaseSensitivity == g_oTextCompare.eCaseSensitivityLevels.Off) {
						szCharacterContent = szCharacterContent.toLowerCase();
					}

					// If we are showing bold italic differences, wrap with the appropriate styling.
					if (g_oTextCompare.g_iBoldItalicDetection == g_oTextCompare.eBoldItalicDetectionLevels.On) {
						if (oCharacter.bold) {
							szCharacterContent = "<b>" + szCharacterContent + "</b>";
						}
						if (oCharacter.italic) {
							szCharacterContent = "<i>" + szCharacterContent + "</i>";
						}
					}

					// Add the character in the correct styling.
					szDifferenceText += szCharacterContent;

					// Determine if this character is the last in the word.
					bPreviousCharacterLastInWord = oCharacter.lastInWord;
				}
			}
		}

		// Return the difference text.
		return szDifferenceText;
	};

	/**
	 * Calculates the bold and italic differences.
	 * @param {object} oApprovalSelections - The approval selections.
	 * @param {object} oCompareSelections - The compare selections.
	 * @param {object} oDifferences - The differences.
	 */
	TextCompare.prototype.calculateBoldItalicDifferences = function (oApprovalSelections, oCompareSelections, oDifferences) {

		// If our accuracy is at a word level.
		if (g_oTextCompare.g_iAccuracy == g_oTextCompare.eAccuracyLevels.Word) {
			g_oTextCompare.calculateWordBoldItalicDifferences(oApprovalSelections, oCompareSelections, oDifferences);
		}
		// If our accuracy is at a character level.
		else {
			g_oTextCompare.calculateCharacterBoldItalicDifferences(oApprovalSelections, oCompareSelections, oDifferences);
		}
	};

	/**
	 * Calculates the word bold and italic differences.
	 * @param {object} oApprovalSelections - The approval selections.
	 * @param {object} oCompareSelections - The compare selections.
	 * @param {object} oDifferences - The differences.
	 */
	TextCompare.prototype.calculateWordBoldItalicDifferences = function (oApprovalSelections, oCompareSelections, oDifferences) {

		// Keep track of the styling changes.
		var oStylingChanges = [];

		// Get the words with no differences for each selection.
		var oApprovalNoDifferenceWords = g_oTextCompare.getWordsWithNoDifferences(oApprovalSelections);
		var oCompareNoDifferenceWords = g_oTextCompare.getWordsWithNoDifferences(oCompareSelections);

		// Loop through each of the words that have no differences.
		for (var i = 0; i < oApprovalNoDifferenceWords.length; i++) {

			// Ensure we have words with no differences on both sides.
			if (oApprovalNoDifferenceWords[i] == undefined || oCompareNoDifferenceWords[i] == undefined) {
				continue;
			}

			// If this word has a difference in styling.
			if (oApprovalNoDifferenceWords[i].bold != oCompareNoDifferenceWords[i].bold || oApprovalNoDifferenceWords[i].italic != oCompareNoDifferenceWords[i].italic) {

				// Determine the substitution type.
				var eSubstitutionType = g_oTextCompare.determineBoldItalicSubstitutionType(oApprovalNoDifferenceWords[i].bold != oCompareNoDifferenceWords[i].bold, oApprovalNoDifferenceWords[i].italic != oCompareNoDifferenceWords[i].italic);

				// If this styling change can be grouped with the previous styling change.
				// - We have at least one previous styling change.
				// - The substitution type is the same.
				// - The approval word follows directly after the previous approval word.
				// - The compare word follows directly after the previous compare word.
				if (oStylingChanges.length > 0 && oStylingChanges[oStylingChanges.length - 1].type == eSubstitutionType &&
					oStylingChanges[oStylingChanges.length - 1].approvalWords[oStylingChanges[oStylingChanges.length - 1].approvalWords.length - 1].order + 1 == oApprovalNoDifferenceWords[i].words[0].order &&
					oStylingChanges[oStylingChanges.length - 1].compareWords[oStylingChanges[oStylingChanges.length - 1].compareWords.length - 1].order + 1 == oCompareNoDifferenceWords[i].words[0].order) {

					// Add the approval words to the previous styling change.
					for (var j = 0; j < oApprovalNoDifferenceWords[i].words.length; j++) {
						oStylingChanges[oStylingChanges.length - 1].approvalWords.push(oApprovalNoDifferenceWords[i].words[j]);
					}

					// Add the compare words to the previous styling change.
					for (var j = 0; j < oCompareNoDifferenceWords[i].words.length; j++) {
						oStylingChanges[oStylingChanges.length - 1].compareWords.push(oCompareNoDifferenceWords[i].words[j]);
					}

					// Continue now we have grouped with the previous styling change.
					continue;
				}

				// Create the styling change.
				oStylingChanges.push({
					approvalWords: oApprovalNoDifferenceWords[i].words,
					compareWords: oCompareNoDifferenceWords[i].words,
					type: eSubstitutionType,
					previousDifference: oApprovalNoDifferenceWords[i].previousDifference
				});
			}
		}

		// Loop through each of the styling changes.
		for (var i = 0; i < oStylingChanges.length; i++) {

			// Get the difference order and number to be used for the styling difference.
			// The order is the previous difference, plus one as we want to be after this difference, plus the number of differences we have already inserted.
			var iDifferenceOrder = oStylingChanges[i].previousDifference + 1 + i;
			var iDifferenceNumber = g_oTextCompare.getDifferenceNumberFromOrder(iDifferenceOrder, oDifferences);

			// Create a gap for the new styling difference.
			g_oTextCompare.createGapForDifference(iDifferenceNumber, iDifferenceOrder, oDifferences);
			g_oTextCompare.createWordGapForDifference(iDifferenceNumber, iDifferenceOrder, oApprovalSelections);
			g_oTextCompare.createWordGapForDifference(iDifferenceNumber, iDifferenceOrder, oCompareSelections);

			// Build the content for the styling change.
			var oApprovalStylingChangeContent = "";
			var oCompareStylingChangeContent = "";

			// Loop through each of the approval words for the styling change.
			for (var j = 0; j < oStylingChanges[i].approvalWords.length; j++) {

				// Extract the approval word.
				var oApprovalNoDifferenceWord = oApprovalSelections[oStylingChanges[i].approvalWords[j].selection].g_oSelectionWords[oStylingChanges[i].approvalWords[j].word];

				// Add the word content.
				oApprovalStylingChangeContent += oApprovalNoDifferenceWord.content;

				// Update the difference and status of the word.
				oApprovalNoDifferenceWord.difference = iDifferenceOrder;
				oApprovalNoDifferenceWord.status = g_oTextCompare.eDifferenceTypes.Addition;

				// Add a space if required.
				if (j + 1 < oStylingChanges[i].approvalWords.length) {
					oApprovalStylingChangeContent += " ";
				}
			}

			// Loop through each of the compare words for the styling change.
			for (var j = 0; j < oStylingChanges[i].compareWords.length; j++) {

				// Extract the compare word.
				var oCompareNoDifferenceWord = oCompareSelections[oStylingChanges[i].compareWords[j].selection].g_oSelectionWords[oStylingChanges[i].compareWords[j].word];

				// Add the word content.
				oCompareStylingChangeContent += oCompareNoDifferenceWord.content;

				// Update the difference and status of the word.
				oCompareNoDifferenceWord.difference = iDifferenceOrder;
				oCompareNoDifferenceWord.status = g_oTextCompare.eDifferenceTypes.Deletion;

				// Add a space if required.
				if (j + 1 < oStylingChanges[i].compareWords.length) {
					oCompareStylingChangeContent += " ";
				}
			}

			// Add the styling difference in the correct spot.
			oDifferences.splice(iDifferenceOrder - 1, 0, new g_oTextCompare.textDifference([oCompareStylingChangeContent, oApprovalStylingChangeContent], oStylingChanges[i].type, iDifferenceNumber, iDifferenceOrder));

			// Increment the number of differences.
			g_oTextCompare.g_iNumberOfDifferences++;
		}
	};

	/**
	 * Calculates the character bold and italic differences.
	 * @param {object} oApprovalSelections - The approval selections.
	 * @param {object} oCompareSelections - The compare selections.
	 * @param {object} oDifferences - The differences.
	 */
	TextCompare.prototype.calculateCharacterBoldItalicDifferences = function (oApprovalSelections, oCompareSelections, oDifferences) {

		// Keep track of the styling changes.
		var oStylingChanges = [];

		// Get the characters with no differences for each selection.
		var oApprovalNoDifferenceCharacters = g_oTextCompare.getCharactersWithNoDifferences(oApprovalSelections);
		var oCompareNoDifferenceCharacters = g_oTextCompare.getCharactersWithNoDifferences(oCompareSelections);

		// Loop through each of the characters that have no differences.
		for (var i = 0; i < oApprovalNoDifferenceCharacters.length; i++) {

			// Ensure we have characters with no differences on both sides.
			if (oApprovalNoDifferenceCharacters[i] == undefined || oCompareNoDifferenceCharacters[i] == undefined) {
				continue;
			}

			// If this character has a difference in styling.
			if (oApprovalNoDifferenceCharacters[i].bold != oCompareNoDifferenceCharacters[i].bold || oApprovalNoDifferenceCharacters[i].italic != oCompareNoDifferenceCharacters[i].italic) {

				// Determine the substitution type.
				var eSubstitutionType = g_oTextCompare.determineBoldItalicSubstitutionType(oApprovalNoDifferenceCharacters[i].bold != oCompareNoDifferenceCharacters[i].bold, oApprovalNoDifferenceCharacters[i].italic != oCompareNoDifferenceCharacters[i].italic);

				// If this styling change can be grouped with the previous styling change.
				// - We have at least one previous styling change.
				// - The substitution type is the same.
				// - The approval character follows directly after the previous approval character.
				// - The compare character follows directly after the previous compare character.
				if (oStylingChanges.length > 0 && oStylingChanges[oStylingChanges.length - 1].type == eSubstitutionType &&
					oStylingChanges[oStylingChanges.length - 1].approvalCharacters[oStylingChanges[oStylingChanges.length - 1].approvalCharacters.length - 1].order + 1 == oApprovalNoDifferenceCharacters[i].characters[0].order &&
					oStylingChanges[oStylingChanges.length - 1].compareCharacters[oStylingChanges[oStylingChanges.length - 1].compareCharacters.length - 1].order + 1 == oCompareNoDifferenceCharacters[i].characters[0].order) {

					// Add the approval characters to the previous styling change.
					for (var j = 0; j < oApprovalNoDifferenceCharacters[i].characters.length; j++) {
						oStylingChanges[oStylingChanges.length - 1].approvalCharacters.push(oApprovalNoDifferenceCharacters[i].characters[j]);
					}

					// Add the compare characters to the previous styling change.
					for (var j = 0; j < oCompareNoDifferenceCharacters[i].characters.length; j++) {
						oStylingChanges[oStylingChanges.length - 1].compareCharacters.push(oCompareNoDifferenceCharacters[i].characters[j]);
					}

					// Continue now we have grouped with the previous styling change.
					continue;
				}

				// Create the styling change.
				oStylingChanges.push({
					approvalCharacters: oApprovalNoDifferenceCharacters[i].characters,
					compareCharacters: oCompareNoDifferenceCharacters[i].characters,
					type: eSubstitutionType,
					previousDifference: oApprovalNoDifferenceCharacters[i].previousDifference
				});
			}
		}

		// Loop through each of the styling changes.
		for (var i = 0; i < oStylingChanges.length; i++) {

			// Get the difference order and number to be used for the styling difference.
			// The order is the previous difference, plus one as we want to be after this difference, plus the number of differences we have already inserted.
			var iDifferenceOrder = oStylingChanges[i].previousDifference + 1 + i;
			var iDifferenceNumber = g_oTextCompare.getDifferenceNumberFromOrder(iDifferenceOrder, oDifferences);

			// Create a gap for the new styling difference.
			g_oTextCompare.createGapForDifference(iDifferenceNumber, iDifferenceOrder, oDifferences);
			g_oTextCompare.createCharacterGapForDifference(iDifferenceNumber, iDifferenceOrder, oApprovalSelections);
			g_oTextCompare.createCharacterGapForDifference(iDifferenceNumber, iDifferenceOrder, oCompareSelections);

			// Build the content for the styling change.
			var oApprovalStylingChangeContent = "";
			var oCompareStylingChangeContent = "";

			// Loop through each of the approval characters for the styling change.
			for (var j = 0; j < oStylingChanges[i].approvalCharacters.length; j++) {

				// Extract the approval character.
				var oApprovalNoDifferenceCharacter = oApprovalSelections[oStylingChanges[i].approvalCharacters[j].selection].g_oSelectionCharacters[oStylingChanges[i].approvalCharacters[j].character];

				// Add the character content.
				oApprovalStylingChangeContent += oApprovalNoDifferenceCharacter.content;

				// Update the difference and status of the character.
				oApprovalNoDifferenceCharacter.difference = iDifferenceOrder;
				oApprovalNoDifferenceCharacter.status = g_oTextCompare.eDifferenceTypes.Addition;

				// Add a space if required.
				if (oStylingChanges[i].approvalCharacters[j].lastCharacterInWord && j + 1 < oStylingChanges[i].approvalCharacters.length) {
					oApprovalStylingChangeContent += " ";
				}
			}

			// Loop through each of the compare characters for the styling change.
			for (var j = 0; j < oStylingChanges[i].compareCharacters.length; j++) {

				// Extract the compare character.
				var oCompareNoDifferenceCharacter = oCompareSelections[oStylingChanges[i].compareCharacters[j].selection].g_oSelectionCharacters[oStylingChanges[i].compareCharacters[j].character];

				// Add the character content.
				oCompareStylingChangeContent += oCompareNoDifferenceCharacter.content;

				// Update the difference and status of the character.
				oCompareNoDifferenceCharacter.difference = iDifferenceOrder;
				oCompareNoDifferenceCharacter.status = g_oTextCompare.eDifferenceTypes.Deletion;

				// Add a space if required.
				if (oStylingChanges[i].compareCharacters[j].lastCharacterInWord && j + 1 < oStylingChanges[i].compareCharacters.length) {
					oCompareStylingChangeContent += " ";
				}
			}

			// Add the styling difference in the correct spot.
			oDifferences.splice(iDifferenceOrder - 1, 0, new g_oTextCompare.textDifference([oCompareStylingChangeContent, oApprovalStylingChangeContent], oStylingChanges[i].type, iDifferenceNumber, iDifferenceOrder));

			// Increment the number of differences.
			g_oTextCompare.g_iNumberOfDifferences++;
		}
	};

	/**
	 * Gets a list of words with no differences.
	 * @param {object} oSelectionGroups - The selections.
	 */
	TextCompare.prototype.getWordsWithNoDifferences = function (oSelectionGroups) {

		// Outline the no difference text to be returned.
		var oNoDifferenceWords = [];

		// Keep track of the word we are on and the previous difference.
		var iCurrentWord = 0;
		var iPreviousDifference = 0;

		// Loop through each of the text selections.
		for (var i = 0; i < oSelectionGroups.length; i++) {

			// Loop through each of the words in the selection.
			for (var j = 0; j < oSelectionGroups[i].g_oSelectionWords.length; j++) {

				// If this word has a difference, set the previous difference number and order.
				iPreviousDifference = Math.max(oSelectionGroups[i].g_oSelectionWords[j].difference, oSelectionGroups[i].g_oSelectionWords[j].markerDifference, iPreviousDifference);

				// If this word has no differences, add it to the list.
				if (oSelectionGroups[i].g_oSelectionWords[j].difference == -1) {

					// If this is an equivalent word and should be grouped with the previous equivalent word:
					// - This word is part of an equivalent.
					// - The previous no difference word is part of an equivalent.
					// - This word is directly after the previous character.
					if (oSelectionGroups[i].g_oSelectionWords[j].equivalentDifference && oNoDifferenceWords.length > 0 && oNoDifferenceWords[oNoDifferenceWords.length - 1].equivalentDifference && iCurrentWord == oNoDifferenceWords[oNoDifferenceWords.length - 1].words[oNoDifferenceWords[oNoDifferenceWords.length - 1].words.length - 1].order + 1) {

						// Combine this equivalent word with the previous equivalent word.
						oNoDifferenceWords[oNoDifferenceWords.length - 1].words.push({
							selection: i,
							word: j,
							order: iCurrentWord,
						});

						// Update the bold italic flags.
						oNoDifferenceWords[oNoDifferenceWords.length - 1].bold = (oNoDifferenceWords[oNoDifferenceWords.length - 1].bold || oSelectionGroups[i].g_oSelectionWords[j].bold);
						oNoDifferenceWords[oNoDifferenceWords.length - 1].italic = (oNoDifferenceWords[oNoDifferenceWords.length - 1].italic || oSelectionGroups[i].g_oSelectionWords[j].italic);

						// Increment the current word tracker and continue.
						iCurrentWord++;
						continue;
					}

					// Add the word to the list.
					oNoDifferenceWords.push({
						words: [{
							selection: i,
							word: j,
							order: iCurrentWord
						}],
						previousDifference: iPreviousDifference,
						equivalentDifference: oSelectionGroups[i].g_oSelectionWords[j].equivalentDifference,
						bold: oSelectionGroups[i].g_oSelectionWords[j].bold,
						italic: oSelectionGroups[i].g_oSelectionWords[j].italic
					});
				}

				// Increment the current word tracker.
				iCurrentWord++;
			}
		}

		// Return the words with no differences.
		return oNoDifferenceWords;
	};

	/**
	 * Gets a list of characters with no differences.
	 * @param {object} oSelectionGroups - The selections.
	 */
	TextCompare.prototype.getCharactersWithNoDifferences = function (oSelectionGroups) {

		// Outline the no difference text to be returned.
		var oNoDifferenceCharacters = [];

		// Keep track of the character we are on and the previous difference.
		var iCurrentCharacter = 0;
		var iPreviousDifference = 0;
		var bLastCharacterInWord = false;

		// Loop through each of the text selections.
		for (var i = 0; i < oSelectionGroups.length; i++) {

			// Loop through each of the characters in the selection.
			for (var j = 0; j < oSelectionGroups[i].g_oSelectionCharacters.length; j++) {

				// If this character has a difference, set the previous difference number and order.
				iPreviousDifference = Math.max(oSelectionGroups[i].g_oSelectionCharacters[j].difference, oSelectionGroups[i].g_oSelectionCharacters[j].markerDifference, iPreviousDifference);

				// If this character has a difference, set the previous difference number and order.
				iPreviousDifference = Math.max(oSelectionGroups[i].g_oSelectionCharacters[j].difference, oSelectionGroups[i].g_oSelectionCharacters[j].markerDifference, iPreviousDifference);

				// Check if this is the last character in the word.
				bLastCharacterInWord = oSelectionGroups[i].g_oSelectionCharacters[j].lastInWord;

				// If this character has no differences, add it to the list.
				if (oSelectionGroups[i].g_oSelectionCharacters[j].difference == -1) {

					// If this is an equivalent character and should be grouped with the previous equivalent character:
					// - This character is part of an equivalent.
					// - The previous no difference character is part of an equivalent.
					// - This character is directly after the previous character.
					if (oSelectionGroups[i].g_oSelectionCharacters[j].equivalentDifference && oNoDifferenceCharacters.length > 0 && oNoDifferenceCharacters[oNoDifferenceCharacters.length - 1].equivalentDifference && iCurrentCharacter == oNoDifferenceCharacters[oNoDifferenceCharacters.length - 1].characters[oNoDifferenceCharacters[oNoDifferenceCharacters.length - 1].characters.length - 1].order + 1) {

						// Combine this equivalent character with the previous equivalent character.
						oNoDifferenceCharacters[oNoDifferenceCharacters.length - 1].characters.push({
							selection: i,
							character: j,
							order: iCurrentCharacter,
							lastCharacterInWord: bLastCharacterInWord
						});

						// Update the bold italic flags.
						oNoDifferenceCharacters[oNoDifferenceCharacters.length - 1].bold = (oNoDifferenceCharacters[oNoDifferenceCharacters.length - 1].bold || oSelectionGroups[i].g_oSelectionCharacters[j].bold);
						oNoDifferenceCharacters[oNoDifferenceCharacters.length - 1].italic = (oNoDifferenceCharacters[oNoDifferenceCharacters.length - 1].italic || oSelectionGroups[i].g_oSelectionCharacters[j].italic);

						// Increment the current character tracker and continue.
						iCurrentCharacter++;
						continue;
					}

					// Add the character to the list.
					oNoDifferenceCharacters.push({
						characters: [{
							selection: i,
							character: j,
							order: iCurrentCharacter,
							lastCharacterInWord: bLastCharacterInWord
						}],
						previousDifference: iPreviousDifference,
						equivalentDifference: oSelectionGroups[i].g_oSelectionCharacters[j].equivalentDifference,
						bold: oSelectionGroups[i].g_oSelectionCharacters[j].bold,
						italic: oSelectionGroups[i].g_oSelectionCharacters[j].italic
					});
				}

				// Increment the current character tracker.
				iCurrentCharacter++;
			}
		}

		// Return the characters with no differences.
		return oNoDifferenceCharacters;
	};

	/**
	 * Gets the difference number from an order.
	 * @param {int} iDifferenceOrder - The difference order.
	 * @param {object} oDifferences - The differences.
	 */
	TextCompare.prototype.getDifferenceNumberFromOrder = function (iDifferenceOrder, oDifferences) {
		return iDifferenceOrder - 1 > oDifferences.length - 1 ? g_oTextCompare.g_iNumberOfDifferences + 1 : oDifferences[iDifferenceOrder - 1].g_iDifferenceNumber;
	};

	/**
	 * Creates a gap for a difference.
	 * @param {int} iDifferenceNumber - The difference number.
	 * @param {int} iDifferenceOrder - The difference order.
	 * @param {object} oDifferences - The differences.
	 */
	TextCompare.prototype.createGapForDifference = function (iDifferenceNumber, iDifferenceOrder, oDifferences) {

		// Create a gap in the difference order and number.
		for (var i = 0; i < oDifferences.length; i++) {
			if (oDifferences[i].g_iDifferenceNumber >= iDifferenceNumber) {
				oDifferences[i].g_iDifferenceNumber++;
				oDifferences[i].g_iDifferenceOrder++;
			}
		}
	};

	/**
	 * Creates a word gap for a difference.
	 * @param {int} iDifferenceNumber - The difference number.
	 * @param {int} iDifferenceOrder - The difference order.
	 * @param {object} oSelections - The selections.
	 */
	TextCompare.prototype.createWordGapForDifference = function (iDifferenceNumber, iDifferenceOrder, oSelections) {

		// Create a gap in the words differences.
		for (var i = 0; i < oSelections.length; i++) {
			for (var j = 0; j < oSelections[i].g_oSelectionWords.length; j++) {
				if (oSelections[i].g_oSelectionWords[j].difference >= iDifferenceOrder) {
					oSelections[i].g_oSelectionWords[j].difference++
				}
				if (oSelections[i].g_oSelectionWords[j].markerDifference >= iDifferenceOrder) {
					oSelections[i].g_oSelectionWords[j].markerDifference++
				}
			}
		}
	};

	/**
	 * Creates a character gap for a difference.
	 * @param {int} iDifferenceNumber - The difference number.
	 * @param {int} iDifferenceOrder - The difference order.
	 * @param {object} oSelections - The selections.
	 */
	TextCompare.prototype.createCharacterGapForDifference = function (iDifferenceNumber, iDifferenceOrder, oSelections) {

		// Create a gap in the characters differences.
		for (var i = 0; i < oSelections.length; i++) {
			for (var j = 0; j < oSelections[i].g_oSelectionCharacters.length; j++) {
				if (oSelections[i].g_oSelectionCharacters[j].difference >= iDifferenceOrder) {
					oSelections[i].g_oSelectionCharacters[j].difference++
				}
				if (oSelections[i].g_oSelectionCharacters[j].markerDifference >= iDifferenceOrder) {
					oSelections[i].g_oSelectionCharacters[j].markerDifference++
				}
			}
		}
	}

	/**
	 * Begins stepping through differences.
	 */
	TextCompare.prototype.beginStepThroughDifferences = function () {

		// Check we have calculated differences.
		if (!g_oTextCompare.g_bDifferencesCalculated) {
			g_oGreenlightMessages.displayWarningBubble(g_oTextCompare.c_szTextCompareDifferencesNotCalculated);
			return;
		}

		// Step through to the first difference.
		g_oTextCompare.stepThroughToDifference(1);
	};

	/**
	 * Steps to the next difference.
	 */
	TextCompare.prototype.nextStepThroughDifferences = function () {

		// If we are not in step through mode, start this.
		if (g_oTextCompare.g_iStepThroughDifference == null) {
			g_oTextCompare.beginStepThroughDifferences();
			return;
		}

		// Step through to the next difference.
		g_oTextCompare.stepThroughToDifference(g_oTextCompare.g_iStepThroughDifference + 1 > g_oTextCompare.g_iNumberOfDifferences ? 1 : g_oTextCompare.g_iStepThroughDifference + 1);
	};

	/**
	 * Steps to the previous difference.
	 */
	TextCompare.prototype.previousStepThroughDifferences = function () {

		// If we are not in step through mode, start this.
		if (g_oTextCompare.g_iStepThroughDifference == null) {
			g_oTextCompare.beginStepThroughDifferences();
			return;
		}

		// Step through to the previous difference.
		g_oTextCompare.stepThroughToDifference(g_oTextCompare.g_iStepThroughDifference - 1 == 0 ? g_oTextCompare.g_iNumberOfDifferences : g_oTextCompare.g_iStepThroughDifference - 1);
	};

	/**
	 * Steps through to a difference.
	 * @param {int} iDifference - The difference.
	 */
	TextCompare.prototype.stepThroughToDifference = function (iDifference) {

		// If the review screen is open, close this.
		if (g_oTextCompare.g_bTextCompareDivShown) {
			g_oTextCompare.toggleTextCompareDiv();
		}

		// Step through to the difference.
		g_oTextCompare.g_iStepThroughDifference = iDifference;

		// Render the text compare annotations.
		g_oTextCompare.renderTextCompareAnnotations();

		// Pan to the difference.
		g_oTextCompare.panToDifference(g_oTextCompare.g_iStepThroughDifference);
	};

	/**
	 * Ends stepping through differences.
	 */
	TextCompare.prototype.endStepThroughDifferences = function () {

		// Stop stepping through differences.
		g_oTextCompare.g_iStepThroughDifference = null;

		// Render the text compare annotations.
		g_oTextCompare.renderTextCompareAnnotations();
	};

	/**
	 * Updates the differences bar.
	 */
	TextCompare.prototype.updateDifferencesBar = function () {

		// If we are not stepping through differences.
		if (g_oTextCompare.g_iStepThroughDifference == null) {

			// If we have differences.
			if (g_oTextCompare.g_iNumberOfDifferences > 0) {

				// Set the differences bar back to its default state.
				$("#divTextDifferencesTitle").html(g_oTextCompare.c_szTextCompareDifferencesAll);
				$("#divTextDifferencesContent").html(g_oTextCompare.c_szTextCompareStepThroughIndividual);
			}
			// If we have no differences.
			else {

				// Set the differences bar back to its default state.
				$("#divTextDifferencesTitle").html(g_oTextCompare.c_szTextCompareNoDifferencesText);
				$("#divTextDifferencesContent").html(g_oTextCompare.c_szTextCompareNoDifferencesText);
			}

			// Update the bar details.
			$("#selTextDifferenceStatus").val(g_oTextCompare.eDifferenceStatuses.Initial);
			$("#selTextDifferenceStatus").prop("disabled", "true");
			$("#divTextDifferenceCounter").html("");
			$("#divTextDifferenceCounter").hide();
			$("#divTextDifferenceAnnotate").hide();
			return;
		}

		// Get the text selections.
		var oTextSelections = g_oTextCompare.getTextSelectionGroup(false);

		// Loop through each of the text selections.
		for (var i = 0; i < oTextSelections.length; i++) {

			// Ensure this text selection has differences.
			if (oTextSelections[i].g_oTextDifferences == null) {
				continue;
			}

			// Loop through each of the differences on the text selection.
			for (var j = 0; j < oTextSelections[i].g_oTextDifferences.length; j++) {

				// If this is the matching difference.
				if (oTextSelections[i].g_oTextDifferences[j].g_iDifferenceNumber == g_oTextCompare.g_iStepThroughDifference) {

					// Extract the difference.
					var oDifference = oTextSelections[i].g_oTextDifferences[j];

					// Update the bar with the difference details.
					$("#divTextDifferencesTitle").html(g_oTextCompare.c_szTextCompareGroupText + " " + oTextSelections[i].g_iGroupOrder + " " + g_oTextCompare.c_szTextCompareDifferenceText + " " + oDifference.g_iDifferenceOrder);
					$("#selTextDifferenceStatus").val(oDifference.g_iDifferenceStatus);
					$("#selTextDifferenceStatus").prop("disabled", "");
					$("#divTextDifferenceCounter").html("(" + g_oTextCompare.g_iStepThroughDifference + "/" + g_oTextCompare.g_iNumberOfDifferences + ")");
					$("#divTextDifferenceCounter").show();

					// If we can add difference annotations, show the annotate icon.
					if (g_oTextCompare.g_bDifferenceAnnotations && !oTextSelections[i].g_oTextDifferences[j].g_bAnnotationCreated) {
						$("#divTextDifferenceAnnotate").show();
					}
					// If we cannot add difference annotations, hide the annotate icon.
					else {
						$("#divTextDifferenceAnnotate").hide();
					}

					// Add the difference text.
					var szHTML = g_oTextCompare.getDifferenceText(oDifference.g_eDifferenceType) + ": ";
					if (g_oTextCompare.differenceTypeIsSubstitution(oDifference.g_eDifferenceType)) {
						szHTML += g_oTextCompare.c_szTextCompareCompareDocument + ": ";
					}
					szHTML += oDifference.g_oFormattedDifferenceText[0];
					if (g_oTextCompare.differenceTypeIsSubstitution(oDifference.g_eDifferenceType)) {
						szHTML += ", " + g_oTextCompare.c_szTextCompareApprovalDocument + ": ";
						szHTML += oDifference.g_oFormattedDifferenceText[1];
					}
					$("#divTextDifferencesContent").html(szHTML);

					// Return.
					return;
				}
			}
		}
	};

	/**
	 * Shows the differences summary.
	 */
	TextCompare.prototype.showDifferencesSummary = function () {

		// Move the differences summary.
		g_oTextCompare.moveDifferencesSummary(event);

		// Show the differences summary.
		$("#divTextComparisonSummary").show();
	};

	/**
	 * Moves the differences summary.
	 */
	TextCompare.prototype.moveDifferencesSummary = function () {

		// Determine the position of the summary.
		var iPositionTop = $(".textCompareSummaryIcon").position().top + 26;
		var iPositionLeft = $(".textCompareSummaryIcon").position().left;

		// Set the position of the summary.
		$("#divTextComparisonSummary").css("top", iPositionTop + "px");
		$("#divTextComparisonSummary").css("left", iPositionLeft + "px");
	};

	/**
	 * Hides the differences summary.
	 */
	TextCompare.prototype.hideDifferencesSummary = function () {

		// Hide the differences summary.
		$("#divTextComparisonSummary").hide();
	};

	/**
	 * Updates the differences summary.
	 */
	TextCompare.prototype.updateDifferencesSummary = function () {

		// Get the number of each type of difference.
		var iTotalDifferences = g_oTextCompare.countDifferences(null, null, null);
		var iAdditionDifferences = g_oTextCompare.countDifferences(null, g_oTextCompare.eDifferenceTypes.Addition, null);
		var iDeletionDifferences = g_oTextCompare.countDifferences(null, g_oTextCompare.eDifferenceTypes.Deletion, null);
		var iSubstitutionDifferences = g_oTextCompare.countDifferences(null, g_oTextCompare.eDifferenceTypes.Substitution, null);
		iSubstitutionDifferences += g_oTextCompare.countDifferences(null, g_oTextCompare.eDifferenceTypes.WeightSubstitution, null);
		iSubstitutionDifferences += g_oTextCompare.countDifferences(null, g_oTextCompare.eDifferenceTypes.StyleSubstitution, null);
		iSubstitutionDifferences += g_oTextCompare.countDifferences(null, g_oTextCompare.eDifferenceTypes.WeightStyleSubstitution, null);

		// Get the number of each status type.
		var iInitialDifferences = g_oTextCompare.countDifferences(null, null, g_oTextCompare.eDifferenceStatuses.Initial);
		var iIgnoredDifferences = g_oTextCompare.countDifferences(null, null, g_oTextCompare.eDifferenceStatuses.Ignore);
		var iAcceptedDifferences = g_oTextCompare.countDifferences(null, null, g_oTextCompare.eDifferenceStatuses.Accept);
		var iRejectedDifferences = g_oTextCompare.countDifferences(null, null, g_oTextCompare.eDifferenceStatuses.Reject);

		// Build up the HTML for the summary.
		var szHTML = '';

		// Open the summary div.
		szHTML += '<div class="textCompareSummary__content">';

		// Add the row for the total differences.
		szHTML += '<div class="textCompareSummary__row">';
		szHTML += '<div class="textCompareSummary__number">';
		szHTML += '<div class="textCompareSummary_text">' + iTotalDifferences + '</div>';
		szHTML += '</div>';
		szHTML += '<div class="textCompareSummary__description">';
		szHTML += '<div class="textCompareSummary_text--left">' + (iTotalDifferences == 1 ? g_oTextCompare.c_szTextCompareDifferenceLocated : g_oTextCompare.c_szTextCompareDifferencesLocated) + '</div>';
		szHTML += '</div>';
		szHTML += '</div>';

		// Add the row for the addition differences.
		szHTML += '<div class="textCompareSummary__row textCompareSummary__row--small">';
		szHTML += '<div class="textCompareSummary__number textCompareSummary__number--small">';
		szHTML += '<div class="textCompareSummary_text">' + iAdditionDifferences + '</div>';
		szHTML += '</div>';
		szHTML += '<div class="textCompareSummary__description textCompareSummary__description--small">';
		szHTML += '<div class="textCompareSummary_text--left">' + (iAdditionDifferences == 1 ? g_oTextCompare.c_szTextCompareDifferenceTypeAddition : g_oTextCompare.c_szTextCompareDifferenceTypeAdditions) + '</div>';
		szHTML += '</div>';
		szHTML += '</div>';

		// Add the row for the deletion differences.
		szHTML += '<div class="textCompareSummary__row textCompareSummary__row--small">';
		szHTML += '<div class="textCompareSummary__number textCompareSummary__number--small">';
		szHTML += '<div class="textCompareSummary_text">' + iDeletionDifferences + '</div>';
		szHTML += '</div>';
		szHTML += '<div class="textCompareSummary__description textCompareSummary__description--small">';
		szHTML += '<div class="textCompareSummary_text--left">' + (iDeletionDifferences == 1 ? g_oTextCompare.c_szTextCompareDifferenceTypeDeletion : g_oTextCompare.c_szTextCompareDifferenceTypeDeletions) + '</div>';
		szHTML += '</div>';
		szHTML += '</div>';

		// Add the row for the substitution differences.
		szHTML += '<div class="textCompareSummary__row textCompareSummary__row--small">';
		szHTML += '<div class="textCompareSummary__number textCompareSummary__number--small textCompareSummary__number--last">';
		szHTML += '<div class="textCompareSummary_text">' + iSubstitutionDifferences + '</div>';
		szHTML += '</div>';
		szHTML += '<div class="textCompareSummary__description textCompareSummary__description--small textCompareSummary__description--last">';
		szHTML += '<div class="textCompareSummary_text--left">' + (iSubstitutionDifferences == 1 ? g_oTextCompare.c_szTextCompareDifferenceTypeSubstitution : g_oTextCompare.c_szTextCompareDifferenceTypeSubstitutions) + '</div>';
		szHTML += '</div>';
		szHTML += '</div>';

		// Close the summary div.
		szHTML += '</div>';

		// Add the summary status div.
		szHTML += '<div class="textCompareSummary__content">';

		// Add the statuses.
		szHTML += '<div class="textCompareSummary__row textCompareSummary__row--small">';
		szHTML += '<div class="textCompareSummary__status textCompareSummaryIntial"><div class="textCompareSummary__statusText">' + iInitialDifferences + ' ' + g_oTextCompare.c_szTextCompareDifferenceStatusInitial + '</div></div>';
		szHTML += '<div class="textCompareSummary__status textCompareSummary__status--last textCompareSummaryIgnored"><div class="textCompareSummary__statusText">' + iIgnoredDifferences + ' ' + g_oTextCompare.c_szTextCompareDifferenceStatusIgnored + '</div></div>';
		szHTML += '</div>';
		szHTML += '<div class="textCompareSummary__row textCompareSummary__row--small">';
		szHTML += '<div class="textCompareSummary__status textCompareSummaryApproved"><div class="textCompareSummary__statusText">' + iAcceptedDifferences + ' ' + g_oTextCompare.c_szTextCompareDifferenceStatusAccepted + '</div></div>';
		szHTML += '<div class="textCompareSummary__status textCompareSummary__status--last textCompareSummaryRejected"><div class="textCompareSummary__statusText">' + iRejectedDifferences + ' ' + g_oTextCompare.c_szTextCompareDifferenceStatusRejected + '</div></div>';
		szHTML += '</div>';

		// Close the summary status div.
		szHTML += '</div>';

		// Apply the HTML.
		$("#divTextComparisonSummary").html(szHTML);
	};

	/**
	 * Pans to a difference.
	 * @param {int} iDifference - The difference.
	 */
	TextCompare.prototype.panToDifference = function (iDifference) {

		// Sync the zoom level for both canvas to the highest zoom level.
		if (g_oViewer.g_oArtworkViewer.g_iZoomLevel > g_oCompare.g_oCompareViewer.g_iZoomLevel) {
			g_oCompare.g_oCompareViewer.synchroniseZoomPanProperties(g_oViewer.g_oArtworkViewer);
			g_oCompare.g_oCompareViewer.g_oCanvasZoom.startZoomAnimate();
		}
		else if (g_oCompare.g_oCompareViewer.g_iZoomLevel > g_oViewer.g_oArtworkViewer.g_iZoomLevel) {
			g_oViewer.g_oArtworkViewer.synchroniseZoomPanProperties(g_oCompare.g_oCompareViewer);
			g_oViewer.g_oArtworkViewer.g_oCanvasZoom.startZoomAnimate();
		}

		// Get the pages for the difference on both canvas.
		var iApprovalDifferencePage = g_oTextCompare.getPageForDifference(iDifference, false);
		var iCompareDifferencePage = g_oTextCompare.getPageForDifference(iDifference, true);

		// If we did not find a page for either canvas, assume we are on the correct page.
		iApprovalDifferencePage = iApprovalDifferencePage == -1 ? g_oViewer.g_oArtworkViewer.g_iCurrentPage : iApprovalDifferencePage;
		iCompareDifferencePage = iCompareDifferencePage == -1 ? g_oCompare.g_oCompareViewer.g_iCurrentPage : iCompareDifferencePage;

		// If we are on the wrong page on either canvas.
		if (iApprovalDifferencePage != g_oViewer.g_oArtworkViewer.g_iCurrentPage || iCompareDifferencePage != g_oCompare.g_oCompareViewer.g_iCurrentPage) {

			// Change to the correct approval page.
			g_oViewer.g_oArtworkViewer.g_iCurrentPage = iApprovalDifferencePage;
			g_oViewer.g_oArtworkViewer.changeCurrentPage();

			// Change to the correct compare page.
			g_oCompare.g_oCompareViewer.g_iCurrentPage = iCompareDifferencePage;
			g_oCompare.g_oCompareViewer.changeCurrentPage();

			// Publish that the pages have changed.
			EventService.publish("changeCurrentPage", g_oViewer.g_oArtworkViewer, g_oCompare.g_oCompareViewer);
		}
		// If we are on the correct pages.
		else {

			// Pan to the difference on both canvas.
			g_oTextCompare.panToDifferenceCanvas(iDifference, false);
			g_oTextCompare.panToDifferenceCanvas(iDifference, true);
		}
	};

	/**
	 * 
	 * @param {any} iDifference
	 * @param {any} bCompare
	 */
	TextCompare.prototype.getPageForDifference = function (iDifference, bCompare) {

		// Get the text selection groups for this canvas.
		var oTextSelectionsGroups = g_oTextCompare.getTextSelectionGroup(bCompare);

		// Get the text selection and difference.
		var oDifference = null;
		var oTextSelectionGroup = null;

		// Check through each of the text selections.
		for (var i = 0; i < oTextSelectionsGroups.length; i++) {

			// Ensure this group has text differences.
			if (oTextSelectionsGroups[i].g_oTextDifferences == null) {
				continue;
			}

			// Check through each of the text selections.
			for (var j = 0; j < oTextSelectionsGroups[i].g_oTextDifferences.length; j++) {

				// If this is the difference we are looking for.
				if (oTextSelectionsGroups[i].g_oTextDifferences[j].g_iDifferenceNumber == iDifference) {

					// Set the difference and text selection group.
					oDifference = oTextSelectionsGroups[i].g_oTextDifferences[j];
					oTextSelectionGroup = oTextSelectionsGroups[i];
					break;
				}
			}

			// Stop if we have found the difference.
			if (oDifference != null) {
				break;
			}
		}

		// Ensure we have found both the difference and text selection group.
		if (oDifference == null || oTextSelectionGroup == null) {
			return -1;
		}

		// Check through each of the text selection.
		for (var j = 0; j < oTextSelectionGroup.g_oTextSelections.length; j++) {

			// If we are at a word level difference.
			if (g_oTextCompare.g_iAccuracy == g_oTextCompare.eAccuracyLevels.Word) {

				// Check through each of the words.
				for (var k = 0; k < oTextSelectionGroup.g_oTextSelections[j].g_oSelectionWords.length; k++) {

					// If this word is part of the difference, return the difference page.
					// If this word is part of the difference as a marker, return the difference page.
					if (oTextSelectionGroup.g_oTextSelections[j].g_oSelectionWords[k].difference == oDifference.g_iDifferenceOrder ||
						oTextSelectionGroup.g_oTextSelections[j].g_oSelectionWords[k].markerDifference == oDifference.g_iDifferenceOrder) {
						return oTextSelectionGroup.g_oTextSelections[j].g_oSelectionWords[k].page;
					}
				}
			}
			// If we are at a character level difference.
			else {

				// Check through each of the characters.
				for (var k = 0; k < oTextSelectionGroup.g_oTextSelections[j].g_oSelectionCharacters.length; k++) {

					// If this character is part of the difference, return the difference page.
					// If this character is part of the difference as a marker, return the difference page.
					if (oTextSelectionGroup.g_oTextSelections[j].g_oSelectionCharacters[k].difference == oDifference.g_iDifferenceOrder ||
						oTextSelectionGroup.g_oTextSelections[j].g_oSelectionCharacters[k].markerDifference == oDifference.g_iDifferenceOrder) {
						return oTextSelectionGroup.g_oTextSelections[j].g_oSelectionCharacters[k].page;
					}
				}
			}
		}

		// Return that we did not find a difference page.
		return -1;
	};

	/**
	 * Pans to a difference on canvas.
	 * @param {int} iDifference - The difference.
	 * @param {boolean} bCompare - If this is the compare canvas.
	 */
	TextCompare.prototype.panToDifferenceCanvas = function (iDifference, bCompare) {

		// Get the canvas we are going to pan on.
		var oArtworkCanvas = !bCompare ? g_oViewer.g_oArtworkViewer : g_oCompare.g_oCompareViewer;
		var oAnnotationCanvas = !bCompare ? g_oViewer.g_oAnnotationViewer : g_oCompare.g_oCompareAnnotationViewer;

		// Ensure we are zoomed.
		if (oArtworkCanvas.g_iZoomLevel == 1) {
			return;
		}

		// Get the text selection groups for this canvas.
		var oTextSelectionsGroups = g_oTextCompare.getTextSelectionGroup(bCompare);

		// Get the difference and text selections.
		var oDifference = null;
		var oTextSelections = null;
		for (var i = 0; i < oTextSelectionsGroups.length; i++) {
			// Ensure this text selection has differences.
			if (oTextSelectionsGroups[i].g_oTextDifferences == null) {
				continue;
			}
			for (var j = 0; j < oTextSelectionsGroups[i].g_oTextDifferences.length; j++) {
				if (oTextSelectionsGroups[i].g_oTextDifferences[j].g_iDifferenceNumber == iDifference) {
					oDifference = oTextSelectionsGroups[i].g_oTextDifferences[j];
					oTextSelections = oTextSelectionsGroups[i].g_oTextSelections;
					break;
				}
			}
			if (oDifference != null) {
				break;
			}
		}

		// Outline the bounds to pan to.
		var oPanBounds = null;

		// If we have calculated differences to the word level.
		if (g_oTextCompare.g_iAccuracy == g_oTextCompare.eAccuracyLevels.Word) {

			// Keep track of a list of words that are part of this difference.
			var oDifferenceWords = g_oTextCompare.getWordsForTextDifference(oTextSelections, oDifference.g_iDifferenceOrder, oArtworkCanvas.g_iCurrentPage);

			// If we have no words, get the markers for this difference.
			if (oDifferenceWords.length == 0) {
				oDifferenceWords = g_oTextCompare.getWordMarkersForTextDifference(oTextSelections, oDifference.g_iDifferenceOrder, oArtworkCanvas.g_iCurrentPage);
			}

			// Ensure we have words to pan to.
			if (oDifferenceWords.length > 0) {

				// Get the bounds of the word list for this difference.
				oPanBounds = g_oTextTools.getWordListBounds(oDifferenceWords, oArtworkCanvas, oAnnotationCanvas);
			}
		}
		// If we have calculated differences to the character level.
		else {

			// Keep track of a list of characters that are part of this difference.
			var oDifferenceCharacters = g_oTextCompare.getCharactersForTextDifference(oTextSelections, oDifference.g_iDifferenceOrder, oArtworkCanvas.g_iCurrentPage);

			// If we have no characters, get the markers for this difference.
			if (oDifferenceCharacters.length == 0) {
				oDifferenceCharacters = g_oTextCompare.getCharacterMarkersForTextDifference(oTextSelections, oDifference.g_iDifferenceOrder, oArtworkCanvas.g_iCurrentPage);
			}

			// Ensure we have characters to pan to.
			if (oDifferenceCharacters.length > 0) {

				// Get the bounds of the character list for this difference.
				oPanBounds = g_oTextTools.getCharacterListBounds(oDifferenceCharacters, oArtworkCanvas, oAnnotationCanvas);
			}
		}

		// Ensure we have bounds to pan to.
		if (oPanBounds == null) {
			return;
		}

		// Calculate the mid point.
		var oBoundsMidPoint = [oPanBounds.x1 + ((oPanBounds.x2 - oPanBounds.x1) / 2), oPanBounds.y1 + ((oPanBounds.y2 - oPanBounds.y1) / 2)];

		// Determine the max canvas width and height.
		var maxCanvasWidth = Math.max(g_oViewer.g_oArtworkViewer.g_iScaledImageWidth, g_oCompare.g_oCompareViewer.g_iScaledImageWidth);
		var maxCanvasHeight = Math.max(g_oViewer.g_oArtworkViewer.g_iScaledImageHeight, g_oCompare.g_oCompareViewer.g_iScaledImageHeight);

		// Determine the drag offset.
		oArtworkCanvas.g_iTempDragOffset_x = (oBoundsMidPoint[0] * oArtworkCanvas.g_iScaledImageWidth) - (maxCanvasWidth / 2);
		oArtworkCanvas.g_iTempDragOffset_y = (oBoundsMidPoint[1] * oArtworkCanvas.g_iScaledImageHeight) - (maxCanvasHeight / 2);

		// Determine the drag difference.
		oArtworkCanvas.g_iDragXDiff = (oArtworkCanvas.g_iDragOffset_x - oArtworkCanvas.g_iTempDragOffset_x) / oArtworkCanvas.g_iAnimateFrames;
		oArtworkCanvas.g_iDragYDiff = (oArtworkCanvas.g_iDragOffset_y - oArtworkCanvas.g_iTempDragOffset_y) / oArtworkCanvas.g_iAnimateFrames;

		// Perform the pan.
		oArtworkCanvas.g_oCanvasZoom.startZoomAnimate();
	};

	/**
	 * Creates a step difference annotation.
	 */
	TextCompare.prototype.createStepDifferenceAnnotation = function () {

		// Get the current difference from the step through.
		var iDifference = g_oTextCompare.g_iStepThroughDifference;

		// Create the difference annotation for this difference.
		g_oTextCompare.createDifferenceAnnotationEdit(iDifference);
	};

	/**
	 * Creates a difference annotation edit area.
	 * @param {int} iDifference - The difference number.
	 */
	TextCompare.prototype.createDifferenceAnnotationEdit = function (iDifference) {

		// Create the difference annotation.
		var oDifferenceAnnotation = g_oTextCompare.createDifferenceAnnotation(iDifference);

		// Ensure we have a difference annotation.
		if (oDifferenceAnnotation == null) {
			g_oGreenlightMessages.displayErrorBubble("Failed to create the difference annotation");
			return;
		}

		// Hide the annotate button.
		$("#divTextDifferenceAnnotate").hide();

		// Remove the content from the text difference to make room for the annotation edit area.
		$("#divTextDifferencesContent").html("");

		// Create the text area input.
		oDifferenceAnnotation.createAnnotationTextArea("divTextDifferencesContent", oDifferenceAnnotation.g_iAuthor, oDifferenceAnnotation.g_szAuthorEmail, oDifferenceAnnotation.g_szAuthorEmail, eFieldTypes.DifferenceHeader, false, oDifferenceAnnotation.g_szTextContent, $("#divTextDifferencesContent")[0], Date.now(), null, g_oViewer.g_oArtworkViewer, g_oViewer.g_oAnnotationViewer, null, null);

		// Add the create and cancel buttons to the text area input.
		$("#divTextDifferencesContent").append(
			'<div class="divTextDifferences__buttons">' +
			'<div class="divTextDifferences__button divTextDifferences__button--cancel" onclick="g_oTextCompare.cancelDifferenceAnnotation();">' + g_oTextCompare.c_szTextCompareCancelAnnotation + '</div>' +
			'<div class="divTextDifferences__button divTextDifferences__button--confirm" onclick="g_oTextCompare.saveDifferenceAnnotation();">' + g_oTextCompare.c_szTextCompareCreateAnnotation + '</div>' +
			'</div>'
		);
	};

	/**
	 * Saves the difference annotation.
	 */
	TextCompare.prototype.saveDifferenceAnnotation = function () {

		// Get the difference number.
		var iDifference = g_oTextCompare.g_iStepThroughDifference;

		// Create the difference annotation.
		var oDifferenceAnnotation = g_oTextCompare.createDifferenceAnnotation(iDifference);

		// Ensure we have a difference annotation.
		if (oDifferenceAnnotation == null) {
			g_oGreenlightMessages.displayErrorBubble("Failed to create the difference annotation");
			return;
		}

		// Update the difference text with that of the editor.
		oDifferenceAnnotation.g_szTextContent = $(".jqte_editor").html();

		// Save the difference annotation.
		oDifferenceAnnotation.AnnotationSave();

		// Get the text selection groups for this canvas.
		var oTextSelectionsGroups = g_oTextCompare.getTextSelectionGroup(false);

		// Set that this difference has had an annotation created.
		for (var i = 0; i < oTextSelectionsGroups.length; i++) {
			// Ensure this text selection has differences.
			if (oTextSelectionsGroups[i].g_oTextDifferences == null) {
				continue;
			}
			for (var j = 0; j < oTextSelectionsGroups[i].g_oTextDifferences.length; j++) {
				if (oTextSelectionsGroups[i].g_oTextDifferences[j].g_iDifferenceNumber == iDifference) {
					oTextSelectionsGroups[i].g_oTextDifferences[j].g_bAnnotationCreated = true;
				}
			}
		}

		// Update the differences bar.
		g_oTextCompare.updateDifferencesBar();
	};

	/**
	 * Cancels the difference annotation.
	 */
	TextCompare.prototype.cancelDifferenceAnnotation = function () {

		// Update the differences bar.
		g_oTextCompare.updateDifferencesBar();
	};

	/**
	 * Creates a difference annotation.
	 * @param {int} iDifference - The difference number.
	 */
	TextCompare.prototype.createDifferenceAnnotation = function (iDifference) {

		// Get the canvas we are going to add annotations to.
		var oArtworkCanvas = g_oViewer.g_oArtworkViewer;
		var oAnnotationCanvas = g_oViewer.g_oAnnotationViewer;

		// Get the text selection groups for this canvas.
		var oTextSelectionsGroups = g_oTextCompare.getTextSelectionGroup(false);

		// Get the difference and text selections.
		var oDifference = null;
		var oTextSelections = null;
		for (var i = 0; i < oTextSelectionsGroups.length; i++) {
			// Ensure this text selection has differences.
			if (oTextSelectionsGroups[i].g_oTextDifferences == null) {
				continue;
			}
			for (var j = 0; j < oTextSelectionsGroups[i].g_oTextDifferences.length; j++) {
				if (oTextSelectionsGroups[i].g_oTextDifferences[j].g_iDifferenceNumber == iDifference) {
					oDifference = oTextSelectionsGroups[i].g_oTextDifferences[j];
					oTextSelections = oTextSelectionsGroups[i].g_oTextSelections;
					break;
				}
			}
			if (oDifference != null) {
				break;
			}
		}

		// Keep track of the annotation text.
		var oAnnotationText = [];

		// If we have calculated differences to a word level.
		if (g_oTextCompare.g_iAccuracy == g_oTextCompare.eAccuracyLevels.Word) {

			// Get the words for the text difference.
			var oDifferenceWords = g_oTextCompare.getWordsForTextDifference(oTextSelections, oDifference.g_iDifferenceOrder, oArtworkCanvas.g_iCurrentPage);

			// If we have no words, get the markers for this difference.
			if (oDifferenceWords.length == 0) {
				oDifferenceWords = g_oTextCompare.getWordMarkersForTextDifference(oTextSelections, oDifference.g_iDifferenceOrder, oArtworkCanvas.g_iCurrentPage);
			}

			// Convert difference words to annotation text.
			oAnnotationText = g_oTextCompare.convertWordsToAnnotationText(oDifferenceWords);
		}
		// If we have calculated differences to a character level.
		else {			
			// Get the characters for the text difference.
			var oDifferenceCharacters = g_oTextCompare.getCharactersForTextDifference(oTextSelections, oDifference.g_iDifferenceOrder, oArtworkCanvas.g_iCurrentPage);

			// If we have no characters, get the markers for this difference.
			if (oDifferenceCharacters.length == 0) {
				oDifferenceCharacters = g_oTextCompare.getCharacterMarkersForTextDifference(oTextSelections, oDifference.g_iDifferenceOrder, oArtworkCanvas.g_iCurrentPage);
			}
			
			// Convert difference characters to annotation text.
			oAnnotationText = g_oTextCompare.convertCharactersToAnnotationText(oDifferenceCharacters);
		}

		// Ensure we have text.
		if (oAnnotationText.length == 0) {
			return null;
		}

		// Create the text annotation.
		var oTextAnnotation = g_oTextTools.generateTextAnnotation(oAnnotationText, eAnnotationTypes.TextHighlight, oArtworkCanvas, oAnnotationCanvas);

		// Update the colour of the annotation based on the difference status.
		oTextAnnotation.g_szColour = g_oAnnotations.getHexColour(g_oTextCompare.getAnnotationColourFromDifferenceStatus(oDifference.g_iDifferenceStatus));

		// Update the text of the annotation based on the difference.
		oTextAnnotation.g_szTextContent = '<div style="font-size:18px; font-weight: bold;">' + g_oTextCompare.getDifferenceText(oDifference.g_eDifferenceType) + '</div>';
		if (g_oTextCompare.differenceTypeIsSubstitution(oDifference.g_eDifferenceType)) {
			oTextAnnotation.g_szTextContent += '<div style="font-weight: bold;">' + g_oTextCompare.c_szTextCompareCompareDocument + ':</div>';
		}
		oTextAnnotation.g_szTextContent += '<div>' + oDifference.g_oFormattedDifferenceText[0] + '</div>';
		if (g_oTextCompare.differenceTypeIsSubstitution(oDifference.g_eDifferenceType)) {
			oTextAnnotation.g_szTextContent += '<div style="font-weight: bold;">' + g_oTextCompare.c_szTextCompareApprovalDocument + ':</div>';
			oTextAnnotation.g_szTextContent += '<div>' + oDifference.g_oFormattedDifferenceText[1] + '</div>';
		}

		// Return the difference annotation.
		return oTextAnnotation;
	};

	/**
	 * Converts the difference words to annotation text.
	 * @param {object} oDifferenceWords - The difference words.
	 */
	TextCompare.prototype.convertWordsToAnnotationText = function (oDifferenceWords) {
		
		// Outline the annotation text to be returned.
		var oAnnotationText = [];

		// Loop through each of the difference words.
		for (var i = 0; i < oDifferenceWords.length; i++) {

			// Add the difference word as an annotation text.
			oAnnotationText.push({
				startingText: {
					paragraph: oDifferenceWords[i].paragraph,
					line: oDifferenceWords[i].line,
					word: oDifferenceWords[i].word
				},
				endingText: {
					paragraph: oDifferenceWords[i].paragraph,
					line: oDifferenceWords[i].line,
					word: oDifferenceWords[i].word
				},
				leftToRight: true
			});
		}

		// Merge the sections words to create the annotation text.
		oAnnotationText = g_oTextTools.mergeSections_Words(oAnnotationText, oArtworkCanvas, oAnnotationCanvas);

		// Return the annotation text.
		return oAnnotationText;
	};

	/**
	 * Converts the difference characters to annotation text.
	 * @param {object} oDifferenceCharacters - The difference characters.
	 */
	TextCompare.prototype.convertCharactersToAnnotationText = function (oDifferenceCharacters) {

		// Outline the annotation text to be returned.
		var oAnnotationText = [];

		// Loop through each of the difference characters.
		for (var i = 0; i < oDifferenceCharacters.length; i++) {

			// Add the difference character as an annotation text.
			oAnnotationText.push({
				startingText: {
					paragraph: oDifferenceCharacters[i].paragraph,
					line: oDifferenceCharacters[i].line,
					word: oDifferenceCharacters[i].word,
					character: oDifferenceCharacters[i].character
				},
				endingText: {
					paragraph: oDifferenceCharacters[i].paragraph,
					line: oDifferenceCharacters[i].line,
					word: oDifferenceCharacters[i].word,
					character: oDifferenceCharacters[i].character
				},
				leftToRight: true
			});
		}

		// Merge the sections characters to create the annotation text.
		oAnnotationText = g_oTextTools.mergeSections_Characters(oAnnotationText, oArtworkCanvas, oAnnotationCanvas);

		// Return the annotation text.
		return oAnnotationText;
	};

	/**
	 * Checks if a difference type is a substitution.
	 * @param {int} eDifferenceType - The difference type to be checked.
	 */
	TextCompare.prototype.differenceTypeIsSubstitution = function (iDifferenceType) {
		return iDifferenceType == g_oTextCompare.eDifferenceTypes.Substitution || iDifferenceType == g_oTextCompare.eDifferenceTypes.WeightSubstitution ||
			iDifferenceType == g_oTextCompare.eDifferenceTypes.StyleSubstitution || iDifferenceType == g_oTextCompare.eDifferenceTypes.WeightStyleSubstitution;
	};

	/**
	 * Determines the bold italic substitution type.
	 * @param {any} bHasBoldDifference - The bold difference.
	 * @param {any} bHasItalicDifference - The italic difference.
	 */
	TextCompare.prototype.determineBoldItalicSubstitutionType = function (bHasBoldDifference, bHasItalicDifference) {
		var iDifferenceType = g_oTextCompare.eDifferenceTypes.NoDifference;

		// Determine the difference type.
		if (bHasBoldDifference && bHasItalicDifference) {
			iDifferenceType = g_oTextCompare.eDifferenceTypes.WeightStyleSubstitution;
		}
		else if (bHasBoldDifference) {
			iDifferenceType = g_oTextCompare.eDifferenceTypes.WeightSubstitution;
		}
		else if (bHasItalicDifference) {
			iDifferenceType = g_oTextCompare.eDifferenceTypes.StyleSubstitution;
		}

		return iDifferenceType;
	};

	/**
	 * Populates text data.
	 * @param {any} oTextData - The text data to be populated.
	 * @param {any} oArtworkCanvas - The artwork canvas.
	 * @param {any} oAnnotationCanvas - The annotation canvas.
	 */
	TextCompare.prototype.populateTextData = function (oTextData, oArtworkCanvas, oAnnotationCanvas) {

		// Get the text data for the document page.
		var oFullTextData = g_oTextData.getTextData(oArtworkCanvas.g_oDoc.documentId, oArtworkCanvas.g_iCurrentPage).textData;

		// Add any missing character data.
		for (var i = 0; i < oTextData.length; i++) {

			// Populate the starting text character if required.
			if (oTextData[i].startingText.character == undefined) {

				// Extract the word.
				var oWord = oFullTextData.Paragraphs[oTextData[i].startingText.paragraph].Lines[oTextData[i].startingText.line].Words[oTextData[i].startingText.word];

				// Set the starting text to the first / last character.
				oTextData[i].startingText.character = oTextData[i].leftToRight ? oWord.Characters[0].Order : oWord.Characters[oWord.Characters.length - 1].Order;
			}

			// Populate the ending text character if required.
			if (oTextData[i].endingText.character == undefined) {

				// Extract the word.
				var oWord = oFullTextData.Paragraphs[oTextData[i].endingText.paragraph].Lines[oTextData[i].endingText.line].Words[oTextData[i].endingText.word];

				// Set the ending text to the last / first character.
				oTextData[i].endingText.character = oTextData[i].leftToRight ? oWord.Characters[oWord.Characters.length - 1].Order : oWord.Characters[0].Order;
			}
		}

		// Return the result.
		return oTextData;
	};

	/**
	 * Constructor for a text selection groups object.
	 * @param {object} oTextSelectionGroups - The text selection groups.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 */
	TextCompare.prototype.textSelectionGroups = function (oTextSelectionGroups, oArtworkCanvas) {

		// Ensure we have a document to store selections for.
		if (oArtworkCanvas.g_oDoc != null) {

			// The id of the canvas the text selection is stored for.
			this.g_szCanvasId = oArtworkCanvas.g_szCanvasId;

			// The id of the document.
			this.g_iDocumentId = oArtworkCanvas.g_oDoc.documentId;

			// The list of text selection groups.
			this.g_oTextSelectionGroups = oTextSelectionGroups;
		}
	};

	/**
	 * Constructor for a text selection group object.
	 * @param {int} iGroupOrder - The group order.
	 */
	TextCompare.prototype.textSelectionGroup = function (iGroupOrder) {

		// The order of this group.
		this.g_iGroupOrder = iGroupOrder;

		// The text selection for this group.
		this.g_oTextSelections = [];

		// The text modifications for this group.
		this.g_oTextModifications = [];

		// The text differences for this group.
		this.g_oTextDifferences = [];
	};

	/**
	 * Constructor for a text selection object.
	 * @param {object} oTextData - The text data.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextCompare.prototype.textSelection = function (oTextData, oArtworkCanvas, oAnnotationCanvas) {

		// The text data for the selection.
		this.g_oTextData = g_oTextCompare.populateTextData(oTextData, oArtworkCanvas, oAnnotationCanvas);

		// The page the selection is on.
		this.g_iPage = oArtworkCanvas.g_iCurrentPage;

		// Get the selection words.
		this.g_oSelectionWords = g_oTextCompare.getTextSelectionWords(oTextData, oArtworkCanvas, oAnnotationCanvas);

		// The selection words bounds.
		this.g_oSelectionWordsBounds = g_oTextTools.getWordListBounds(this.g_oSelectionWords, oArtworkCanvas, oAnnotationCanvas);

		// Get the selection words text.
		this.g_oSelectionWordsText = g_oTextTools.getWordListText(this.g_oSelectionWords, oArtworkCanvas, oAnnotationCanvas);

		// Get the selection characters.
		this.g_oSelectionCharacters = g_oTextCompare.getTextSelectionCharacters(oTextData, oArtworkCanvas, oAnnotationCanvas);

		// The selection characters bounds.
		this.g_oSelectionCharacterBounds = g_oTextTools.getCharacterListBounds(this.g_oSelectionCharacters, oArtworkCanvas, oAnnotationCanvas);

		// Get the selection characters text.
		this.g_oSelectionCharactersText = g_oTextTools.getCharacterListText(this.g_oSelectionCharacters, oArtworkCanvas, oAnnotationCanvas);
	};

	/**
	 * Constructor for a text selection word object.
	 * @param {object} oWord - The word.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextCompare.prototype.textSelectionWord = function (oWord, oArtworkCanvas, oAnnotationCanvas) {
		this.paragraph = oWord.paragraph;
		this.line = oWord.line;
		this.word = oWord.word;
		this.page = oArtworkCanvas.g_iCurrentPage;
		this.bold = g_oTextTools.getWordBoldSetting(oWord, oArtworkCanvas, oAnnotationCanvas);
		this.italic = g_oTextTools.getWordItalicSetting(oWord, oArtworkCanvas, oAnnotationCanvas);
		this.content = g_oTextTools.getWordText(oWord, oArtworkCanvas, oAnnotationCanvas);
		this.status = g_oTextCompare.eDifferenceTypes.NoDifference;
		this.difference = -1;
		this.marker = g_oTextCompare.eMarkerTypes.NoMarker;
		this.markerDifference = -1;
		this.equivalentDifference = false;
	};

	/**
	 * Constructor for a text selection character object.
	 * @param {object} oCharacter - The character.
	 * @param {object} oArtworkCanvas - The artwork canvas.
	 * @param {object} oAnnotationCanvas - The annotation canvas.
	 */
	TextCompare.prototype.textSelectionCharacter = function (oCharacter, oArtworkCanvas, oAnnotationCanvas) {
		this.paragraph = oCharacter.paragraph;
		this.line = oCharacter.line;
		this.word = oCharacter.word;
		this.character = oCharacter.character;
		this.page = oArtworkCanvas.g_iCurrentPage;
		this.lastInWord = g_oTextTools.getCharacterLastInWord(oCharacter, oArtworkCanvas, oAnnotationCanvas);
		this.bold = g_oTextTools.getCharacterBoldSetting(oCharacter, oArtworkCanvas, oAnnotationCanvas);
		this.italic = g_oTextTools.getCharacterItalicSetting(oCharacter, oArtworkCanvas, oAnnotationCanvas);
		this.content = g_oTextTools.getCharacterText(oCharacter, oArtworkCanvas, oAnnotationCanvas);
		this.status = g_oTextCompare.eDifferenceTypes.NoDifference;
		this.difference = -1;
		this.marker = g_oTextCompare.eMarkerTypes.NoMarker;
		this.markerDifference = -1;
		this.equivalentDifference = false;
	};

	/**
	 * Constructor for a text modification object.
	 * @param {object} oComparison - The comparison.
	 */
	TextCompare.prototype.textModification = function (oComparison) {

		// The modification text.
		this.g_szModificationText = oComparison[1];

		// The difference type.
		this.g_eDifferenceType = oComparison[0];

		// The difference mapping.
		this.g_eDifferenceMapping = g_oTextCompare.g_eDifferenceMapping.Both;
	};

	/**
	 * Constructor for a text difference object.
	 * @param {object} oDifferenceTextList - The difference text list.
	 * @param {enum} eDifferenceType - The difference type.
	 * @param {int} iDifferenceNumber - The difference number.
	 * @param {int} iDifferenceOrder - The difference order.
	 */
	TextCompare.prototype.textDifference = function (oDifferenceTextList, eDifferenceType, iDifferenceNumber, iDifferenceOrder) {

		// The text to be used for the difference.
		this.g_oDifferenceText = oDifferenceTextList;

		// The formatted difference text.
		this.g_oFormattedDifferenceText = [];

		// The difference type.
		this.g_eDifferenceType = eDifferenceType;

		// The number for this difference.
		this.g_iDifferenceNumber = iDifferenceNumber;

		// The order for this difference.
		this.g_iDifferenceOrder = iDifferenceOrder;

		// The status of the difference.
		this.g_iDifferenceStatus = g_oTextCompare.eDifferenceStatuses.Initial;

		// The annotation for the difference.
		this.g_bAnnotationCreated = false;
	};

	// Add the Text Compare UI object to the window.
	window.TextCompare = TextCompare;
}());